import { z } from "zod";
import type {
  DaySummary,
  Flag,
  Movement,
  MovementEvent,
  MovementMessage,
  MovementStatus,
  ScheduleLine,
} from "../domain/aodb";
import type { Checkpoint, FlowDay, LanePlan, Observation as FlowObservation } from "../domain/flow";
import type { FeedDraft, FeedMessage, FeedOutcome, FeedResult, FeedTestResult, FlightFeed } from "../domain/flightFeeds";
import type {
  FinanceConnection,
  FinanceConnectionInput,
  FinanceImportResult,
  FinanceLatest,
  FinanceStatus,
  FinanceTest,
} from "../domain/sapConnection";
import type { AllocDay, EngineResult, Policy, Proposal, Refusal, Resource, Rule as AllocRule, WhatIf, Window } from "../domain/allocation";
import type { ActualKey, NewMovement } from "../domain/flightEntries";
import type {
  ContactOrg,
  ContactPerson,
  Directory,
  NoticeEvent,
  NotifyRule,
  OrgKind,
  Reach,
  Recipient,
  WorkOrderMessage,
} from "../domain/contacts";
import {
  summarise,
  type DispatchGroup,
  type DispatchInput,
  type DispatchNotification,
  type DispatchResult,
} from "../domain/dispatch";
import type { StoresRegister } from "../domain/stores";
import type { ApprovalStatus, ApprovalStepAck, ApprovalsBoard, NewApprovalRequest } from "../domain/approvals";
import type { PlatformStores, StoresApplied, StoresImported, StoresOp } from "../domain/storesOps";
import type {
  Closure,
  DeclareClosureInput,
  DeclaredClosure,
  EndedClosure,
  ListClosuresOptions,
} from "../domain/closures";
import type { EstateSummary, NewNoteInput, SiteNote } from "../domain/estate";
import type { FleetAssetRow } from "../domain/fleet";
import type {
  ActionKind,
  AssetHistoryPoint,
  LogEntry,
  TestHistory,
  Comparison,
  ConsumptionPoint,
  LatestTest,
  NewTestInput,
  TestingSummary,
  TestRecord,
  TestRecordDetail,
  TestResult,
  TestType,
  Threshold,
} from "../domain/testing";
import {
  toPriority,
  WORK_ORDER_KINDS,
  WORK_ORDER_STATUSES,
  type CreateWorkOrderInput,
  type SetSlaInput,
  type ClassifyInput,
  type ReceiptInput,
  type WorkSettings,
  type TransitionInput,
  type WorkOrder,
  type WorkOrderEvent,
  type WorkOrderEvidence,
} from "../domain/workOrders";
import { ALERT_KINDS, ALERT_STATUSES, type Alert, type AlertExplanation, type AlertKind } from "../domain/alerts";
import { INSPECTION_STATUSES, type Inspection, type InspectionDetail, type Observation } from "../domain/inspections";
import type {
  InspectionRound,
  InspectionRoundInput,
  InspectionRoundQuery,
  InspectionRoundResult,
} from "../domain/inspectionRounds";
import * as inspectionsWire from "./inspectionsWire";
import * as registerWire from "./registerRowsWire";
import type { PlanAnswer, PlanSide } from "../domain/planning";
import type { LiabilityCurve, LiabilityRegister } from "../domain/liability";
import {
  patchToWire,
  type AmAssetDetail,
  type AmCondition,
  type AmFilter,
  type AmImportResult,
  type AmImportRow,
  type AmLifecycle,
  type AmMaintenance,
  type AmPatch,
  type AmPlan,
  type AmPlanInput,
  type AmPolicy,
  type AmRegister,
  type AmTypeDef,
  type AmCreateInput,
  type AmHistoryEvent,
  type AmNote,
  type NoteCategory,
} from "../domain/assetManagement";
import type {
  HrCost,
  HrEmployeeInput,
  HrEmployeePatch,
  HrLabour,
  HrPeople,
  HrRulesPatch,
  HrRulesView,
  HrSaudization,
} from "../domain/hr";
import type { HrAccessView } from "../domain/hrAccess";
import type {
  HrAttendanceDay,
  HrAttendanceInput,
  HrAttendanceRow,
  HrRecord,
  HrRecordInput,
  HrStaffFile,
  RecordStatus,
} from "../domain/hrFile";
import type {
  HrChannels,
  HrClockEvent,
  HrClockInput,
  HrGeofence,
  HrMe,
  HrNotification,
  HrSelfRequest,
} from "../domain/hrClock";
import type { ImportResponse } from "../domain/hrImport";
import * as amWire from "./assetManagementWire";
import * as hrWire from "./hrWire";
import * as callsWire from "./callsWire";
import * as safetyWire from "./safetyWire";
import type * as Safety from "../domain/safety";
import type {
  Call,
  CallDirectory,
  CallMedia,
  CallMessage,
  CallPoll,
  CallPolicy,
  CallSettingsPatch,
  CallStartOptions,
  DndState,
  IceConfig,
  LiveCall,
  Meeting,
  MeetingDraft,
  ParticipantRole,
  PolicyView,
} from "../domain/calls";
import type { CallCheck } from "./dataSource";
import type { ClosureCost, CriticalReport } from "../domain/capacity";
import type { DriftCheck, DriftSchedule } from "../domain/drift";
import type { Fit, Population } from "../domain/reliability";
import type { Divergence, FactSummary } from "../domain/ledger";
import type { FieldPosition, ModeChange } from "./dataSource";
import type {
  AddedPerson,
  AuthStatus,
  Invitation,
  Invited,
  NewPerson,
  Person,
  ProviderChange,
  RoleRow,
  SetupState,
} from "../domain/administration";
import type { AccessLevel } from "../assistant/access";
import type {
  Difference,
  Evidence as MigrationEvidence,
  MigrationScreen,
  MigrationStatus,
  ScreenDetail,
  ScreenMode,
  Triage,
  Verdict,
} from "../domain/migration";
import type { CanWork } from "../domain/weather";
import type { Programme } from "../domain/programme";
import { UTILITIES, type MeterRegister, type MeterUploadResult, type NewMeterInput } from "../domain/meters";
import type { Study } from "../domain/counterfactual";
import type { Chain, Link } from "../domain/provenance";
import type { Kpi } from "../domain/kpis";
import type {
  AmConditionInput,
  AmConditionResult,
  EnvelopeInput,
  FundingEnvelope,
  KpiTargetChange,
  KpiTargetInput,
  ThresholdsInput,
} from "../domain/entryForms";
import type { AssistQuestion, AssistReply } from "../domain/assist";
import type { Geometry, MapFeature, MapLayer } from "../domain/geo";
import {
  OBLIGATION_STATUSES,
  type ComplianceReport,
  type Rule,
  type VerifyRuleInput,
  type VerifyRuleResult,
} from "../domain/compliance";
import {
  DOCUMENT_STATUSES,
  REVISION_STATUSES,
  type DocumentDetail,
  type DocumentRow,
  type DocumentType,
  type NewDocumentInput,
  type NewDocumentResult,
  type NewRevisionInput,
  type NewRevisionResult,
  type RevisionTransitionInput,
  type RevisionTransitionResult,
} from "../domain/library";
import {
  CERTIFICATE_TYPE,
  type AttachedFile,
  type CertificateIssueInput,
  type CertificateWrite,
  type NewCertificateInput,
} from "../domain/certificates";
import {
  ATTENTION_KINDS,
  GATE_DECISIONS,
  GATE_STATUSES,
  INSPECTION_KINDS,
  INSPECTION_RESULTS,
  ITEM_RESULTS,
  PROJECT_STATUSES,
  type AttentionKind,
  type AttentionReport,
  type AttentionRow,
  type Evidence,
  type Gate,
  type GateDecisionInput,
  type GateDecisionResult,
  type GateSubmitInput,
  type GateSubmitResult,
  type HoldPoint,
  type HoldPointReport,
  type InspectionWriteResult,
  type ItemResultInput,
  type Money,
  type CloseActionInput,
  type CreateProjectInput,
  type NewLogEntryInput,
  type ProjectLog,
  type ScheduleInput,
  type ScheduleResult,
  type UpdateProjectInput,
  type ProjectDetail,
  type ProjectInspectionDetail,
  type ProjectInspectionRow,
  type ProjectQuery,
  type ProjectRow,
  type ProjectVocabulary,
  type Quality,
  type RecordResultInput,
  type Schedule,
  type VocabularyTerm,
} from "../domain/projects";
import type { Site } from "../domain/sites";
import {
  DataSourceError,
  type Bootstrap,
  type DataSource,
  type DocumentQuery,
  type InspectionQuery,
  type Notification,
  type PlanQuery,
  type ClosureQuery,
  type DivergenceQuery,
  type WorkOrderPage,
  type WorkOrderQuery,
  type EventPage,
  type SkyModel,
  type ComposedText,
  type SkyConverseInput,
  type SkyConverseReply,
} from "./dataSource";
import { HttpClient } from "./http";

/*
 * Wire schemas — written from the platform's routers, not from guesses:
 *
 *   console.py  bootstrap
 *   ops.py      list_work_orders, list_alerts, list_notifications
 *   work.py     get_work_order, work_order_history, create_work_order, transition_work_order
 *   insight.py  kpis, explain_alert
 *   projects.py list_projects, get_project, hold_points, attention, get_inspection
 *
 * Parsed leniently (`passthrough`) so an added column never breaks the UI,
 * and mapped into the domain in one place. When a router changes, this file
 * is the only one that should.
 */

const wireStatus = z.enum(WORK_ORDER_STATUSES);
const wireKind = z.enum(WORK_ORDER_KINDS);
const num = z.union([z.number(), z.string().transform(Number)]); // numeric(14,2) may arrive as a string
const iso = z.string();
const geoPoint = z
  .object({ type: z.literal("Point"), coordinates: z.tuple([z.number(), z.number()]).rest(z.number()) })
  .nullable()
  .optional();

/* ---- bootstrap ------------------------------------------------------------ */

const wireSite = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    timezone: z.string().nullable().optional(),
    lon: z.number().nullable().optional(),
    lat: z.number().nullable().optional(),
    assets: z.number().optional(),
    open_defects: z.number().optional(),
    open_work: z.number().optional(),
    open_alerts: z.number().optional(),
  })
  .passthrough();

const wireBootstrap = z
  .object({
    tenant: z.object({ name: z.string().optional(), slug: z.string().optional() }).passthrough().optional(),
    identity: z
      .object({
        principal: z.string().nullable().optional(),
        method: z.string().optional(),
        subject: z.string().nullable().optional(),
        groups: z.array(z.string()).default([]),
        access_level: z.enum(["operations", "engineering", "executive", "master"]).optional(),
      })
      .passthrough(),
    permissions: z.array(z.string()).default([]),
    sites: z.array(wireSite),
    packs: z.array(z.object({ key: z.string(), version: z.string() }).passthrough()).default([]),
    development_auth: z.boolean().default(false),
  })
  .passthrough();

function mapSite(s: z.infer<typeof wireSite>): Site {
  return {
    id: s.id,
    code: s.code,
    name: s.name,
    timezone: s.timezone ?? undefined,
    centre: s.lon != null && s.lat != null ? { lon: s.lon, lat: s.lat } : undefined,
    assets: s.assets,
    counts: {
      openDefects: s.open_defects ?? 0,
      openWorkOrders: s.open_work ?? 0,
      openAlerts: s.open_alerts ?? 0,
    },
  };
}

/* ---- work orders ------------------------------------------------------------ */

/** Columns common to the list row (ops.py) and the detail head (work.py, `w.*`). */
const wireWorkOrderRow = z
  .object({
    id: z.string(),
    number: z.string(),
    site_id: z.string().optional(),
    title: z.string(),
    kind: wireKind.catch("corrective"),
    priority: z.number(),
    status: wireStatus,
    description: z.string().nullable().optional(),
    zone_code: z.string().nullable().optional(),
    asset_tag: z.string().nullable().optional(),
    crew: z.string().nullable().optional(),
    opened_at: iso,
    due_at: iso.nullable().optional(),
    started_at: iso.nullable().optional(),
    completed_at: iso.nullable().optional(),
    verified_at: iso.nullable().optional(),
    cost_actual: num.nullable().optional(),
    cost_planned: num.nullable().optional(),
    labour_hours: num.nullable().optional(),
    sla_hours: num.nullable().optional(),
    asset_type: z.string().nullable().optional(),
    asset_type_name: z.string().nullable().optional(),
    defect_type_name: z.string().nullable().optional(),
    discipline: z.string().nullable().optional(),
    rule_key: z.string().nullable().optional(),
    capex: z.boolean().optional(),
    alert_id: z.string().nullable().optional(),
    defect_ids: z.array(z.string()).nullable().optional(),
    client_id: z.string().nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    point: geoPoint,
    geometry: z.unknown().optional(),
    requested_by_name: z.string().nullable().optional(),
    completed_by_name: z.string().nullable().optional(),
    verified_by_name: z.string().nullable().optional(),
    received_at: iso.nullable().optional(),
    received_via: z.enum(["app", "link", "phone", "email", "in_person", "other"]).nullable().optional().catch(null),
    received_by: z.string().nullable().optional(),
    completed_by_contact: z.string().nullable().optional(),
    verified_via: z.enum(["person", "photo"]).nullable().optional().catch(null),
    delivery: z.record(z.string(), z.number()).nullable().optional(),
  })
  .passthrough();

const wireWorkOrderList = z
  .object({
    work_orders: z.array(wireWorkOrderRow),
    limit: z.number().optional(),
    truncated: z.boolean().default(false),
    note: z.string().optional(),
  })
  .passthrough();

const wireWorkOrderDetail = z
  .object({
    work_order: wireWorkOrderRow,
    tasks: z
      .array(
        z
          .object({
            seq: z.number(),
            description: z.string(),
            status: z.string(),
            labour_hours: num.nullable().optional(),
            completed_at: iso.nullable().optional(),
            note: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
    defects: z
      .array(
        z
          .object({
            id: z.string(),
            summary: z.string(),
            severity: z.number(),
            status: z.string(),
            defect_type: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
    evidence: z
      .array(
        z
          .object({
            id: z.string(),
            phase: z.string().nullable().optional(),
            created_at: iso,
            object_id: z.string().nullable().optional(),
            content_type: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
    allowed_transitions: z.array(wireStatus).default([]),
  })
  .passthrough();

const wireHistory = z
  .object({
    history: z.array(
      z
        .object({
          from_status: wireStatus.nullable(),
          to_status: wireStatus,
          note: z.string().nullable().optional(),
          attrs: z.unknown().optional(),
          occurred_at: iso,
          principal: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

/** create and transition both answer with a stub; the detail is fetched afterwards. */
const wireWorkOrderStub = z.object({ id: z.string(), number: z.string(), status: wireStatus }).passthrough();

function mapWorkOrder(w: z.infer<typeof wireWorkOrderRow>, siteId?: string): WorkOrder {
  const point = w.point ? { lon: w.point.coordinates[0], lat: w.point.coordinates[1] } : undefined;
  return {
    id: w.id,
    number: w.number,
    siteId: w.site_id ?? siteId ?? "",
    title: w.title,
    kind: w.kind,
    priority: toPriority(w.priority),
    status: w.status,
    description: w.description ?? undefined,
    zoneCode: w.zone_code ?? undefined,
    assetTag: w.asset_tag ?? undefined,
    crew: w.crew ?? undefined,
    point,
    openedAt: w.opened_at,
    dueAt: w.due_at ?? undefined,
    startedAt: w.started_at ?? undefined,
    completedAt: w.completed_at ?? undefined,
    verifiedAt: w.verified_at ?? undefined,
    costActual: w.cost_actual ?? undefined,
    costPlanned: w.cost_planned ?? undefined,
    labourHours: w.labour_hours ?? undefined,
    slaHours: w.sla_hours ?? undefined,
    assetType: w.asset_type ?? undefined,
    assetTypeName: w.asset_type_name ?? undefined,
    defectTypeName: w.defect_type_name ?? undefined,
    discipline: w.discipline ?? undefined,
    ruleKey: w.rule_key ?? undefined,
    capex: w.capex,
    alertId: w.alert_id ?? undefined,
    defectIds: w.defect_ids ?? undefined,
    clientId: w.client_id ?? undefined,
    attrs: w.attrs ?? undefined,
    requestedBy: w.requested_by_name ?? undefined,
    completedBy: w.completed_by_name ?? w.completed_by_contact ?? undefined,
    completedByContact: w.completed_by_contact ?? undefined,
    verifiedVia: w.verified_via ?? undefined,
    verifiedBy: w.verified_by_name ?? undefined,
    receivedAt: w.received_at ?? undefined,
    receivedVia: w.received_via ?? undefined,
    receivedBy: w.received_by ?? undefined,
    delivery: w.delivery ?? undefined,
  };
}

/* ---- test records ------------------------------------------------------------------ */

const outcome = z.enum(["pass", "attention", "fail", "not_assessed"]);

const wireThreshold = z.record(z.string(), z.unknown());

function mapThreshold(t: z.infer<typeof wireThreshold>): Threshold {
  return {
    label: String(t.label ?? ""),
    match: (t.match as Record<string, string> | null) ?? null,
    attention: typeof t.attention === "number" ? t.attention : null,
    fail: typeof t.fail === "number" ? t.fail : null,
    basis: t.basis === "pct_of_specified" ? "pct_of_specified" : "value",
    higherIsBetter: t.higher_is_better !== false,
    status: t.status === "agreed" ? "agreed" : "proposed",
    basisNote: typeof t.basis_note === "string" ? t.basis_note : undefined,
  };
}

const wireTestType = z
  .object({
    key: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    frameworks: z.array(z.string()).default([]),
    mode: z.enum(["threshold", "ratio", "judged"]),
    measurement_key: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
    interval_days: z.number().nullable().optional(),
    interval_status: z.enum(["proposed", "agreed"]).default("proposed"),
    thresholds: z.array(wireThreshold).default([]),
    ratio: z.record(z.string(), z.unknown()).nullable().optional(),
    summary_fields: z
      .array(z.object({ key: z.string(), label: z.string(), type: z.enum(["text", "number"]) }))
      .default([]),
    updated_at: z.string().nullable().optional(),
    updated_by: z.string().nullable().optional(),
  })
  .passthrough();

function mapTestType(t: z.infer<typeof wireTestType>): TestType {
  const r = t.ratio;
  return {
    key: t.key,
    name: t.name,
    description: t.description ?? undefined,
    frameworks: t.frameworks,
    mode: t.mode,
    measurementKey: t.measurement_key ?? undefined,
    unit: t.unit ?? undefined,
    intervalDays: t.interval_days ?? undefined,
    intervalStatus: t.interval_status,
    thresholds: t.thresholds.map(mapThreshold),
    ratio: r
      ? {
          value: String(r.value),
          valueLabel: r.value_label as string | undefined,
          limit: String(r.limit),
          limitLabel: r.limit_label as string | undefined,
          status: r.status === "agreed" ? "agreed" : "proposed",
          basisNote: r.basis_note as string | undefined,
        }
      : undefined,
    summaryFields: t.summary_fields,
    updatedAt: t.updated_at ?? undefined,
    updatedBy: t.updated_by ?? undefined,
  };
}

const wireTestRecord = z
  .object({
    id: z.string(),
    reference: z.string(),
    test_type: z.string(),
    test_type_name: z.string(),
    frameworks: z.array(z.string()).default([]),
    title: z.string(),
    performed_on: z.string(),
    performed_by: z.string().nullable().optional(),
    method: z.string().nullable().optional(),
    device: z.string().nullable().optional(),
    scope: z.string().nullable().optional(),
    summary: z.record(z.string(), z.unknown()).default({}),
    outcome,
    outcome_basis: z.string(),
    judged: z.boolean().default(false),
    counts: z.record(z.string(), z.number()).default({}),
    notes: z.string().nullable().optional(),
    next_due: z.string().nullable().optional(),
    demo: z.boolean().default(false),
    created_at: z.string(),
    created_by_name: z.string().nullable().optional(),
    results: z.number().default(0),
    files: z.number().default(0),
    actions: z.number().default(0),
    revision: z.number().default(1),
    supersedes: z.string().nullable().optional(),
    superseded_by: z.string().nullable().optional(),
    amendment_reason: z.string().nullable().optional(),
    source: z.enum(["form", "file", "demo", "api"]).default("form"),
    source_file: z.string().nullable().optional(),
  })
  .passthrough();

function mapTestRecord(r: z.infer<typeof wireTestRecord>): TestRecord {
  return {
    id: r.id,
    reference: r.reference,
    testType: r.test_type,
    testTypeName: r.test_type_name,
    frameworks: r.frameworks,
    title: r.title,
    performedOn: r.performed_on.slice(0, 10),
    performedBy: r.performed_by ?? undefined,
    method: r.method ?? undefined,
    device: r.device ?? undefined,
    scope: r.scope ?? undefined,
    summary: r.summary,
    outcome: r.outcome,
    outcomeBasis: r.outcome_basis,
    judged: r.judged,
    counts: r.counts,
    notes: r.notes ?? undefined,
    nextDue: r.next_due?.slice(0, 10) ?? undefined,
    demo: r.demo,
    createdAt: r.created_at,
    createdBy: r.created_by_name ?? undefined,
    results: r.results,
    files: r.files,
    actions: r.actions,
    revision: r.revision,
    supersedes: r.supersedes ?? undefined,
    supersededBy: r.superseded_by ?? undefined,
    amendmentReason: r.amendment_reason ?? undefined,
    source: r.source,
    sourceFile: r.source_file ?? undefined,
  };
}

const wireTestResult = z
  .object({
    id: z.number(),
    asset_tag: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    value: z.number(),
    specified: z.number().nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).default({}),
    judged_value: z.number().nullable().optional(),
    outcome,
    threshold_label: z.string().nullable().optional(),
    asset_removed: z.boolean().default(false),
  })
  .passthrough();

function mapTestResult(r: z.infer<typeof wireTestResult>): TestResult {
  return {
    id: r.id,
    assetTag: r.asset_tag ?? undefined,
    location: r.location ?? undefined,
    value: r.value,
    specified: r.specified ?? undefined,
    attrs: r.attrs,
    judgedValue: r.judged_value ?? undefined,
    outcome: r.outcome,
    thresholdLabel: r.threshold_label ?? undefined,
    assetRemoved: r.asset_removed,
  };
}

const wireComparison = z
  .object({
    prior: z.object({
      id: z.string(),
      reference: z.string(),
      performed_on: z.string(),
      outcome,
      counts: z.record(z.string(), z.number()).default({}),
    }),
    matched: z.number(),
    only_latest: z.number(),
    only_prior: z.number(),
    mean_latest: z.number().nullable().optional(),
    mean_prior: z.number().nullable().optional(),
    worsened: z.number(),
    improved: z.number(),
    rows: z.array(
      z
        .object({
          key: z.string(),
          asset_tag: z.string().nullable().optional(),
          location: z.string().nullable().optional(),
          attrs: z.record(z.string(), z.unknown()).default({}),
          latest: z.number(),
          latest_outcome: outcome,
          prior: z.number().nullable().optional(),
          prior_outcome: outcome.nullable().optional(),
          delta: z.number().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

function mapComparison(c: z.infer<typeof wireComparison>): Comparison {
  return {
    prior: {
      id: c.prior.id,
      reference: c.prior.reference,
      performedOn: c.prior.performed_on.slice(0, 10),
      outcome: c.prior.outcome,
      counts: c.prior.counts,
    },
    matched: c.matched,
    onlyLatest: c.only_latest,
    onlyPrior: c.only_prior,
    meanLatest: c.mean_latest ?? undefined,
    meanPrior: c.mean_prior ?? undefined,
    worsened: c.worsened,
    improved: c.improved,
    rows: c.rows.map((r) => ({
      key: r.key,
      assetTag: r.asset_tag ?? undefined,
      location: r.location ?? undefined,
      attrs: r.attrs,
      latest: r.latest,
      latestOutcome: r.latest_outcome,
      prior: r.prior ?? undefined,
      priorOutcome: r.prior_outcome ?? undefined,
      delta: r.delta ?? undefined,
    })),
  };
}

const wireTestDetail = z
  .object({
    record: wireTestRecord.extend({ thresholds_used: z.array(wireThreshold).default([]) }),
    results: z.array(wireTestResult),
    files: z.array(
      z
        .object({
          id: z.string(),
          object_id: z.string(),
          content_type: z.string().nullable().optional(),
          bytes: z.number().nullable().optional(),
          note: z.string().nullable().optional(),
          created_at: z.string(),
        })
        .passthrough(),
    ),
    comparison: wireComparison.nullable(),
    readings_written: z
      .object({ n: z.number(), measurement: z.string().nullable().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();

const wireLog = z
  .object({
    id: z.number(),
    action: z.string(),
    label: z.string(),
    occurred_at: z.string(),
    principal_name: z.string().nullable().optional(),
    before: z.record(z.string(), z.unknown()).nullable().optional(),
    after: z.record(z.string(), z.unknown()).nullable().optional(),
    record_id: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
    revision: z.number().nullable().optional(),
    title: z.string().nullable().optional(),
    test_type: z.string().nullable().optional(),
  })
  .passthrough();

function mapLog(e: z.infer<typeof wireLog>): LogEntry {
  return {
    id: e.id,
    action: e.action,
    label: e.label,
    at: e.occurred_at,
    by: e.principal_name ?? undefined,
    before: e.before ?? null,
    after: e.after ?? null,
    recordId: e.record_id ?? undefined,
    reference: e.reference ?? undefined,
    revision: e.revision ?? undefined,
    title: e.title ?? undefined,
    testType: e.test_type ?? undefined,
  };
}

/* ---- estate slices ---------------------------------------------------------------- */

/* Narrow on purpose: the register reads the attributes, and `attrs` is
   pack-declared so its shape is the pack's, not this application's. */
const wireFleetAssets = z.array(
  z
    .object({
      id: z.string(),
      tag: z.string(),
      name: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
      asset_type: z.string(),
      zone_code: z.string().nullable().optional(),
      condition_score: z.number().nullable().optional(),
      attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    })
    .passthrough(),
);

const wireEstateSummary = z
  .object({
    asset_types: z.array(
      z
        .object({
          key: z.string(),
          name: z.string(),
          domain: z.string().nullable().optional(),
          category: z.string().nullable().optional(),
          assets: z.number(),
          scored: z.number(),
          condition_mean: z.number().nullable().optional(),
          located: z.number(),
          by_status: z.record(z.string(), z.number()).default({}),
          by_criticality: z.record(z.string(), z.number()).default({}),
          attributes: z
            .record(
              z.string(),
              z.object({
                values: z.array(z.object({ value: z.union([z.string(), z.number(), z.boolean()]), count: z.number() })),
                not_recorded: z.number(),
              }),
            )
            .default({}),
        })
        .passthrough(),
    ),
    unknown_asset_types: z.array(z.string()).default([]),
    assets_total: z.number(),
    defects: z
      .object({
        open: z.number(),
        raised_30d: z.number(),
        resolved_30d: z.number(),
        assets_with_open: z.number(),
        open_list_total: z.number(),
        open_list: z.array(
          z
            .object({
              id: z.string(),
              asset_tag: z.string(),
              asset_type: z.string(),
              defect_type: z.string().nullable().optional(),
              defect_type_name: z.string().nullable().optional(),
              severity: z.number(),
              status: z.string(),
              summary: z.string(),
              detected_at: z.string(),
              due_at: z.string().nullable().optional(),
              zone_code: z.string().nullable().optional(),
            })
            .passthrough(),
        ),
        by_type_severity: z.array(
          z.object({ defect_type: z.string(), severity: z.number(), n: z.number(), assets: z.number() }).passthrough(),
        ),
      })
      .passthrough(),
    alerts: z
      .object({
        open: z.number(),
        list: z.array(
          z
            .object({
              id: z.string(),
              title: z.string(),
              kind: z.string(),
              severity: z.number(),
              status: z.string(),
              raised_at: z.string(),
              asset_tag: z.string(),
              asset_type: z.string(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
    readings: z.array(
      z
        .object({
          asset_type: z.string(),
          measurement: z.string(),
          measurement_name: z.string(),
          unit: z.string().nullable().optional(),
          readings: z.number(),
          assets: z.number(),
          first_at: z.string(),
          latest_at: z.string(),
        })
        .passthrough(),
    ),
    latest_readings: z.array(
      z
        .object({
          asset_tag: z.string(),
          asset_type: z.string(),
          measurement: z.string(),
          value_num: z.number(),
          observed_at: z.string(),
          attrs: z.record(z.string(), z.unknown()).nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

/* ---- insight ------------------------------------------------------------------ */

const wireKpis = z
  .object({
    metrics: z.array(
      z
        .object({
          key: z.string(),
          name: z.string(),
          unit: z.string().nullable().optional(),
          grain: z.string().nullable().optional(),
          target: num.nullable().optional(),
          target_status: z.enum(["pack", "proposed", "agreed"]).nullable().optional(),
          target_basis: z.string().nullable().optional(),
          pack_target: num.nullable().optional(),
          target_set_by: z.string().nullable().optional(),
          target_set_at: z.string().nullable().optional(),
          target_scope: z.enum(["site", "tenant"]).nullable().optional(),
          direction: z.string().nullable().optional(), // higher_is_better | lower_is_better
          display: z.unknown().optional(),
          value: num.nullable().optional(),
          breakdown: z.unknown().optional(),
          bucket: z.string().nullable().optional(),
          computed_at: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
    uncomputable: z
      .array(
        z.object({ key: z.string(), name: z.string(), compile_error: z.string().nullable().optional() }).passthrough(),
      )
      .default([]),
  })
  .passthrough();

const wireAlertRow = z
  .object({
    id: z.string(),
    title: z.string(),
    kind: z.enum(ALERT_KINDS).catch("reactive"),
    severity: z.number(),
    status: z.enum(ALERT_STATUSES).catch("open"),
    raised_at: iso,
    predicted_for: iso.nullable().optional(),
    confidence: num.nullable().optional(),
    rule_key: z.string().nullable().optional(),
    asset_tag: z.string().nullable().optional(),
    zone_code: z.string().nullable().optional(),
    point: geoPoint,
  })
  .passthrough();

const wireAlerts = z.object({ alerts: z.array(wireAlertRow) }).passthrough();

const wireExplain = z
  .object({
    alert: wireAlertRow.extend({
      occurrences: z.number().nullable().optional(),
      confirmed: z.boolean().nullable().optional(),
      rule_name: z.string().nullable().optional(),
      rule_class: z.string().nullable().optional(),
      expression: z.string().nullable().optional(),
      reference: z.string().nullable().optional(),
      parameters: z.record(z.string(), z.unknown()).nullable().optional(),
      resolved: z.record(z.string(), z.unknown()).nullable().optional(),
      verify_required: z.boolean().nullable().optional(),
      subject_kind: z.string().nullable().optional(),
      subject_label: z.string().nullable().optional(),
      outcome: z.string().nullable().optional(),
    }),
    resulting_actions: z
      .array(z.object({ kind: z.string(), ref: z.string(), title: z.string(), status: z.string() }).passthrough())
      .default([]),
  })
  .passthrough();

function mapAlert(a: z.infer<typeof wireAlertRow>): Alert {
  return {
    id: a.id,
    title: a.title,
    kind: a.kind,
    severity: a.severity,
    status: a.status,
    raisedAt: a.raised_at,
    predictedFor: a.predicted_for ?? undefined,
    confidence: a.confidence ?? undefined,
    ruleKey: a.rule_key ?? undefined,
    assetTag: a.asset_tag ?? undefined,
    zoneCode: a.zone_code ?? undefined,
    point: a.point ? { lon: a.point.coordinates[0], lat: a.point.coordinates[1] } : undefined,
  };
}

const wireNotifications = z
  .object({
    notifications: z.array(
      z
        .object({
          id: z.union([z.string(), z.number()]),
          role: z.string().nullable().optional(),
          severity: z.number().nullable().optional(),
          title: z.string(),
          body: z.string().nullable().optional(),
          rule_key: z.string().nullable().optional(),
          subject_kind: z.string().nullable().optional(),
          subject_id: z.string().nullable().optional(),
          created_at: iso,
          read_at: iso.nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

/* ---- inspections (work.py list_inspections / get_inspection) -------------------- */

const wireInspectionRow = z
  .object({
    id: z.string(),
    status: z.enum(INSPECTION_STATUSES).catch("scheduled"),
    scheduled_for: iso.nullable().optional(),
    started_at: iso.nullable().optional(),
    completed_at: iso.nullable().optional(),
    score: num.nullable().optional(),
    coverage_pct: num.nullable().optional(),
    captured_offline: z.boolean().nullable().optional(),
    template: z.string(),
    template_name: z.string().nullable().optional(),
    depth: z.enum(["quick", "detailed"]).nullable().optional(),
    domain: z.string().nullable().optional(),
    zone_code: z.string().nullable().optional(),
    answers: z.number().optional(),
    failures: z.number().optional(),
    skipped: z.number().optional(),
  })
  .passthrough();

const wireInspections = z.object({ inspections: z.array(wireInspectionRow) }).passthrough();

const wireObservation = z
  .object({
    id: z.string(),
    item_key: z.string(),
    result: z.enum(["pass", "fail", "na", "value"]).nullable().optional(),
    value_num: num.nullable().optional(),
    value_text: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    skipped_reason: z.string().nullable().optional(),
    asset_tag: z.string().nullable().optional(),
    zone_code: z.string().nullable().optional(),
    defect_id: z.string().nullable().optional(),
    geometry: geoPoint.or(z.unknown()),
    photos: z.number().optional(),
  })
  .passthrough();

const wireInspectionDetail = z
  .object({
    inspection: wireInspectionRow.extend({
      attrs: z.record(z.string(), z.unknown()).nullable().optional(),
      route: z.unknown().optional(),
      scoring: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
    observations: z.array(wireObservation).default([]),
  })
  .passthrough();

function mapInspection(i: z.infer<typeof wireInspectionRow>, siteId: string): Inspection {
  return {
    id: i.id,
    siteId,
    status: i.status,
    template: i.template,
    templateName: i.template_name ?? i.template,
    depth: i.depth ?? undefined,
    domain: i.domain ?? undefined,
    zoneCode: i.zone_code ?? undefined,
    scheduledFor: i.scheduled_for ?? undefined,
    startedAt: i.started_at ?? undefined,
    completedAt: i.completed_at ?? undefined,
    score: i.score ?? undefined,
    coveragePct: i.coverage_pct ?? undefined,
    capturedOffline: i.captured_offline ?? undefined,
    answers: i.answers ?? 0,
    failures: i.failures ?? 0,
    ...(i.skipped != null ? { skipped: i.skipped } : {}),
  };
}

function mapObservation(o: z.infer<typeof wireObservation>): Observation {
  const g = o.geometry as { type?: string; coordinates?: number[] } | null | undefined;
  const point =
    g && g.type === "Point" && Array.isArray(g.coordinates)
      ? { lon: g.coordinates[0], lat: g.coordinates[1] }
      : undefined;
  return {
    id: o.id,
    itemKey: o.item_key,
    result: o.result ?? undefined,
    valueNum: o.value_num ?? undefined,
    valueText: o.value_text ?? undefined,
    notes: o.notes ?? undefined,
    skippedReason: o.skipped_reason ?? undefined,
    assetTag: o.asset_tag ?? undefined,
    zoneCode: o.zone_code ?? undefined,
    defectId: o.defect_id ?? undefined,
    point,
    photos: o.photos ?? 0,
  };
}

/* ---- geo (geo.py list_layers / list_features) ----------------------------------- */

const wireLayer = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z
      .union([z.string(), z.object({ en: z.string().optional() }).passthrough()])
      .nullable()
      .optional(),
    level: z.string().nullable().optional(),
    z_index: z.number().nullable().optional(),
    visible_default: z.boolean().nullable().optional(),
    style: z.record(z.string(), z.unknown()).nullable().optional(),
    feature_count: z.number().optional(),
  })
  .passthrough();
const wireLayers = z.object({ layers: z.array(wireLayer) }).passthrough();

const wireGeometry = z.object({ type: z.string(), coordinates: z.unknown() }).passthrough();
const wireFeature = z
  .object({
    type: z.literal("Feature"),
    id: z.string(),
    geometry: wireGeometry.nullable(),
    properties: z.record(z.string(), z.unknown()).default({}),
  })
  .passthrough();
const wireFeatureCollection = z
  .object({ type: z.literal("FeatureCollection"), features: z.array(wireFeature) })
  .passthrough();

function literalColour(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function mapLayer(l: z.infer<typeof wireLayer>): MapLayer {
  const st = l.style ?? {};
  return {
    key: l.key,
    name: typeof l.name === "string" ? l.name : (l.name?.en ?? l.key),
    level: l.level ?? undefined,
    zIndex: l.z_index ?? 0,
    visibleDefault: l.visible_default ?? true,
    style: {
      fill: literalColour(st.fill),
      stroke: literalColour(st.stroke),
      widthPx: typeof st.width_px === "number" ? st.width_px : undefined,
      opacity: typeof st.opacity === "number" ? st.opacity : undefined,
    },
    featureCount: l.feature_count ?? 0,
  };
}

/* ---- compliance (insight.py compliance / rule_status) ----------------------------- */

const wireCompliance = z
  .object({
    readiness: z.array(
      z
        .object({ framework: z.string(), total: z.number(), met: z.number(), overdue: z.number(), pct: num.nullable() })
        .passthrough(),
    ),
    obligations: z.array(
      z
        .object({
          framework: z.string(),
          title: z.string(),
          period: z.string().nullable().optional(),
          period_key: z.string().nullable().optional(),
          status: z.enum(OBLIGATION_STATUSES).catch("pending"),
          due_at: iso.nullable().optional(),
          met_at: iso.nullable().optional(),
          evidence_count: z.number().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

const wireRules = z
  .object({
    rules: z.array(
      z
        .object({
          key: z.string(),
          name: z.string(),
          class: z.string().nullable().optional(),
          kind: z.string().nullable().optional(),
          severity: z.number().nullable().optional(),
          enabled: z.boolean().nullable().optional(),
          verify_required: z.boolean().nullable().optional(),
          parameters: z.record(z.string(), z.unknown()).nullable().optional(),
          pack_defaults: z.record(z.string(), z.unknown()).nullable().optional(),
          confirmed_against: z.string().nullable().optional(),
          verified_at: iso.nullable().optional(),
          reference: z.string().nullable().optional(),
          trigger: z.string().nullable().optional(),
          gated_reason: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

const wireVerify = z
  .object({
    key: z.string(),
    enabled: z.boolean(),
    parameters: z.record(z.string(), z.unknown()).nullable().optional(),
    pack_defaults: z.record(z.string(), z.unknown()).nullable().optional(),
    confirmed_against: z.string(),
    verified_at: iso.nullable().optional(),
    note: z.string().nullable().optional(),
  })
  .passthrough();

/* ---- library (library.py) -------------------------------------------------------- */

const wireDocRow = z
  .object({
    id: z.string(),
    title: z.string(),
    reference: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    kind: z.string().nullable().optional(),
    status: z.enum(DOCUMENT_STATUSES).catch("issued"),
    classification: z.enum(["public", "internal", "restricted"]).catch("internal"),
    site_id: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    created_at: iso,
    revision: z.string().nullable().optional(),
    issued_on: z.string().nullable().optional(),
    expires_on: z.string().nullable().optional(),
    review_due_on: z.string().nullable().optional(),
    revision_status: z.enum(REVISION_STATUSES).nullable().optional(),
    revisions: z.number().nullable().optional(),
    /* build 2: what a register row needs (absent from a platform older than 0086) */
    category: z.string().nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    revision_id: z.string().nullable().optional(),
    revision_attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    has_file: z.boolean().nullable().optional(),
    latest_id: z.string().nullable().optional(),
    latest_revision: z.string().nullable().optional(),
    latest_status: z.enum(REVISION_STATUSES).nullable().optional(),
    latest_issued_on: z.string().nullable().optional(),
    latest_expires_on: z.string().nullable().optional(),
    latest_at: iso.nullable().optional(),
    latest_has_file: z.boolean().nullable().optional(),
  })
  .passthrough();

const wireDocs = z.object({ documents: z.array(wireDocRow) }).passthrough();

const wireRevision = z
  .object({
    id: z.string(),
    revision: z.string(),
    sequence: z.number(),
    status: z.enum(REVISION_STATUSES).catch("draft"),
    issued_on: z.string().nullable().optional(),
    effective_from: iso.nullable().optional(),
    superseded_at: iso.nullable().optional(),
    expires_on: z.string().nullable().optional(),
    review_due_on: z.string().nullable().optional(),
    change_note: z.string().nullable().optional(),
    rejected_reason: z.string().nullable().optional(),
    created_at: iso,
    author: z.string().nullable().optional(),
    approved_by_name: z.string().nullable().optional(),
    approved_at: iso.nullable().optional(),
    sha256: z.string().nullable().optional(),
    bytes: z.number().nullable().optional(),
    content_type: z.string().nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough();

const wireDocDetail = z
  .object({
    document: wireDocRow.extend({
      owner_id: z.string().nullable().optional(),
      withdrawn_at: iso.nullable().optional(),
      withdrawn_reason: z.string().nullable().optional(),
      retention_until: z.string().nullable().optional(),
      attrs: z.record(z.string(), z.unknown()).nullable().optional(),
      current_revision_id: z.string().nullable().optional(),
    }),
    revisions: z.array(wireRevision).default([]),
    links: z
      .array(
        z
          .object({ entity_kind: z.string(), entity_id: z.string(), relation: z.string().nullable().optional() })
          .passthrough(),
      )
      .default([]),
    acknowledgements: z
      .array(
        z
          .object({
            revision: z.string(),
            revision_status: z.enum(REVISION_STATUSES).catch("issued"),
            display_name: z.string(),
            required: z.boolean().nullable().optional(),
            acknowledged_at: iso.nullable().optional(),
            is_current: z.boolean().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();

const wireDocTypes = z
  .object({
    types: z.array(
      z
        .object({
          key: z.string(),
          name: z
            .union([z.string(), z.object({ en: z.string().optional() }).passthrough()])
            .nullable()
            .optional(),
          description: z
            .union([z.string(), z.object({ en: z.string().optional() }).passthrough()])
            .nullable()
            .optional(),
          category: z.string().nullable().optional(),
          controlled: z.boolean().nullable().optional(),
          requires_approval: z.boolean().nullable().optional(),
          review_months: z.number().nullable().optional(),
          expires: z.boolean().nullable().optional(),
          retention_years: z.number().nullable().optional(),
          default_classification: z.enum(["public", "internal", "restricted"]).nullable().optional(),
          pack: z.string(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

function mapDocRow(d: z.infer<typeof wireDocRow>): DocumentRow {
  return {
    id: d.id,
    title: d.title,
    reference: d.reference ?? undefined,
    type: d.type ?? d.kind ?? "document",
    status: d.status,
    classification: d.classification,
    siteId: d.site_id ?? undefined,
    tags: d.tags ?? [],
    createdAt: d.created_at,
    revision: d.revision ?? undefined,
    issuedOn: d.issued_on ?? undefined,
    expiresOn: d.expires_on ?? undefined,
    reviewDueOn: d.review_due_on ?? undefined,
    revisionStatus: d.revision_status ?? undefined,
    revisions: d.revisions ?? 0,
    category: d.category ?? undefined,
    attrs: d.attrs ?? undefined,
    revisionId: d.revision_id ?? undefined,
    revisionAttrs: d.revision_attrs ?? undefined,
    hasFile: d.has_file ?? undefined,
    latest:
      d.latest_id && d.latest_status
        ? {
            id: d.latest_id,
            revision: d.latest_revision ?? "",
            status: d.latest_status,
            issuedOn: d.latest_issued_on ?? undefined,
            expiresOn: d.latest_expires_on ?? undefined,
            at: d.latest_at ?? d.created_at,
            hasFile: d.latest_has_file ?? false,
          }
        : undefined,
  };
}

/* Writes — `create_document`, `add_revision`, `transition_revision`, `acknowledge`. */
const wireNewDoc = z
  .object({
    id: z.string(),
    title: z.string(),
    reference: z.string().nullable().optional(),
    classification: z.enum(["public", "internal", "restricted"]).catch("internal"),
    status: z.enum(DOCUMENT_STATUSES).catch("draft"),
    controlled: z.boolean().nullable().optional(),
  })
  .passthrough();

const wireNewRev = z
  .object({
    id: z.string(),
    revision: z.string(),
    sequence: z.number(),
    status: z.enum(REVISION_STATUSES).catch("draft"),
    review_due_on: iso.nullable().optional(),
    expires_on: iso.nullable().optional(),
  })
  .passthrough();

const wireRevTransition = z
  .object({ revision_id: z.string(), from: z.enum(REVISION_STATUSES), to: z.enum(REVISION_STATUSES) })
  .passthrough();

const wireAck = z.object({ acknowledged: z.boolean() }).passthrough();

function text(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "en" in v && typeof (v as { en?: unknown }).en === "string")
    return (v as { en: string }).en;
  return undefined;
}

/* ---- capital projects (projects.py) ----------------------------------------------
 *
 * Written from `projects.py` and the services it calls — `portfolio.py`,
 * `gates.py`, `inspections.py`, `attention.py` — plus migration 0019.
 *
 * Two shapes in here are worth naming, because both cost time to discover:
 *
 * `gates` is a COUNT on a portfolio row and an OBJECT on the detail. The
 * listing query counts them; `get_project` replaces the field with
 * `gates_svc.listing(...)`. So the row and the detail are built from a shared
 * base rather than one extending the other, which would parse the detail's
 * gate listing as a number and fail on a field that is present and correct.
 *
 * `items` is the same trap one level down: a count on an inspection listing
 * row, the array of sheet items on `get_inspection`.
 */

const wireSchedule = z
  .object({
    state: z.enum(["on_baseline", "ahead", "slipped", "no_baseline", "no_current_date", "no_dates"]).catch("no_dates"),
    why: z.string().nullable().optional(),
    baseline_start: z.string().nullable().optional(),
    baseline_end: z.string().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    end_slip_days: num.nullable().optional(),
    start_slip_days: num.nullable().optional(),
    overdue_days: num.nullable().optional(),
    rebaselined: num.nullable().optional(),
    rebaseline_note: z.string().nullable().optional(),
  })
  .passthrough();

const wireMoney = z
  .object({
    currency: z.string().nullable().optional(),
    /* bigint minor units; `num` because a bigint may arrive as a string. */
    budget_minor: num.nullable().optional(),
    committed_minor: num.nullable().optional(),
    spent_minor: num.nullable().optional(),
    state: z.enum(["declared", "not_declared"]).catch("not_declared"),
    note: z.string().nullable().optional(),
  })
  .passthrough();

const wireGateSet = z
  .object({
    key: z.string(),
    version: z.string().nullable().optional(),
    pack: z.string().nullable().optional(),
  })
  .passthrough();

/** Everything both the portfolio row and the detail carry. */
const wireProjectBase = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    category: z.string().nullable().optional(),
    domain: z.string().nullable().optional(),
    status: z.enum(PROJECT_STATUSES).catch("planned"),
    site_id: z.string().nullable().optional(),
    site_code: z.string().nullable().optional(),
    owner_id: z.string().nullable().optional(),
    contractor: z.string().nullable().optional(),
    schedule: wireSchedule,
    money: wireMoney,
    gate_set: wireGateSet.nullable().optional(),
    created_at: iso,
    updated_at: iso.nullable().optional(),
  })
  .passthrough();

const wireProjectRow = wireProjectBase.extend({
  gates: num.nullable().optional(),
  gates_approved: num.nullable().optional(),
  holding: num.nullable().optional(),
  work_stopped: z.boolean().nullable().optional(),
});

const wireProjects = z.object({ projects: z.array(wireProjectRow).default([]) }).passthrough();

const wireEvidenceState = z
  .object({
    state: z
      .enum(["none_required", "complete", "incomplete", "decided_without_evidence", "decided"])
      .catch("none_required"),
    required: z.array(z.string()).default([]),
    supplied: z.array(z.string()).default([]),
    missing: z.array(z.string()).default([]),
    why: z.string().nullable().optional(),
  })
  .passthrough();

const wireGate = z
  .object({
    id: z.string(),
    seq: num,
    key: z.string(),
    name: z.string(),
    status: z.enum(GATE_STATUSES).catch("pending"),
    due_date: z.string().nullable().optional(),
    submitted_at: iso.nullable().optional(),
    decided_at: iso.nullable().optional(),
    decided_by_name: z.string().nullable().optional(),
    decision_note: z.string().nullable().optional(),
    evidence_state: wireEvidenceState,
  })
  .passthrough();

const wireGateListing = z
  .object({
    gates: z.array(wireGate).default([]),
    count: num.default(0),
    decided: num.default(0),
    next_gate: z.string().nullable().default(null),
    decided_without_evidence: z.array(z.string()).default([]),
    state: z.enum(["no_gates", "in_progress", "all_decided"]).catch("no_gates"),
    why: z.string().nullable().optional(),
  })
  .passthrough();

const wireInspectionRowP = z
  .object({
    id: z.string(),
    reference: z.string(),
    title: z.string(),
    kind: z.enum(INSPECTION_KINDS).catch("surveillance"),
    discipline: z.string(),
    checklist_key: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    scheduled_for: iso.nullable().optional(),
    performed_at: iso.nullable().optional(),
    performed_by_name: z.string().nullable().optional(),
    result: z.enum(INSPECTION_RESULTS).nullable().optional(),
    released_at: iso.nullable().optional(),
    released_by_name: z.string().nullable().optional(),
    witnessed_by: z.string().nullable().optional(),
    items: num.default(0),
    not_checked: num.default(0),
    failed: num.default(0),
    defects: num.default(0),
    stopping_work: z.boolean().nullable().optional(),
    holding_because: z.string().nullable().optional(),
    created_at: iso,
  })
  .passthrough();

const wireInspectionListing = z.object({ inspections: z.array(wireInspectionRowP).default([]) }).passthrough();

const wireHoldPoint = z
  .object({
    id: z.string(),
    reference: z.string(),
    title: z.string(),
    discipline: z.string(),
    location: z.string().nullable().optional(),
    kind: z.enum(INSPECTION_KINDS).catch("hold"),
    result: z.enum(INSPECTION_RESULTS).nullable().optional(),
    scheduled_for: iso.nullable().optional(),
    performed_at: iso.nullable().optional(),
    created_at: iso,
    project_id: z.string(),
    project_code: z.string(),
    project_name: z.string().nullable().optional(),
    project_status: z.enum(PROJECT_STATUSES).nullable().optional(),
    site_id: z.string().nullable().optional(),
    site_code: z.string().nullable().optional(),
    items: num.default(0),
    not_checked: num.default(0),
    failed: num.default(0),
    holding_because: z.string().default(""),
    awaiting_release_only: z.boolean().nullable().optional(),
  })
  .passthrough();

const wireHoldPoints = z
  .object({
    hold_points: z.array(wireHoldPoint).default([]),
    count: num.default(0),
    hold_points_ever: num.default(0),
    state: z.enum(["work_stopped", "nothing_holding"]).catch("nothing_holding"),
    note: z.string().nullable().optional(),
  })
  .passthrough();

const wireProjectLink = z
  .object({
    entity_kind: z.string(),
    entity_id: z.string(),
    relation: z.string().nullable().optional(),
    entity_ref: z.string().nullable().optional(),
    entity_name: z.string().nullable().optional(),
  })
  .passthrough();

const wireRebaseline = z
  .object({
    baseline_start: z.string().nullable().optional(),
    baseline_end: z.string().nullable().optional(),
    replaced_at: iso.nullable().optional(),
    replaced_by: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
  })
  .passthrough();

const wireProjectDetail = wireProjectBase.extend({
  description: z.string().nullable().optional(),
  consultant: z.string().nullable().optional(),
  owner_name: z.string().nullable().optional(),
  rebaselines: z.array(wireRebaseline).default([]),
  gates: wireGateListing,
  inspections: wireInspectionListing,
  links: z.array(wireProjectLink).default([]),
  hold_points: wireHoldPoints,
});

const wireQuality = z
  .object({
    state: z.enum(["no_items", "not_started", "incomplete", "complete"]).catch("no_items"),
    items: num.default(0),
    passed: num.default(0),
    failed: num.default(0),
    not_applicable: num.default(0),
    not_checked: num.default(0),
    why: z.string().nullable().optional(),
  })
  .passthrough();

const wireSheetItem = z
  .object({
    seq: num,
    item_key: z.string().nullable().optional(),
    prompt: z.string().nullable().optional(),
    result: z.enum(ITEM_RESULTS).catch("not_checked"),
    value_num: num.nullable().optional(),
    value_text: z.string().nullable().optional(),
    tolerance: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    defect_id: z.string().nullable().optional(),
    defect_status: z.string().nullable().optional(),
    defect_severity: num.nullable().optional(),
  })
  .passthrough();

const wireInspectionDetailP = wireInspectionRowP.omit({ items: true, not_checked: true, failed: true }).extend({
  project_id: z.string(),
  project_code: z.string(),
  project_name: z.string().nullable().optional(),
  site_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  items: z.array(wireSheetItem).default([]),
  quality: wireQuality,
});

const wireVocabularyTerm = z
  .object({
    key: z.string(),
    name: z.string().nullable().optional(),
    pack: z.string().nullable().optional(),
    domain: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    default_gate_set: z.string().nullable().optional(),
  })
  .passthrough();

const wireVocabulary = z
  .object({
    gate_sets: z
      .array(
        z
          .object({
            key: z.string(),
            version: z.string().nullable().optional(),
            name: z.string().nullable().optional(),
            pack: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            gates: z
              .array(
                z
                  .object({
                    key: z.string(),
                    name: z.string().nullable().optional(),
                    requires: z.array(z.string()).default([]),
                    description: z.string().nullable().optional(),
                  })
                  .passthrough(),
              )
              .default([]),
          })
          .passthrough(),
      )
      .default([]),
    disciplines: z.array(wireVocabularyTerm).default([]),
    categories: z.array(wireVocabularyTerm).default([]),
    issues: z.array(z.string()).default([]),
  })
  .passthrough();

/* attention.py answers nine lists of differently-shaped rows under one
   envelope. They are read leniently and mapped field by field: a schema per
   list would be nine schemas that must each be revisited when a column is
   added to a query nobody remembered was here. */
const wireAttention = z
  .object({
    as_of: z.string().nullable().optional(),
    site_id: z.string().nullable().optional(),
    counts: z.record(z.string(), num).default({}),
    note: z.string().nullable().optional(),
  })
  .catchall(z.unknown());

/* Writes. */
const wireGateSubmit = z
  .object({
    key: z.string(),
    status: z.enum(GATE_STATUSES).catch("submitted"),
    submitted_at: iso.nullable().optional(),
    evidence_state: wireEvidenceState,
    warnings: z.array(z.string()).default([]),
  })
  .passthrough();

const wireGateDecision = z
  .object({
    key: z.string(),
    decision: z.enum(GATE_DECISIONS),
    decided_at: iso.nullable().optional(),
    project_code: z.string().nullable().optional(),
    evidence_state: wireEvidenceState,
    out_of_order_after: z.array(z.string()).default([]),
    warnings: z.array(z.string()).default([]),
  })
  .passthrough();

const wireInspectionWrite = z
  .object({
    reference: z.string(),
    result: z.enum(INSPECTION_RESULTS).nullable().optional(),
    quality: wireQuality,
    performed_at: iso.nullable().optional(),
    released_at: iso.nullable().optional(),
    recorded: num.nullable().optional(),
    still_stopping_work: z.boolean().nullable().optional(),
    why: z.string().nullable().optional(),
    warnings: z.array(z.string()).default([]),
    note: z.string().nullable().optional(),
  })
  .passthrough();

function mapSchedule(s: z.infer<typeof wireSchedule>): Schedule {
  return {
    state: s.state,
    why: s.why ?? undefined,
    baselineStart: s.baseline_start ?? undefined,
    baselineEnd: s.baseline_end ?? undefined,
    startDate: s.start_date ?? undefined,
    endDate: s.end_date ?? undefined,
    endSlipDays: s.end_slip_days ?? undefined,
    startSlipDays: s.start_slip_days ?? undefined,
    overdueDays: s.overdue_days ?? undefined,
    rebaselined: s.rebaselined ?? 0,
    rebaselineNote: s.rebaseline_note ?? undefined,
  };
}

function mapMoney(m: z.infer<typeof wireMoney>): Money {
  return {
    currency: m.currency ?? undefined,
    budgetMinor: m.budget_minor ?? undefined,
    committedMinor: m.committed_minor ?? undefined,
    spentMinor: m.spent_minor ?? undefined,
    state: m.state,
    note: m.note ?? undefined,
  };
}

function mapGateSet(g: z.infer<typeof wireGateSet> | null | undefined) {
  return g ? { key: g.key, version: g.version ?? undefined, pack: g.pack ?? undefined } : undefined;
}

function mapProjectRow(p: z.infer<typeof wireProjectRow>): ProjectRow {
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    category: p.category ?? undefined,
    domain: p.domain ?? undefined,
    status: p.status,
    siteId: p.site_id ?? undefined,
    siteCode: p.site_code ?? undefined,
    ownerId: p.owner_id ?? undefined,
    contractor: p.contractor ?? undefined,
    schedule: mapSchedule(p.schedule),
    money: mapMoney(p.money),
    gateSet: mapGateSet(p.gate_set),
    gates: p.gates ?? 0,
    gatesApproved: p.gates_approved ?? 0,
    holding: p.holding ?? 0,
    workStopped: p.work_stopped ?? (p.holding ?? 0) > 0,
    createdAt: p.created_at,
    updatedAt: p.updated_at ?? undefined,
  };
}

function mapEvidence(e: z.infer<typeof wireEvidenceState>): Evidence {
  return {
    state: e.state,
    required: e.required,
    supplied: e.supplied,
    missing: e.missing,
    why: e.why ?? undefined,
  };
}

function mapGate(g: z.infer<typeof wireGate>): Gate {
  return {
    id: g.id,
    seq: g.seq,
    key: g.key,
    name: g.name,
    status: g.status,
    evidence: mapEvidence(g.evidence_state),
    dueDate: g.due_date ?? undefined,
    submittedAt: g.submitted_at ?? undefined,
    decidedAt: g.decided_at ?? undefined,
    decidedByName: g.decided_by_name ?? undefined,
    decisionNote: g.decision_note ?? undefined,
  };
}

function mapProjectInspection(i: z.infer<typeof wireInspectionRowP>): ProjectInspectionRow {
  return {
    id: i.id,
    reference: i.reference,
    title: i.title,
    kind: i.kind,
    discipline: i.discipline,
    checklistKey: i.checklist_key ?? undefined,
    location: i.location ?? undefined,
    scheduledFor: i.scheduled_for ?? undefined,
    performedAt: i.performed_at ?? undefined,
    performedByName: i.performed_by_name ?? undefined,
    result: i.result ?? undefined,
    releasedAt: i.released_at ?? undefined,
    releasedByName: i.released_by_name ?? undefined,
    witnessedBy: i.witnessed_by ?? undefined,
    items: i.items,
    notChecked: i.not_checked,
    failed: i.failed,
    defects: i.defects,
    stoppingWork: i.stopping_work ?? (i.kind === "hold" && !i.released_at),
    holdingBecause: i.holding_because ?? undefined,
    createdAt: i.created_at,
  };
}

function mapHoldPoint(h: z.infer<typeof wireHoldPoint>): HoldPoint {
  return {
    id: h.id,
    reference: h.reference,
    title: h.title,
    discipline: h.discipline,
    location: h.location ?? undefined,
    kind: h.kind,
    result: h.result ?? undefined,
    scheduledFor: h.scheduled_for ?? undefined,
    performedAt: h.performed_at ?? undefined,
    projectId: h.project_id,
    projectCode: h.project_code,
    projectName: h.project_name ?? undefined,
    projectStatus: h.project_status ?? undefined,
    siteId: h.site_id ?? undefined,
    siteCode: h.site_code ?? undefined,
    items: h.items,
    notChecked: h.not_checked,
    failed: h.failed,
    holdingBecause: h.holding_because,
    awaitingReleaseOnly: h.awaiting_release_only ?? false,
    createdAt: h.created_at,
  };
}

function mapQuality(q: z.infer<typeof wireQuality>): Quality {
  return {
    state: q.state,
    items: q.items,
    passed: q.passed,
    failed: q.failed,
    notApplicable: q.not_applicable,
    notChecked: q.not_checked,
    why: q.why ?? undefined,
  };
}

function mapInspectionWrite(w: z.infer<typeof wireInspectionWrite>): InspectionWriteResult {
  return {
    reference: w.reference,
    result: w.result ?? undefined,
    quality: mapQuality(w.quality),
    performedAt: w.performed_at ?? undefined,
    releasedAt: w.released_at ?? undefined,
    recorded: w.recorded ?? undefined,
    stillStoppingWork: w.still_stopping_work ?? undefined,
    why: w.why ?? undefined,
    warnings: w.warnings,
    note: w.note ?? undefined,
  };
}

/* Field readers for the attention lists, whose rows differ per list. */
function pickStr(row: Record<string, unknown>, key: string): string | undefined {
  const v = row[key];
  return typeof v === "string" ? v : undefined;
}
function pickNum(row: Record<string, unknown>, key: string): number | undefined {
  const v = row[key];
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
  return undefined;
}
function pickEnum<T extends string>(row: Record<string, unknown>, key: string, allowed: readonly T[]): T | undefined {
  const v = row[key];
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function mapAttentionRow(raw: unknown): AttentionRow {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    projectId: pickStr(row, "project_id") ?? "",
    projectCode: pickStr(row, "project_code"),
    projectName: pickStr(row, "project_name"),
    name: pickStr(row, "name"),
    status: pickEnum(row, "status", PROJECT_STATUSES),
    key: pickStr(row, "key"),
    seq: pickNum(row, "seq"),
    submittedAt: pickStr(row, "submitted_at"),
    dueDate: pickStr(row, "due_date"),
    daysWaiting: pickNum(row, "days_waiting"),
    overdue: typeof row.overdue === "boolean" ? row.overdue : undefined,
    inspectionId: pickStr(row, "inspection_id") ?? pickStr(row, "id"),
    reference: pickStr(row, "reference"),
    title: pickStr(row, "title"),
    kind: pickEnum(row, "kind", INSPECTION_KINDS),
    discipline: pickStr(row, "discipline"),
    result: pickEnum(row, "result", INSPECTION_RESULTS),
    performedAt: pickStr(row, "performed_at"),
    notChecked: pickNum(row, "not_checked"),
    items: pickNum(row, "items"),
    endDate: pickStr(row, "end_date"),
    baselineEnd: pickStr(row, "baseline_end"),
    startDate: pickStr(row, "start_date"),
    daysOverdue: pickNum(row, "days_overdue") ?? pickNum(row, "days_since_start"),
    endSlipDays: pickNum(row, "end_slip_days"),
    rebaselined: pickNum(row, "rebaselined"),
    holdingBecause: pickStr(row, "holding_because"),
  };
}

function mapAttention(a: z.infer<typeof wireAttention>): AttentionReport {
  const lists = {} as Record<AttentionKind, AttentionRow[]>;
  const counts = {} as Record<AttentionKind, number>;
  for (const kind of ATTENTION_KINDS) {
    const raw = (a as Record<string, unknown>)[kind];
    lists[kind] = Array.isArray(raw) ? raw.map(mapAttentionRow) : [];
    /* The platform's own count, not the length of the list: they agree today,
       and if they ever stop agreeing that is the platform's answer to report,
       not a discrepancy to paper over here. */
    counts[kind] = a.counts[kind] ?? lists[kind].length;
  }
  return { asOf: a.as_of ?? undefined, siteId: a.site_id ?? undefined, lists, counts, note: a.note ?? undefined };
}

function mapVocabularyTerm(t: z.infer<typeof wireVocabularyTerm>): VocabularyTerm {
  return {
    key: t.key,
    name: t.name ?? t.key,
    pack: t.pack ?? undefined,
    domain: t.domain ?? undefined,
    description: t.description ?? undefined,
    defaultGateSet: t.default_gate_set ?? undefined,
  };
}

/* ---- the adapter ---------------------------------------------------------------- */

export interface PlatformSourceOptions {
  baseUrl: string;
  apiKey?: string;
  devTenant?: string;
  devPrincipal?: string;
  token?: () => Promise<string | undefined>;
  fetchImpl?: typeof fetch;
}

const wireEvents = z.object({
  events: z.array(
    z.object({
      sequence: z.number(),
      type: z.string(),
      /* a connector or model event has no subject; the screens refresh either way */
      subject_kind: z.string().nullable(),
      subject_id: z.string().nullable(),
    }),
  ),
  cursor: z.number(),
  more: z.boolean(),
});

const wireClosureRecord = z
  .object({
    id: z.string(),
    reference: z.string().nullable().optional(),
    kind: z.enum(["planned", "unplanned"]).catch("unplanned"),
    extent: z.enum(["full", "partial"]).catch("full"),
    reason: z.string().nullable().optional(),
    reason_code: z.string().nullable().optional(),
    status: z.string(),
    /* null on a closure that has not started yet: a planned one, or one lifted before it began */
    started_at: z.string().nullable(),
    ended_at: z.string().nullable().optional(),
    planned_start: z.string().nullable().optional(),
    planned_end: z.string().nullable().optional(),
    from_chainage_m: z.number().nullable().optional(),
    to_chainage_m: z.number().nullable().optional(),
    zone_code: z.string().nullable().optional(),
    zone_id: z.string().nullable().optional(),
    /* the register sends the work order's number as a string; an object is accepted too */
    work_order: z
      .union([
        z.string(),
        z
          .object({ id: z.string(), number: z.string().nullable().optional(), title: z.string().nullable().optional() })
          .passthrough(),
      ])
      .nullable()
      .optional(),
    work_order_id: z.string().nullable().optional(),
    minutes: z.number().nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    declared_at: z.string().nullable().optional(),
    declared_by_name: z.string().nullable().optional(),
    ended_by_name: z.string().nullable().optional(),
  })
  .passthrough();

const wireDeclaredClosure = z
  .object({
    id: z.string(),
    status: z.string(),
    started_at: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
  })
  .passthrough();

const wireEndedClosure = z.object({ ended_at: z.string(), minutes: z.number().nullable().optional() }).passthrough();

const wireEvidenceList = z.object({
  evidence: z.array(
    z
      .object({
        id: z.string(),
        phase: z.string().nullable().optional(),
        created_at: iso,
        object_id: z.string().nullable().optional(),
        content_type: z.string().nullable().optional(),
      })
      .passthrough(),
  ),
});

/* ── stores wire shapes (routers/stores.py) ─────────────────────────────── */

/*
 * The register is sent under the web model's names (domain/stores.ts), so it
 * is checked for its outline — every list present, the platform's own flags —
 * and taken as it comes. A missing list would otherwise surface as a desk that
 * crashes on `.filter` of undefined rather than as a platform that answered
 * wrongly.
 */
/* build 4: the approval chain (routers/stores.py GET /v1/stores/approvals) */
const wireApprovalsBoard = z.object({
  currency: z.string(),
  me: z.string().optional(),
  chain: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      who: z.string(),
      limit: z.number().nullable().optional(),
      permission: z.string(),
      mine: z.boolean(),
    }),
  ),
  can: z.object({ raise: z.boolean(), order: z.boolean() }),
  requests: z.array(
    z
      .object({
        number: z.string(),
        kind: z.string().optional(),
        title: z.string().optional(),
        supplier: z.string().optional(),
        value: z.number().optional(),
        currency: z.string().optional(),
        why: z.string().optional(),
        source: z.string().optional(),
        emergency: z.boolean().optional(),
        raisedAt: z.string().optional(),
        raisedBy: z.string().optional(),
        status: z.string(),
        steps: z
          .array(
            z.object({
              step: z.string(),
              decision: z.enum(["approved", "rejected"]),
              by: z.string(),
              at: z.string(),
              note: z.string().optional(),
              limit: z.number().optional(),
              over: z.boolean().optional(),
            }),
          )
          .default([]),
        next: z.object({ key: z.string(), label: z.string(), who: z.string() }).nullable().optional(),
        mayAct: z.boolean(),
        whyNot: z.string().optional(),
        order: z
          .object({
            number: z.string(),
            at: z.string().optional(),
            by: z.string().optional(),
            supplier: z.string().optional(),
            value: z.number().optional(),
            status: z.string().optional(),
          })
          .optional(),
      })
      .passthrough(),
  ),
  awaitingMe: z.array(z.string()).default([]),
  stores: z.object({ waiting: z.number(), approved: z.number() }).default({ waiting: 0, approved: 0 }),
});

const wireStoresRegister = z
  .object({
    currency: z.string(),
    locations: z.array(z.object({ code: z.string(), name: z.string() }).passthrough()),
    categories: z.array(z.object({ key: z.string(), label: z.string() }).passthrough()).default([]),
    items: z.array(z.object({ sku: z.string(), bins: z.array(z.object({ location: z.string(), onHand: z.number() }).passthrough()) }).passthrough()),
    movements: z.array(z.object({ sku: z.string(), kind: z.string(), qty: z.number() }).passthrough()),
    reservations: z.array(z.object({ id: z.string(), sku: z.string() }).passthrough()),
    requisitions: z.array(z.object({ number: z.string() }).passthrough()),
    orders: z.array(z.object({ number: z.string() }).passthrough()),
    platform: z.object({
      held: z.boolean(),
      noteWaiting: z.boolean(),
      importedAt: z.string().nullable().optional(),
    }),
  })
  .passthrough();

/**
 * A step as the platform reads it: snake_case keys, nothing undefined. What a
 * line `serves` is kept as written — it is stored whole and handed back to
 * the web model under the same names.
 */
function toWireStep(value: unknown, key?: string): unknown {
  if (key === "serves") return value;
  if (Array.isArray(value)) return value.map((v) => toWireStep(v));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), toWireStep(v, k)]),
    );
  }
  return value;
}

/* ── dispatch wire shapes (routers/dispatch.py) ─────────────────────────── */
const wireDispatchMember = z.object({
  name: z.string(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  principal_id: z.string().nullable().optional(),
  employee_id: z.string().nullable().optional(),
});
const wireDispatchGroup = z.object({
  key: z.string(),
  name: z.string(),
  kind: z.string().nullable().optional(),
  members: z.array(wireDispatchMember).default([]),
});
const wireDispatchNotification = z.object({
  id: z.string(),
  recipient: z.string(),
  channel: z.enum(["sms", "email", "app"]),
  to: z.string().nullable().optional(),
  status: z.string(),
  error: z.string().nullable().optional(),
  event: z.string().nullable().optional(),
});
const wireDispatchResult = z.object({
  work_order: z.object({
    id: z.string(),
    number: z.string(),
    status: z.string(),
    crew: z.string().nullable().optional(),
  }),
  notifications: z.array(wireDispatchNotification).default([]),
  channels: z
    .object({
      sms: z.object({ configured: z.boolean() }).partial().optional(),
      email: z.object({ configured: z.boolean() }).partial().optional(),
    })
    .optional(),
});
function fromWireGroup(g: z.infer<typeof wireDispatchGroup>): DispatchGroup {
  return {
    key: g.key,
    name: g.name,
    kind: g.kind ?? undefined,
    members: g.members.map((m) => ({
      name: m.name,
      phone: m.phone ?? undefined,
      email: m.email ?? undefined,
      principalId: m.principal_id ?? undefined,
      employeeId: m.employee_id ?? undefined,
    })),
  };
}
function fromWireNotification(n: z.infer<typeof wireDispatchNotification>): DispatchNotification {
  return {
    id: n.id,
    recipient: n.recipient,
    channel: n.channel,
    to: n.to ?? undefined,
    status: n.status as DispatchNotification["status"],
    error: n.error ?? undefined,
    event: n.event ?? undefined,
  };
}

/* ── directory and rule wire shapes (routers/contacts.py) ─────────────────── */
const wirePoint = z.object({
  id: z.string().optional(),
  kind: z.enum(["mobile", "phone", "email", "whatsapp"]),
  label: z.string().nullable().optional(),
  value: z.string(),
  primary: z.boolean(),
  notify: z.boolean(),
});
const wirePerson = z.object({
  id: z.string(),
  org_id: z.string().nullable().optional(),
  name: z.string(),
  role: z.string().nullable().optional(),
  language: z.enum(["ar", "en"]),
  preferred: z.enum(["sms", "email", "app"]),
  principal_id: z.string().nullable().optional(),
  employee_id: z.string().nullable().optional(),
  hours: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  active: z.boolean(),
  points: z.array(wirePoint).default([]),
});
const wireDirectory = z.object({
  orgs: z.array(
    z.object({
      id: z.string(),
      site_id: z.string().nullable().optional(),
      name: z.string(),
      kind: z.string(),
      notes: z.string().nullable().optional(),
      active: z.boolean(),
      people: z.array(wirePerson).default([]),
    }),
  ),
  people: z.array(wirePerson).default([]),
  channels: z
    .object({
      sms: z.object({ configured: z.boolean() }).partial().optional(),
      email: z.object({ configured: z.boolean() }).partial().optional(),
    })
    .optional(),
});
const wireRecipient = z.object({ type: z.enum(["person", "org", "group"]), id: z.string() });
const wireRule = z.object({
  id: z.string(),
  site_id: z.string(),
  name: z.string(),
  active: z.boolean(),
  events: z.array(z.string()),
  match: z
    .object({
      priorities: z.array(z.number()).optional(),
      kinds: z.array(z.string()).optional(),
      zone_codes: z.array(z.string()).optional(),
      dispatch_groups: z.array(z.string()).optional(),
    })
    .default({}),
  recipients: z.array(wireRecipient).default([]),
  channels: z.array(z.enum(["sms", "email", "app"])),
  escalate_after_min: z.number().nullable().optional(),
  escalate_to: z.array(wireRecipient).default([]),
});
const wireMessage = z.object({
  id: z.string(),
  recipient: z.string(),
  channel: z.enum(["sms", "email", "app"]),
  to: z.string().nullable().optional(),
  status: z.string(),
  error: z.string().nullable().optional(),
  event: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  sent_at: z.string().nullable().optional(),
  read_at: z.string().nullable().optional(),
  rule: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
});
function fromWirePerson(p: z.infer<typeof wirePerson>): ContactPerson {
  return {
    id: p.id,
    orgId: p.org_id ?? undefined,
    name: p.name,
    role: p.role ?? undefined,
    language: p.language,
    preferred: p.preferred,
    principalId: p.principal_id ?? undefined,
    employeeId: p.employee_id ?? undefined,
    hours: p.hours ?? undefined,
    notes: p.notes ?? undefined,
    active: p.active,
    points: p.points.map((x) => ({
      id: x.id,
      kind: x.kind,
      value: x.value,
      label: x.label ?? undefined,
      primary: x.primary,
      notify: x.notify,
    })),
  };
}
function fromWireRule(r: z.infer<typeof wireRule>): NotifyRule {
  return {
    id: r.id,
    siteId: r.site_id,
    name: r.name,
    active: r.active,
    events: r.events as NoticeEvent[],
    match: {
      priorities: r.match.priorities,
      kinds: r.match.kinds,
      zoneCodes: r.match.zone_codes,
      dispatchGroups: r.match.dispatch_groups,
    },
    recipients: r.recipients,
    channels: r.channels,
    escalateAfterMin: r.escalate_after_min ?? undefined,
    escalateTo: r.escalate_to,
  };
}

/* ── the flight record wire shape (routers/aodb.py → movements/record.py `shape`) ── */
interface WireMovement {
  id: string;
  direction: "arrival" | "departure";
  carrier: string;
  number: string;
  registration?: string | null;
  class_key?: string | null;
  counterpart_node?: string | null;
  service_type?: string | null;
  service_date: string;
  status?: MovementStatus | null;
  scheduled_at?: string | null;
  estimated_at?: string | null;
  actual_at?: string | null;
  predicted_at?: string | null;
  prediction?: {
    delay_minutes: number;
    basis: string[];
    confidence: "high" | "medium" | "low";
    sample: number;
    model: string;
    at: string;
  } | null;
  milestones?: Record<string, string>;
  handling?: Record<string, string>;
  loads?: Record<string, number>;
  capacity?: number | null;
  delay_codes?: { code: string; minutes?: number; source?: string }[];
  linked_movement_id?: string | null;
  remarks?: string | null;
  cancelled_reason?: string | null;
  notices?: { at: string; source: string; by?: string | null; new_time: string; code?: string | null; note?: string | null }[] | null;
  flags?: Flag[];
  version: number;
  source?: string | null;
  provenance?: Record<string, { source: string; by?: string | null; at?: string | null }> | null;
}
function fromWireMovement(w: WireMovement): Movement {
  const u = <T>(v: T | null | undefined): T | undefined => (v == null ? undefined : v);
  return {
    id: w.id,
    direction: w.direction,
    carrier: w.carrier,
    number: w.number,
    registration: u(w.registration),
    classKey: u(w.class_key),
    counterpartNode: u(w.counterpart_node),
    serviceType: u(w.service_type),
    serviceDate: w.service_date,
    status: u(w.status),
    scheduledAt: u(w.scheduled_at),
    estimatedAt: u(w.estimated_at),
    actualAt: u(w.actual_at),
    predictedAt: u(w.predicted_at),
    prediction: w.prediction
      ? {
          delayMinutes: w.prediction.delay_minutes,
          basis: w.prediction.basis,
          confidence: w.prediction.confidence,
          sample: w.prediction.sample,
          model: w.prediction.model,
          at: w.prediction.at,
        }
      : undefined,
    milestones: w.milestones ?? {},
    handling: w.handling ?? {},
    loads: w.loads ?? {},
    capacity: u(w.capacity),
    delayCodes: w.delay_codes ?? [],
    linkedMovementId: u(w.linked_movement_id),
    remarks: u(w.remarks),
    cancelledReason: u(w.cancelled_reason),
    notices: (w.notices ?? []).map((n) => ({
      at: n.at,
      source: n.source,
      by: u(n.by),
      newTime: n.new_time,
      code: u(n.code),
      note: u(n.note),
    })),
    flags: w.flags ?? [],
    version: w.version,
    source: u(w.source),
    ...(w.provenance && Object.keys(w.provenance).length
      ? {
          provenance: Object.fromEntries(
            Object.entries(w.provenance).map(([k, p]) => [
              k,
              { source: p.source, ...(p.by ? { by: p.by } : {}), ...(p.at ? { at: p.at } : {}) },
            ]),
          ),
        }
      : {}),
  };
}

export class PlatformSource implements DataSource {
  readonly kind = "platform" as const;

  async listClosures(siteId: string, since?: string, opts?: ListClosuresOptions): Promise<Closure[]> {
    const raw = await this.http.get("/v1/closures", { site_id: siteId, since, limit: 500 });
    const d = parse(z.object({ closures: z.array(wireClosureRecord) }), raw, "closures");
    /* what has not started is not yet a closure anyone lost time to — the
       insight screens count days, and a planned row has none */
    const rows = opts?.includePlanned ? d.closures : d.closures.filter((c) => c.started_at && c.status !== "cancelled");
    return rows.map((c) => {
      const wo = c.work_order;
      return {
        id: c.id,
        reference: c.reference ?? undefined,
        kind: c.kind,
        extent: c.extent,
        reason: c.reason ?? "",
        reasonCode: c.reason_code ?? undefined,
        status: c.status,
        startedAt: c.started_at ?? c.planned_start ?? c.declared_at ?? "",
        endedAt: c.ended_at ?? undefined,
        plannedStart: c.planned_start ?? undefined,
        plannedEnd: c.planned_end ?? undefined,
        fromChainageM: c.from_chainage_m ?? undefined,
        toChainageM: c.to_chainage_m ?? undefined,
        zoneCode: c.zone_code ?? undefined,
        zoneId: c.zone_id ?? undefined,
        workOrder:
          typeof wo === "string"
            ? { id: c.work_order_id ?? "", number: wo }
            : wo
              ? { id: wo.id, number: wo.number ?? undefined, title: wo.title ?? undefined }
              : undefined,
        minutes: c.minutes ?? undefined,
        ...(c.attrs ? { attrs: c.attrs } : {}),
        ...(c.declared_at ? { declaredAt: c.declared_at } : {}),
        ...(c.declared_by_name ? { declaredBy: c.declared_by_name } : {}),
        ...(c.ended_by_name ? { endedBy: c.ended_by_name } : {}),
      };
    });
  }

  async listEvidence(subjectKind: string, subjectId: string): Promise<WorkOrderEvidence[]> {
    const raw = await this.http.get("/v1/evidence", { subject_kind: subjectKind, subject_id: subjectId });
    const d = parse(wireEvidenceList, raw, "evidence");
    return d.evidence.map((e) => ({
      id: e.id,
      phase: e.phase ?? undefined,
      createdAt: e.created_at,
      objectId: e.object_id ?? undefined,
      contentType: e.content_type ?? undefined,
    }));
  }

  async pollEvents(siteId: string, after: number, limit = 200): Promise<EventPage> {
    const raw = parse(wireEvents, await this.http.get("/v1/events", { site_id: siteId, after, limit }), "events");
    return {
      events: raw.events.map((e) => ({
        sequence: e.sequence,
        type: e.type,
        subjectKind: e.subject_kind ?? "",
        subjectId: e.subject_id ?? "",
      })),
      cursor: raw.cursor,
      more: raw.more,
    };
  }
  private readonly http: HttpClient;

  constructor(opts: PlatformSourceOptions) {
    this.http = new HttpClient(opts);
  }

  async bootstrap(): Promise<Bootstrap> {
    const b = parse(wireBootstrap, await this.http.get("/v1/console/bootstrap"), "bootstrap");
    return {
      tenant: b.tenant,
      principal: {
        name: b.identity.principal ?? b.identity.subject ?? "unknown",
        subject: b.identity.subject ?? undefined,
        method: b.identity.method,
        groups: b.identity.groups,
        permissions: b.permissions,
        accessLevel: b.identity.access_level,
      },
      sites: b.sites.map(mapSite),
      packs: b.packs,
      developmentAuth: b.development_auth,
      receivedAt: new Date().toISOString(),
    };
  }

  async listWorkOrders(q: WorkOrderQuery): Promise<WorkOrderPage> {
    const raw = await this.http.get("/v1/work-orders", { site_id: q.siteId, status: q.status });
    const page = parse(wireWorkOrderList, raw, "work orders");
    return {
      items: page.work_orders.map((w) => mapWorkOrder(w, q.siteId)),
      truncated: page.truncated,
      note: page.note,
    };
  }

  async getWorkOrder(id: string): Promise<WorkOrder> {
    const d = parse(
      wireWorkOrderDetail,
      await this.http.get(`/v1/work-orders/${encodeURIComponent(id)}`),
      "work order",
    );
    return {
      ...mapWorkOrder(d.work_order),
      tasks: d.tasks.map((t) => ({
        seq: t.seq,
        description: t.description,
        status: t.status,
        labourHours: t.labour_hours ?? undefined,
        completedAt: t.completed_at ?? undefined,
        note: t.note ?? undefined,
      })),
      defects: d.defects.map((x) => ({
        id: x.id,
        summary: x.summary,
        severity: x.severity,
        status: x.status,
        defectType: x.defect_type ?? undefined,
      })),
      evidence: d.evidence.map((e) => ({
        id: e.id,
        phase: e.phase ?? undefined,
        createdAt: e.created_at,
        objectId: e.object_id ?? undefined,
        contentType: e.content_type ?? undefined,
      })),
      allowedTransitions: d.allowed_transitions,
    };
  }

  async getWorkOrderHistory(id: string): Promise<WorkOrderEvent[]> {
    const h = parse(wireHistory, await this.http.get(`/v1/work-orders/${encodeURIComponent(id)}/history`), "history");
    return h.history.map((e, i) => ({
      id: `${id}-${i}`,
      workOrderId: id,
      at: e.occurred_at,
      by: e.principal ?? "—",
      from: e.from_status,
      to: e.to_status,
      note: e.note ?? undefined,
      kind: (e.attrs as { kind?: string } | null | undefined)?.kind === "note" ? ("note" as const) : undefined,
    }));
  }

  async addWorkOrderNote(id: string, text: string): Promise<void> {
    await this.http.post(`/v1/work-orders/${encodeURIComponent(id)}/notes`, { text });
  }

  async createWorkOrder(input: CreateWorkOrderInput): Promise<WorkOrder> {
    /* the caller's key when it has one, so its retry files the order once */
    const clientId = input.clientId || crypto.randomUUID();
    const stub = parse(
      wireWorkOrderStub,
      await this.http.post(
        "/v1/work-orders",
        {
          site_id: input.siteId,
          title: input.title,
          kind: input.kind,
          priority: input.priority,
          description: input.description,
          zone_code: input.zoneCode,
          asset_tag: input.assetTag,
          defect_ids: input.defectIds ?? [],
          sla_hours: input.slaHours,
          capex: input.capex ?? false,
          client_id: clientId,
          alert_id: input.alertId,
          area: input.area || undefined,
          trade: input.trade || undefined,
          contractor: input.contractor?.trim() || undefined,
        },
        clientId,
      ),
      "created work order",
    );
    return this.getWorkOrder(stub.id);
  }

  async transitionWorkOrder(id: string, input: TransitionInput, idempotencyKey?: string): Promise<WorkOrder> {
    parse(
      wireWorkOrderStub,
      await this.http.post(
        `/v1/work-orders/${encodeURIComponent(id)}/transition`,
        {
          to: input.to,
          note: input.note,
          labour_hours: input.labourHours,
          cost_actual: input.costActual,
          on_hold_reason: input.onHoldReason,
          allow_missing_evidence: input.allowMissingEvidence ?? false,
        },
        idempotencyKey || crypto.randomUUID(),
      ),
      "transition",
    );
    return this.getWorkOrder(id);
  }

  async listAssetsOfTypes(siteId: string, assetTypes: readonly string[]): Promise<FleetAssetRow[]> {
    if (!assetTypes.length) return [];
    const q = new URLSearchParams({
      site_id: siteId,
      /* One request, not eleven: the endpoint takes a comma-separated list. */
      asset_type: assetTypes.join(","),
      limit: "500",
    });
    const raw = (await this.http.get(`/v1/assets?${q}`)) as
      | {
          assets?: unknown[];
        }
      | unknown[];
    const rows = Array.isArray(raw) ? raw : (raw.assets ?? []);
    const parsed = parse(wireFleetAssets, rows, "fleet assets");
    return parsed.map((a) => ({
      id: a.id,
      tag: a.tag,
      name: a.name ?? undefined,
      status: a.status ?? undefined,
      assetType: a.asset_type,
      zoneCode: a.zone_code ?? undefined,
      conditionScore: a.condition_score ?? null,
      attrs: a.attrs ?? {},
    }));
  }

  async getEstateSummary(siteId: string, assetTypes: string[]): Promise<EstateSummary> {
    const q = new URLSearchParams([
      ["site_id", siteId],
      ...assetTypes.map((t) => ["asset_type", t] as [string, string]),
    ]);
    const d = parse(wireEstateSummary, await this.http.get(`/v1/assets/summary?${q}`), "estate summary");
    return {
      assetTypes: d.asset_types.map((t) => ({
        key: t.key,
        name: t.name,
        domain: t.domain ?? undefined,
        category: t.category ?? undefined,
        assets: t.assets,
        scored: t.scored,
        conditionMean: t.condition_mean ?? null,
        located: t.located,
        byStatus: t.by_status,
        byCriticality: t.by_criticality,
        attributes: Object.fromEntries(
          Object.entries(t.attributes).map(([k, v]) => [
            k,
            { values: v.values.map((x) => ({ value: String(x.value), count: x.count })), notRecorded: v.not_recorded },
          ]),
        ),
      })),
      unknownAssetTypes: d.unknown_asset_types,
      assetsTotal: d.assets_total,
      defects: {
        open: d.defects.open,
        raised30d: d.defects.raised_30d,
        resolved30d: d.defects.resolved_30d,
        assetsWithOpen: d.defects.assets_with_open,
        openListTotal: d.defects.open_list_total,
        openList: d.defects.open_list.map((x) => ({
          id: x.id,
          assetTag: x.asset_tag,
          assetType: x.asset_type,
          defectType: x.defect_type ?? undefined,
          defectTypeName: x.defect_type_name ?? undefined,
          severity: x.severity,
          status: x.status,
          summary: x.summary,
          detectedAt: x.detected_at,
          dueAt: x.due_at ?? undefined,
          zoneCode: x.zone_code ?? undefined,
        })),
        byTypeSeverity: d.defects.by_type_severity.map((x) => ({
          defectType: x.defect_type,
          severity: x.severity,
          count: x.n,
          assets: x.assets,
        })),
      },
      alerts: {
        open: d.alerts.open,
        list: d.alerts.list.map((a) => ({
          id: a.id,
          title: a.title,
          kind: a.kind,
          severity: a.severity,
          status: a.status,
          raisedAt: a.raised_at,
          assetTag: a.asset_tag,
          assetType: a.asset_type,
        })),
      },
      readings: d.readings.map((r) => ({
        assetType: r.asset_type,
        measurement: r.measurement,
        measurementName: r.measurement_name,
        unit: r.unit ?? undefined,
        readings: r.readings,
        assets: r.assets,
        firstAt: r.first_at,
        latestAt: r.latest_at,
      })),
      latestReadings: d.latest_readings.map((r) => ({
        assetTag: r.asset_tag,
        assetType: r.asset_type,
        measurement: r.measurement,
        value: r.value_num,
        observedAt: r.observed_at,
        attrs: r.attrs ?? {},
      })),
    };
  }

  async listTestTypes(): Promise<TestType[]> {
    const d = parse(
      z.object({ types: z.array(wireTestType) }).passthrough(),
      await this.http.get("/v1/test-types"),
      "test types",
    );
    return d.types.map(mapTestType);
  }

  async listTestRecords(siteId: string): Promise<TestRecord[]> {
    const d = parse(
      z.object({ records: z.array(wireTestRecord) }).passthrough(),
      await this.http.get("/v1/test-records", { site_id: siteId }),
      "test records",
    );
    return d.records.map(mapTestRecord);
  }

  async getTestingSummary(siteId: string): Promise<TestingSummary> {
    const d = parse(
      z
        .object({
          types: z.array(
            z
              .object({
                test_type: z.string(),
                name: z.string(),
                frameworks: z.array(z.string()).default([]),
                mode: z.enum(["threshold", "ratio", "judged"]),
                interval_days: z.number().nullable().optional(),
                interval_status: z.enum(["proposed", "agreed"]).default("proposed"),
                records: z.number(),
                last: wireTestRecord.nullable(),
                next_due: z.string().nullable().optional(),
                state: z.enum(["overdue", "due_soon", "current", "no_interval", "never_tested"]),
              })
              .passthrough(),
          ),
          latest_outcomes: z.record(z.string(), z.number()),
          overdue: z.number(),
          due_soon: z.number(),
          never_tested: z.number(),
        })
        .passthrough(),
      await this.http.get("/v1/test-records/summary", { site_id: siteId }),
      "testing summary",
    );
    return {
      types: d.types.map((t) => ({
        testType: t.test_type,
        name: t.name,
        frameworks: t.frameworks,
        mode: t.mode,
        intervalDays: t.interval_days ?? undefined,
        intervalStatus: t.interval_status,
        records: t.records,
        last: t.last ? mapTestRecord(t.last) : null,
        nextDue: t.next_due ?? undefined,
        state: t.state,
      })),
      latestOutcomes: {
        fail: 0,
        attention: 0,
        pass: 0,
        not_assessed: 0,
        ...d.latest_outcomes,
      } as TestingSummary["latestOutcomes"],
      overdue: d.overdue,
      dueSoon: d.due_soon,
      neverTested: d.never_tested,
    };
  }

  async getTestRecord(id: string): Promise<TestRecordDetail> {
    const d = parse(wireTestDetail, await this.http.get(`/v1/test-records/${encodeURIComponent(id)}`), "test record");
    return {
      record: { ...mapTestRecord(d.record), thresholdsUsed: (d.record.thresholds_used ?? []).map(mapThreshold) },
      results: d.results.map(mapTestResult),
      files: d.files.map((f) => ({
        id: f.id,
        objectId: f.object_id,
        contentType: f.content_type ?? undefined,
        bytes: f.bytes ?? undefined,
        note: f.note ?? undefined,
        createdAt: f.created_at,
      })),
      comparison: d.comparison ? mapComparison(d.comparison) : null,
      readingsWritten: { count: d.readings_written?.n ?? 0, measurement: d.readings_written?.measurement ?? undefined },
    };
  }

  async reviseTestRecord(
    id: string,
    input: NewTestInput & { reason: string },
  ): Promise<{ id: string; reference: string; revision: number }> {
    return parse(
      z.object({ id: z.string(), reference: z.string(), revision: z.number() }).passthrough(),
      await this.http.post(`/v1/test-records/${encodeURIComponent(id)}/revise`, {
        reason: input.reason,
        performed_on: input.performedOn,
        title: input.title,
        performed_by: input.performedBy,
        method: input.method,
        device: input.device,
        scope: input.scope,
        summary: input.summary,
        results: input.results.map((r) => ({
          asset_tag: r.assetTag,
          location: r.location,
          value: r.value,
          specified: r.specified,
          attrs: r.attrs ?? {},
        })),
        outcome: input.outcome,
        outcome_basis: input.outcomeBasis,
        notes: input.notes,
        next_due: input.nextDue,
        source: input.source ?? "form",
        source_file: input.sourceFile,
      }),
      "revised test record",
    );
  }

  async getTestHistory(id: string): Promise<TestHistory> {
    const d = parse(
      z
        .object({
          reference: z.string(),
          revisions: z.array(
            z
              .object({
                id: z.string(),
                revision: z.number(),
                outcome,
                counts: z.record(z.string(), z.number()).default({}),
                amendment_reason: z.string().nullable().optional(),
                source: z.enum(["form", "file", "demo", "api"]).default("form"),
                source_file: z.string().nullable().optional(),
                created_at: z.string(),
                created_by_name: z.string().nullable().optional(),
                superseded_by: z.string().nullable().optional(),
                results: z.number(),
              })
              .passthrough(),
          ),
          actions: z.array(
            z
              .object({
                id: z.string(),
                kind: z.enum(["action_taken", "comment", "retest_planned", "work_order_raised"]),
                body: z.string(),
                asset_tags: z.array(z.string()).default([]),
                created_at: z.string(),
                created_by_name: z.string().nullable().optional(),
                revision: z.number(),
                work_order_id: z.string().nullable().optional(),
                work_order_number: z.string().nullable().optional(),
                work_order_status: z.string().nullable().optional(),
              })
              .passthrough(),
          ),
          log: z.array(wireLog),
        })
        .passthrough(),
      await this.http.get(`/v1/test-records/${encodeURIComponent(id)}/history`),
      "test history",
    );
    return {
      reference: d.reference,
      revisions: d.revisions.map((r) => ({
        id: r.id,
        revision: r.revision,
        outcome: r.outcome,
        counts: r.counts,
        amendmentReason: r.amendment_reason ?? undefined,
        source: r.source,
        sourceFile: r.source_file ?? undefined,
        createdAt: r.created_at,
        createdBy: r.created_by_name ?? undefined,
        supersededBy: r.superseded_by ?? undefined,
        results: r.results,
      })),
      actions: d.actions.map((a) => ({
        id: a.id,
        kind: a.kind,
        body: a.body,
        assetTags: a.asset_tags,
        createdAt: a.created_at,
        createdBy: a.created_by_name ?? undefined,
        revision: a.revision,
        workOrderId: a.work_order_id ?? undefined,
        workOrderNumber: a.work_order_number ?? undefined,
        workOrderStatus: a.work_order_status ?? undefined,
      })),
      log: d.log.map(mapLog),
    };
  }

  async addTestAction(
    id: string,
    input: { kind: ActionKind; body: string; workOrderId?: string; assetTags?: string[] },
  ): Promise<void> {
    await this.http.post(`/v1/test-records/${encodeURIComponent(id)}/actions`, {
      kind: input.kind,
      body: input.body,
      work_order_id: input.workOrderId,
      asset_tags: input.assetTags ?? [],
    });
  }

  async getTestingActivity(siteId: string): Promise<LogEntry[]> {
    const d = parse(
      z.object({ activity: z.array(wireLog) }).passthrough(),
      await this.http.get("/v1/test-records/activity", { site_id: siteId }),
      "testing activity",
    );
    return d.activity.map(mapLog);
  }

  async getAssetTestHistory(siteId: string, testType: string, assetTag: string): Promise<AssetHistoryPoint[]> {
    const d = parse(
      z
        .object({
          history: z.array(
            z
              .object({
                record_id: z.string(),
                reference: z.string(),
                revision: z.number(),
                performed_on: z.string(),
                demo: z.boolean(),
                value: z.number(),
                specified: z.number().nullable().optional(),
                judged_value: z.number().nullable().optional(),
                outcome,
                threshold_label: z.string().nullable().optional(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get("/v1/test-records/asset-history", {
        site_id: siteId,
        test_type: testType,
        asset_tag: assetTag,
      }),
      "asset test history",
    );
    return d.history.map((h) => ({
      recordId: h.record_id,
      reference: h.reference,
      revision: h.revision,
      performedOn: h.performed_on.slice(0, 10),
      demo: h.demo,
      value: h.value,
      specified: h.specified ?? undefined,
      judgedValue: h.judged_value ?? undefined,
      outcome: h.outcome,
      thresholdLabel: h.threshold_label ?? undefined,
    }));
  }

  async getLatestTest(siteId: string, testType: string): Promise<LatestTest> {
    const d = parse(
      z
        .object({
          latest: wireTestRecord.nullable(),
          record: wireTestRecord
            .extend({ thresholds_used: z.array(z.record(z.string(), z.unknown())).optional() })
            .optional(),
          results: z.array(wireTestResult).default([]),
          comparison: wireComparison.nullable().optional(),
        })
        .passthrough(),
      await this.http.get("/v1/test-records/latest", { site_id: siteId, test_type: testType }),
      "latest test",
    );
    return {
      latest: d.latest ? mapTestRecord(d.latest) : null,
      results: d.results.map(mapTestResult),
      comparison: d.comparison ? mapComparison(d.comparison) : null,
      thresholdsUsed: (d.record?.thresholds_used ?? []).map(mapThreshold),
    };
  }

  async createTestRecord(siteId: string, input: NewTestInput): Promise<{ id: string; reference: string }> {
    return parse(
      z.object({ id: z.string(), reference: z.string() }).passthrough(),
      await this.http.post("/v1/test-records", {
        site_id: siteId,
        test_type: input.testType,
        performed_on: input.performedOn,
        title: input.title,
        performed_by: input.performedBy,
        method: input.method,
        device: input.device,
        scope: input.scope,
        summary: input.summary,
        results: input.results.map((r) => ({
          asset_tag: r.assetTag,
          location: r.location,
          value: r.value,
          specified: r.specified,
          attrs: r.attrs ?? {},
        })),
        outcome: input.outcome,
        outcome_basis: input.outcomeBasis,
        notes: input.notes,
        next_due: input.nextDue,
        source: input.source ?? "form",
        source_file: input.sourceFile,
      }),
      "created test record",
    );
  }

  async uploadTestFile(recordId: string, file: File, note?: string): Promise<void> {
    const form = new FormData();
    form.append("file", file);
    form.append("subject_kind", "test_record");
    form.append("subject_id", recordId);
    form.append("phase", "reference");
    if (note) form.append("note", note);
    await this.http.postForm("/v1/evidence", form);
  }

  downloadFile(objectId: string): Promise<Blob> {
    return this.http.blob(`/v1/evidence/${encodeURIComponent(objectId)}/content`);
  }

  async getConsumption(
    siteId: string,
    assetTypes: string[],
    bucket: "day" | "week" | "month",
    days: number,
  ): Promise<ConsumptionPoint[]> {
    const q = new URLSearchParams([
      ["site_id", siteId],
      ["bucket", bucket],
      ["days", String(days)],
      ...assetTypes.map((t) => ["asset_type", t] as [string, string]),
    ]);
    const d = parse(
      z
        .object({
          series: z.array(
            z
              .object({
                asset_tag: z.string(),
                asset_type: z.string(),
                asset_type_name: z.string(),
                role: z.string().nullable().optional(),
                serves: z.string().nullable().optional(),
                measurement: z.string(),
                measurement_name: z.string(),
                unit: z.string().nullable().optional(),
                period: z.string(),
                consumed: z.number(),
                resets: z.number(),
                readings: z.number(),
                sources: z.array(z.string()).nullable().optional(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get(`/v1/readings/consumption?${q}`),
      "consumption",
    );
    return d.series.map((r) => ({
      assetTag: r.asset_tag,
      assetType: r.asset_type,
      assetTypeName: r.asset_type_name,
      role: r.role ?? undefined,
      serves: r.serves ?? undefined,
      measurement: r.measurement,
      measurementName: r.measurement_name,
      unit: r.unit ?? undefined,
      period: r.period,
      consumed: r.consumed,
      resets: r.resets,
      readings: r.readings,
      sources: r.sources ?? undefined,
    }));
  }

  async listMeters(siteId: string, assetTypes: string[]): Promise<MeterRegister> {
    const n = z.number().nullable().optional();
    const s = z.string().nullable().optional();
    const d = parse(
      z
        .object({
          counters: z.array(
            z
              .object({
                asset_id: z.string(),
                asset_tag: z.string(),
                name: s,
                asset_type: z.string(),
                asset_type_name: z.string(),
                role: s,
                serves: s,
                channel_id: s,
                point_key: s,
                reading_kind: z.enum(["cumulative", "interval"]).nullable().optional(),
                scale: n,
                modulus: n,
                stale_after_min: z.number(),
                rollovers: n,
                resets: n,
                measurement: s,
                unit: s,
                last_observed_at: s,
                last_value: n,
                last_source: s,
                age_min: n,
                status: z.enum(["live", "stale", "never"]),
              })
              .passthrough(),
          ),
          connector: z.object({ key: z.string(), name: z.string(), push_path: z.string() }).passthrough().nullable(),
        })
        .passthrough(),
      await this.http.get("/v1/counters", { site_id: siteId, asset_type: assetTypes }),
      "meters",
    );
    return {
      meters: d.counters.map((r) => ({
        assetId: r.asset_id,
        assetTag: r.asset_tag,
        name: r.name ?? undefined,
        assetType: r.asset_type,
        assetTypeName: r.asset_type_name,
        role: r.role ?? undefined,
        serves: r.serves ?? undefined,
        channelId: r.channel_id ?? null,
        pointKey: r.point_key ?? undefined,
        readingKind: r.reading_kind ?? undefined,
        scale: r.scale ?? undefined,
        modulus: r.modulus ?? undefined,
        staleAfterMin: r.stale_after_min,
        rollovers: r.rollovers ?? 0,
        resets: r.resets ?? 0,
        measurement: r.measurement ?? undefined,
        unit: r.unit ?? undefined,
        lastObservedAt: r.last_observed_at ?? undefined,
        lastValue: r.last_value ?? undefined,
        lastSource: r.last_source ?? undefined,
        ageMin: r.age_min ?? undefined,
        status: r.status,
      })),
      connector: d.connector
        ? { key: d.connector.key, name: d.connector.name, pushPath: d.connector.push_path }
        : null,
    };
  }

  async registerMeter(siteId: string, input: NewMeterInput): Promise<void> {
    const u = UTILITIES.find((x) => x.key === input.utility);
    if (!u) throw new Error(`unknown utility ${input.utility}`);
    await this.http.post(
      "/v1/counters",
      {
        site_id: siteId,
        asset_tag: input.assetTag,
        measurement: u.measurement,
        point_key: input.pointKey || null,
        reading_kind: input.readingKind,
        register_digits: input.registerDigits ?? null,
        scale: input.scale ?? 1,
        stale_after_min: input.staleAfterMin ?? 360,
        create_asset: input.createName
          ? { asset_type: u.assetType, name: input.createName, attrs: { meter_role: input.role ?? "sub" } }
          : null,
      },
      crypto.randomUUID(),
    );
  }

  async uploadMeterReadings(siteId: string, csv: string, filename?: string): Promise<MeterUploadResult> {
    const d = parse(
      z
        .object({
          received: z.number(),
          accepted: z.number(),
          duplicate: z.number(),
          rejected: z.number(),
          replayed: z.boolean(),
          rejections: z.array(z.object({ line: z.number().nullable().optional(), reason: z.string() }).passthrough()),
        })
        .passthrough(),
      await this.http.post("/v1/counters/upload", { site_id: siteId, csv, filename: filename ?? null }),
      "meter upload",
    );
    return d;
  }

  async listNotes(siteId: string, topic: string): Promise<SiteNote[]> {
    const d = parse(
      z
        .object({
          notes: z.array(
            z
              .object({
                id: z.string(),
                topic: z.string(),
                category: z.enum(["observation", "recommendation", "handover", "follow_up"]),
                body: z.string(),
                created_at: z.string(),
                asset_tag: z.string().nullable().optional(),
                created_by_name: z.string().nullable().optional(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get("/v1/notes", { site_id: siteId, topic }),
      "notes",
    );
    return d.notes.map((n) => ({
      id: n.id,
      topic: n.topic,
      category: n.category,
      body: n.body,
      assetTag: n.asset_tag ?? undefined,
      createdAt: n.created_at,
      createdBy: n.created_by_name ?? undefined,
    }));
  }

  async addNote(siteId: string, input: NewNoteInput): Promise<void> {
    await this.http.post("/v1/notes", {
      site_id: siteId,
      topic: input.topic,
      category: input.category,
      body: input.body,
      asset_tag: input.assetTag,
    });
  }

  async getPreference(key: string): Promise<unknown> {
    const r = await this.http.get<{ value?: unknown }>(`/v1/me/preferences/${encodeURIComponent(key)}`);
    return r?.value ?? null;
  }

  async setPreference(key: string, value: unknown): Promise<void> {
    await this.http.put(`/v1/me/preferences/${encodeURIComponent(key)}`, { value });
  }

  async deletePreference(key: string): Promise<void> {
    await this.http.delete(`/v1/me/preferences/${encodeURIComponent(key)}`);
  }

  /* ── dispatch ─────────────────────────────────────────────────────────── */

  async dispatchTeam(input: DispatchInput): Promise<DispatchResult> {
    const clientId = crypto.randomUUID();
    const raw = parse(
      wireDispatchResult,
      await this.http.post(
        "/v1/dispatch",
        {
          site_id: input.siteId,
          group_key: input.groupKey,
          group_name: input.groupName,
          group_kind: input.groupKind,
          title: input.title,
          description: input.description,
          zone_code: input.zoneCode,
          asset_tag: input.assetTag,
          lon: input.lon,
          lat: input.lat,
          where: input.where,
          client_id: clientId,
        },
        clientId,
      ),
      "dispatch",
    );
    const rows = raw.notifications.map(fromWireNotification);
    return {
      workOrder: {
        id: raw.work_order.id,
        number: raw.work_order.number,
        status: raw.work_order.status,
        crew: raw.work_order.crew ?? undefined,
      },
      notifications: rows,
      summary: summarise(rows),
      channels: raw.channels
        ? { sms: !!raw.channels.sms?.configured, email: !!raw.channels.email?.configured }
        : undefined,
    };
  }

  async listDispatchGroups(siteId: string): Promise<DispatchGroup[]> {
    const raw = parse(
      z.object({ items: z.array(wireDispatchGroup) }),
      await this.http.get("/v1/dispatch/groups", { site_id: siteId }),
      "dispatch groups",
    );
    return raw.items.map(fromWireGroup);
  }

  async saveDispatchGroup(siteId: string, group: DispatchGroup): Promise<DispatchGroup> {
    const raw = parse(
      wireDispatchGroup,
      await this.http.put(`/v1/dispatch/groups/${encodeURIComponent(group.key)}`, {
        site_id: siteId,
        name: group.name,
        kind: group.kind,
        members: group.members.map((m) => ({
          name: m.name,
          phone: m.phone || null,
          email: m.email || null,
          principal_id: m.principalId || null,
          employee_id: m.employeeId || null,
        })),
      }),
      "dispatch group",
    );
    return fromWireGroup(raw);
  }

  /* ── the flight record (routers/aodb.py) ──────────────────────────────── */

  async aodbDay(siteId: string, date?: string): Promise<{ serviceDate: string; covered: boolean; items: Movement[] }> {
    const raw = (await this.http.get("/v1/aodb/day", { site_id: siteId, date })) as {
      service_date: string;
      covered: boolean;
      items: WireMovement[];
    };
    return { serviceDate: raw.service_date, covered: raw.covered, items: raw.items.map(fromWireMovement) };
  }

  async aodbSummary(siteId: string, date?: string): Promise<DaySummary> {
    const r = (await this.http.get("/v1/aodb/summary", { site_id: siteId, date })) as Record<string, unknown> & {
      predicted_late: {
        id: string;
        flight: string;
        direction: "arrival" | "departure";
        delay_minutes: number;
        confidence: "high" | "medium" | "low";
      }[];
      flagged: { id: string; flight: string; flags: Flag[] }[];
    };
    return {
      serviceDate: String(r.service_date),
      movements: Number(r.movements),
      arrivals: Number(r.arrivals),
      departures: Number(r.departures),
      flown: Number(r.flown),
      cancelled: Number(r.cancelled),
      onTimePct: (r.on_time_pct as number | null) ?? null,
      averageDelayMin: (r.average_delay_min as number | null) ?? null,
      delayMinutesByCode: (r.delay_minutes_by_code as Record<string, number>) ?? {},
      predictedLate: r.predicted_late.map((x) => ({
        id: x.id,
        flight: x.flight,
        direction: x.direction,
        delayMinutes: x.delay_minutes,
        confidence: x.confidence,
      })),
      flagged: r.flagged,
      loadActual: Number(r.load_actual ?? 0),
    };
  }

  async aodbMovement(
    id: string,
  ): Promise<{ movement: Movement; history: MovementEvent[]; messages: MovementMessage[] }> {
    const r = (await this.http.get(`/v1/aodb/movements/${encodeURIComponent(id)}`)) as {
      movement: WireMovement;
      history: MovementEvent[];
      messages: {
        id: string;
        kind: string;
        raw: string;
        received_at: string;
        outcome: MovementMessage["outcome"];
        refusal?: string;
      }[];
    };
    return {
      movement: fromWireMovement(r.movement),
      history: r.history,
      messages: r.messages.map((m) => ({
        id: m.id,
        kind: m.kind,
        raw: m.raw,
        receivedAt: m.received_at,
        outcome: m.outcome,
        refusal: m.refusal ?? undefined,
        changes: [],
      })),
    };
  }

  async aodbChange(
    id: string,
    changes: Partial<Movement>,
    version?: number,
    note?: string,
  ): Promise<{ movement: Movement; changed: string[] }> {
    const wire: Record<string, unknown> = {};
    const names: Record<string, string> = {
      scheduledAt: "scheduled_at",
      estimatedAt: "estimated_at",
      actualAt: "actual_at",
      status: "status",
      registration: "registration",
      classKey: "class_key",
      counterpartNode: "counterpart_node",
      serviceType: "service_type",
      cancelledReason: "cancelled_reason",
      remarks: "remarks",
      milestones: "milestones",
      handling: "handling",
      loads: "loads",
      delayCodes: "delay_codes",
      capacity: "capacity",
    };
    for (const [k, v] of Object.entries(changes)) if (names[k]) wire[names[k]] = v;
    const r = (await this.http.patch(`/v1/aodb/movements/${encodeURIComponent(id)}`, {
      changes: wire,
      version,
      note,
    })) as {
      movement: WireMovement;
      changed: string[];
    };
    return { movement: fromWireMovement(r.movement), changed: r.changed };
  }

  async aodbNotice(
    siteId: string,
    movementId: string,
    n: { source: string; newTime: string; code?: string; note?: string },
    version?: number,
  ): Promise<{ movement: Movement; changed: string[] }> {
    const r = (await this.http.post(
      `/v1/aodb/movements/${encodeURIComponent(movementId)}/notices`,
      { site_id: siteId, source: n.source, new_time: n.newTime, code: n.code, note: n.note, version },
      crypto.randomUUID(),
    )) as { movement: WireMovement; changed: string[] };
    return { movement: fromWireMovement(r.movement), changed: r.changed };
  }

  /* ── build 6: the flight-record figures a person enters by hand ── */

  async aodbAdd(siteId: string, d: NewMovement): Promise<Movement> {
    const r = (await this.http.post(
      "/v1/aodb/movements",
      {
        site_id: siteId,
        carrier: d.carrier,
        number: d.number,
        direction: d.direction,
        service_date: d.serviceDate,
        scheduled_at: d.scheduledAt,
        counterpart_node: d.counterpartNode || undefined,
        class_key: d.classKey || undefined,
        registration: d.registration || undefined,
        service_type: d.serviceType || undefined,
        capacity: d.capacity ?? undefined,
        loads: d.loads ?? {},
        note: d.note || undefined,
      },
      crypto.randomUUID(),
    )) as WireMovement;
    return fromWireMovement(r);
  }

  private async aodbEntry(
    method: "post" | "put",
    id: string,
    what: string,
    body: Record<string, unknown>,
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }> {
    const path = `/v1/aodb/movements/${encodeURIComponent(id)}/${what}`;
    const r = (await (method === "post" ? this.http.post(path, body, crypto.randomUUID()) : this.http.put(path, body))) as {
      movement: WireMovement;
      changed: string[];
      replaced?: { field: string; source: string }[];
    };
    return { movement: fromWireMovement(r.movement), changed: r.changed, replaced: r.replaced ?? [] };
  }

  aodbActuals(id: string, times: Partial<Record<ActualKey, string>>, version?: number, note?: string) {
    return this.aodbEntry("post", id, "actuals", { ...times, version, note });
  }

  aodbLoads(
    id: string,
    loads: Partial<Record<string, number | null>>,
    opts: { capacity?: number | null; version?: number; note?: string } = {},
  ) {
    /* seats travel only when the person changed them: the platform reads a missing field as "leave it" */
    const body: Record<string, unknown> = { loads, version: opts.version, note: opts.note };
    if ("capacity" in opts) body.capacity = opts.capacity ?? null;
    return this.aodbEntry("post", id, "loads", body);
  }

  aodbDelayCodes(id: string, codes: { code: string; minutes?: number }[], version?: number, note?: string) {
    return this.aodbEntry("put", id, "delay-codes", { codes, version, note });
  }

  aodbTobt(id: string, tobt: string, opts: { givenBy?: string; version?: number; note?: string } = {}) {
    return this.aodbEntry("put", id, "tobt", { tobt, given_by: opts.givenBy, version: opts.version, note: opts.note });
  }

  async aodbSchedule(siteId: string): Promise<ScheduleLine[]> {
    const r = (await this.http.get("/v1/aodb/schedule", { site_id: siteId })) as { items: Record<string, unknown>[] };
    return r.items.map((x) => ({
      id: String(x.id),
      season: (x.season as string) ?? undefined,
      carrier: String(x.carrier),
      number: String(x.number),
      direction: x.direction as "arrival" | "departure",
      validFrom: String(x.valid_from),
      validTo: String(x.valid_to),
      days: x.days as number[],
      localTime: String(x.local_time),
      dayOffset: Number(x.day_offset ?? 0),
      counterpartNode: (x.counterpart_node as string) ?? undefined,
      classKey: (x.class_key as string) ?? undefined,
      serviceType: (x.service_type as string) ?? undefined,
      capacity: (x.capacity as number) ?? undefined,
      source: (x.source as string) ?? undefined,
    }));
  }

  async aodbImportSsim(
    siteId: string,
    ssim: string,
    season?: string,
  ): Promise<{ legs: number; lines: number; skipped: number }> {
    return (await this.http.post(
      "/v1/aodb/schedule/import",
      { site_id: siteId, ssim, season },
      crypto.randomUUID(),
    )) as {
      legs: number;
      lines: number;
      skipped: number;
    };
  }

  async aodbGenerate(
    siteId: string,
    from: string,
    to: string,
  ): Promise<{ created: number; alreadyThere: number; lines: number }> {
    const r = (await this.http.post(
      "/v1/aodb/generate",
      { site_id: siteId, date_from: from, date_to: to },
      crypto.randomUUID(),
    )) as {
      created: number;
      already_there: number;
      lines: number;
    };
    return { created: r.created, alreadyThere: r.already_there, lines: r.lines };
  }

  async aodbPostMessage(
    siteId: string,
    kind: "mvt" | "aidx",
    raw: string,
  ): Promise<{ outcome: MovementMessage["outcome"]; refusal?: string; changed: string[] }> {
    const r = (await this.http.post("/v1/aodb/messages", { site_id: siteId, kind, raw }, crypto.randomUUID())) as {
      outcome: MovementMessage["outcome"];
      refusal?: string | null;
      changed: string[];
    };
    return { outcome: r.outcome, refusal: r.refusal ?? undefined, changed: r.changed ?? [] };
  }

  async aodbMessages(siteId: string): Promise<MovementMessage[]> {
    const r = (await this.http.get("/v1/aodb/messages", { site_id: siteId })) as {
      items: {
        id: string;
        kind: string;
        raw: string;
        received_at: string;
        outcome: MovementMessage["outcome"];
        refusal?: string | null;
        changes: string[];
        movement?: string | null;
        movement_id?: string | null;
      }[];
    };
    return r.items.map((m) => ({
      id: m.id,
      kind: m.kind,
      raw: m.raw,
      receivedAt: m.received_at,
      outcome: m.outcome,
      refusal: m.refusal ?? undefined,
      changes: m.changes ?? [],
      movement: m.movement ?? undefined,
      movementId: m.movement_id ?? undefined,
    }));
  }

  async aodbPredict(siteId: string, date?: string): Promise<{ predicted: number; moved: number; flagged: number }> {
    return (await this.http.post("/v1/aodb/predict", { site_id: siteId, date }, crypto.randomUUID())) as {
      predicted: number;
      moved: number;
      flagged: number;
    };
  }

  /* ── flight data feeds (routers/flight_feeds.py) ─────────────────────── */

  async flightFeeds(siteId: string): Promise<{ items: FlightFeed[]; sample: boolean }> {
    const r = (await this.http.get("/v1/aodb/feeds", { site_id: siteId })) as { items: WireFeed[] };
    return { items: r.items.map(feedFromWire), sample: false };
  }

  async flightFeedMessages(siteId: string, opts?: { feedId?: string; outcome?: FeedOutcome }): Promise<FeedMessage[]> {
    const r = (await this.http.get("/v1/aodb/feeds/messages", {
      site_id: siteId,
      feed_id: opts?.feedId,
      outcome: opts?.outcome,
    })) as { items: WireFeedMessage[] };
    return r.items.map((m) => ({
      id: m.id,
      feedId: m.feed_id ?? undefined,
      feed: m.feed ?? undefined,
      kind: m.kind,
      channel: m.channel,
      outcome: m.outcome,
      reason: m.reason ?? undefined,
      raw: m.raw,
      envelope: m.envelope,
      ignored: m.ignored ?? [],
      changes: m.changes ?? [],
      movementId: m.movement_id ?? undefined,
      duplicateOf: m.duplicate_of ?? undefined,
      replayOf: m.replay_of ?? undefined,
      sentAt: m.sent_at ?? undefined,
      receivedAt: m.received_at,
    }));
  }

  async saveFlightFeed(siteId: string, draft: FeedDraft, id?: string): Promise<FlightFeed> {
    const c = draft.config;
    const config = {
      url: c.url,
      cursor_param: c.cursorParam,
      auth: c.auth && {
        kind: c.auth.kind,
        header: c.auth.header,
        prefix: c.auth.prefix,
        token_url: c.auth.tokenUrl,
        client_id: c.auth.clientId,
        scope: c.auth.scope,
      },
      secret_refs: c.secretRefs,
      imap: c.imap,
      mapping: c.mapping,
      poll_seconds: c.pollSeconds,
      quiet_minutes: c.quietMinutes,
      create_unknown: c.createUnknown,
    };
    const r = id
      ? ((await this.http.patch(`/v1/aodb/feeds/${encodeURIComponent(id)}`, {
          name: draft.name,
          enabled: draft.enabled,
          config,
        })) as WireFeed)
      : ((await this.http.post(
          "/v1/aodb/feeds",
          { site_id: siteId, key: draft.key, name: draft.name, kind: draft.kind, transport: draft.transport, enabled: draft.enabled, config },
          crypto.randomUUID(),
        )) as WireFeed);
    /* a feed just saved has no status of its own yet; the list refetch brings it */
    return feedFromWire({ ...r, status: r.status ?? emptyWireStatus() });
  }

  async testFlightFeed(id: string, sample?: string): Promise<FeedTestResult> {
    const r = (await this.http.post(`/v1/aodb/feeds/${encodeURIComponent(id)}/test`, { sample }, crypto.randomUUID())) as {
      ok: boolean;
      error?: string;
      note?: string;
      secrets: FeedTestResult["secrets"];
      results: WireFeedResult[];
    };
    return { ok: r.ok, error: r.error, note: r.note, secrets: r.secrets ?? {}, results: (r.results ?? []).map(resultFromWire) };
  }

  async replayFeedMessage(id: string): Promise<{ results: FeedResult[] }> {
    const r = (await this.http.post(`/v1/aodb/feeds/messages/${encodeURIComponent(id)}/replay`, {}, crypto.randomUUID())) as {
      results: WireFeedResult[];
    };
    return { results: r.results.map(resultFromWire) };
  }

  /*
   * ── resource allocation (routers/allocation.py) ──────────────────────
   * The engine's structures come back under the names the web engine uses,
   * so the two can be compared field for field.
   */

  /* ── passenger flow (routers/flow.py), under the web model's names ─── */

  async flowDay(siteId: string): Promise<FlowDay> {
    return (await this.http.get("/v1/flow/day", { site_id: siteId })) as FlowDay;
  }

  async flowObserve(siteId: string, obs: Omit<FlowObservation, "source" | "by">): Promise<{ ok: true }> {
    return (await this.http.post("/v1/flow/observations", { site_id: siteId, ...obs }, crypto.randomUUID())) as { ok: true };
  }

  async flowAdopt(siteId: string, code: string, steps: LanePlan["steps"], note?: string): Promise<LanePlan[]> {
    return (await this.http.put(`/v1/flow/plans/${encodeURIComponent(code)}`, { site_id: siteId, steps, note })) as LanePlan[];
  }

  async flowSaveCheckpoint(siteId: string, cp: Checkpoint): Promise<Checkpoint[]> {
    return (await this.http.put("/v1/flow/checkpoints", { site_id: siteId, ...cp })) as Checkpoint[];
  }

  /* ── the finance system (routers/finance.py); the answers are already in the web model's names ── */

  async financeConnection(siteId: string): Promise<FinanceConnection | null> {
    const raw = (await this.http.get("/v1/finance/connection", { site_id: siteId })) as {
      connection: FinanceConnection | null;
    };
    return raw.connection;
  }

  async saveFinanceConnection(siteId: string, input: FinanceConnectionInput): Promise<FinanceConnection> {
    const raw = (await this.http.put("/v1/finance/connection", { site_id: siteId, ...input })) as {
      connection: FinanceConnection;
    };
    return raw.connection;
  }

  async testFinanceConnection(siteId: string): Promise<FinanceTest> {
    return (await this.http.post("/v1/finance/connection/test", { site_id: siteId })) as FinanceTest;
  }

  async readFinanceNow(
    siteId: string,
    period: { fiscalYear?: string; fiscalPeriod?: string } = {},
  ): Promise<{ id: string; status: string; fiscalYear: string; fiscalPeriod: string }> {
    return (await this.http.post(
      "/v1/finance/read-now",
      { site_id: siteId, ...period },
      crypto.randomUUID(),
    )) as { id: string; status: string; fiscalYear: string; fiscalPeriod: string };
  }

  async financeStatus(siteId: string): Promise<FinanceStatus> {
    return (await this.http.get("/v1/finance/status", { site_id: siteId })) as FinanceStatus;
  }

  async financeLatest(siteId: string): Promise<FinanceLatest> {
    return (await this.http.get("/v1/finance/read", { site_id: siteId })) as FinanceLatest;
  }

  async importFinanceFile(siteId: string, fileName: string, text: string): Promise<FinanceImportResult> {
    return (await this.http.post(
      "/v1/finance/import",
      { site_id: siteId, fileName, text },
      crypto.randomUUID(),
    )) as FinanceImportResult;
  }

  async allocDay(siteId: string): Promise<AllocDay> {
    return (await this.http.get("/v1/allocation/day", { site_id: siteId })) as AllocDay;
  }

  async allocReplan(siteId: string, mode: "replan" | "plan"): Promise<Proposal> {
    return (await this.http.post("/v1/allocation/replan", { site_id: siteId, mode }, crypto.randomUUID())) as Proposal;
  }

  async allocDecide(siteId: string, proposalId: string, decision: "apply" | "reject", note?: string): Promise<{ applied: number }> {
    return (await this.http.post(
      `/v1/allocation/proposals/${encodeURIComponent(proposalId)}`,
      { site_id: siteId, decision, note },
      crypto.randomUUID(),
    )) as { applied: number };
  }

  async allocAssign(
    siteId: string,
    key: string,
    codes: string[],
    opts: { note?: string; force?: boolean } = {},
  ): Promise<{ refused: Refusal[]; learned?: string }> {
    return (await this.http.post(
      "/v1/allocation/assign",
      { site_id: siteId, key, codes, note: opts.note, force: !!opts.force },
      crypto.randomUUID(),
    )) as { refused: Refusal[]; learned?: string };
  }

  async allocRelease(siteId: string, key: string, note?: string): Promise<{ released: string }> {
    return (await this.http.post("/v1/allocation/release", { site_id: siteId, key, note }, crypto.randomUUID())) as {
      released: string;
    };
  }

  async allocAddResource(siteId: string, item: Resource): Promise<{ saved: string }> {
    return (await this.http.post("/v1/allocation/resources", { site_id: siteId, item }, crypto.randomUUID())) as {
      saved: string;
    };
  }

  async allocRegister(siteId: string): Promise<(Resource & { active: boolean })[]> {
    return (await this.http.get("/v1/allocation/resources", { site_id: siteId, all: "true" })) as (Resource & {
      active: boolean;
    })[];
  }

  async allocChangeResource(
    siteId: string,
    code: string,
    changes: Partial<Resource> & { active?: boolean },
    note?: string,
  ): Promise<{ resource: Resource & { active: boolean }; changed: string[] }> {
    return (await this.http.patch(`/v1/allocation/resources/${encodeURIComponent(code)}`, {
      site_id: siteId,
      changes,
      note,
    })) as { resource: Resource & { active: boolean }; changed: string[] };
  }

  async allocWhatIf(siteId: string, change: WhatIf): Promise<EngineResult> {
    return (await this.http.post("/v1/allocation/what-if", { site_id: siteId, change }, crypto.randomUUID())) as EngineResult;
  }

  async allocWindows(siteId: string, code: string, minutes: number, hours = 24): Promise<Window[]> {
    return (await this.http.get("/v1/allocation/windows", {
      site_id: siteId,
      code,
      minutes: String(minutes),
      hours: String(hours),
    })) as Window[];
  }

  async allocSetPolicy(siteId: string, policy: Policy): Promise<Policy[]> {
    return (await this.http.put(`/v1/allocation/policies/${policy.kind}`, { site_id: siteId, ...policy })) as Policy[];
  }

  async allocSaveRule(siteId: string, rule: AllocRule): Promise<AllocRule[]> {
    return (await this.http.put("/v1/allocation/rules", { site_id: siteId, ...rule })) as AllocRule[];
  }

  async allocDeleteRule(siteId: string, id: string): Promise<AllocRule[]> {
    return (await this.http.delete(`/v1/allocation/rules/${encodeURIComponent(id)}?site_id=${encodeURIComponent(siteId)}`)) as unknown as AllocRule[];
  }

  /* ── build 2: safety ── */
  /* Who and when are the platform's to stamp: nothing below sends a name,
     and a confidential or anonymous report goes out with nothing that could
     carry one. The filing POSTs send the record's key as Idempotency-Key, so
     a second press, or TRY AGAIN, is the same report. */

  async safetyVocabulary(): Promise<Safety.SafetyVocabulary> {
    return safetyWire.fromWireVocabulary(
      parse(safetyWire.wireVocabulary, await this.http.get("/v1/safety/vocabulary"), "safety vocabulary"),
    );
  }

  async listHazards(siteId: string): Promise<Safety.HazardPage> {
    return safetyWire.fromWireHazardPage(
      parse(
        safetyWire.wireHazardPage,
        await this.http.get("/v1/safety/hazards", { site_id: siteId, limit: 500 }),
        "hazard register",
      ),
    );
  }

  async getHazard(id: string): Promise<Safety.HazardDetail> {
    return safetyWire.fromWireHazardDetail(
      parse(safetyWire.wireHazardDetail, await this.http.get(`/v1/safety/hazards/${encodeURIComponent(id)}`), "hazard"),
    );
  }

  async raiseHazard(input: Safety.RaiseHazardInput, key?: string): Promise<{ id: string; reference: string }> {
    const attrs: Record<string, string> = {};
    if (input.area?.trim()) attrs.area = input.area.trim();
    if (input.ownerLabel?.trim()) attrs.owner_label = input.ownerLabel.trim();
    return parse(
      z.object({ id: z.string(), reference: z.string() }).passthrough(),
      await this.http.post(
        "/v1/safety/hazards",
        {
          site_id: input.siteId,
          category: input.category,
          title: input.title,
          description: input.description || undefined,
          origin: input.origin ?? "report",
          review_due: input.reviewDue || undefined,
          attrs,
        },
        key,
      ),
      "raised hazard",
    );
  }

  async assessHazard(id: string, input: Safety.AssessHazardInput): Promise<Safety.AssessedHazard> {
    return safetyWire.fromWireAssessed(
      parse(
        safetyWire.wireAssessed,
        await this.http.post(`/v1/safety/hazards/${encodeURIComponent(id)}/assessments`, {
          likelihood: input.likelihood,
          severity: input.severity,
          kind: input.kind,
          rationale: input.rationale || undefined,
          review_due: input.reviewDue || undefined,
        }),
        "assessment",
      ),
    );
  }

  async addHazardAction(
    id: string,
    input: Safety.HazardActionInput,
  ): Promise<{ seq: number; status: string; warnings: string[] }> {
    const r = parse(
      z.object({ seq: z.number(), status: z.string(), warnings: z.array(z.string()).nullish() }).passthrough(),
      await this.http.post(`/v1/safety/hazards/${encodeURIComponent(id)}/actions`, {
        description: input.description,
        control_type: input.controlType,
        due_date: input.dueDate || undefined,
      }),
      "hazard action",
    );
    return { seq: r.seq, status: r.status, warnings: r.warnings ?? [] };
  }

  async completeHazardAction(
    id: string,
    seq: number,
    status: "done" | "cancelled",
  ): Promise<{ hazardStatus: string; warnings: string[] }> {
    const r = parse(
      z.object({ hazard_status: z.string(), warnings: z.array(z.string()).nullish() }).passthrough(),
      await this.http.post(`/v1/safety/hazards/${encodeURIComponent(id)}/actions/${seq}/complete`, { status }),
      "completed action",
    );
    return { hazardStatus: r.hazard_status, warnings: r.warnings ?? [] };
  }

  async acceptHazardRisk(id: string, input: { rationale: string; reviewDue?: string }): Promise<{ note: string }> {
    const r = parse(
      z.object({ note: z.string().nullish() }).passthrough(),
      await this.http.post(`/v1/safety/hazards/${encodeURIComponent(id)}/accept-risk`, {
        rationale: input.rationale,
        review_due: input.reviewDue || undefined,
      }),
      "risk acceptance",
    );
    return { note: r.note ?? "" };
  }

  async closeHazard(id: string, input: Safety.CloseHazardInput): Promise<{ status: string; warnings: string[] }> {
    const r = parse(
      z.object({ status: z.string(), warnings: z.array(z.string()).nullish() }).passthrough(),
      await this.http.post(`/v1/safety/hazards/${encodeURIComponent(id)}/close`, {
        note: input.note,
        decision: input.decision,
        acceptance_rationale: input.acceptanceRationale || undefined,
      }),
      "closed hazard",
    );
    return { status: r.status, warnings: r.warnings ?? [] };
  }

  async listSafetyReports(siteId: string): Promise<Safety.SafetyReportPage> {
    return safetyWire.fromWireReportPage(
      parse(
        safetyWire.wireReportPage,
        await this.http.get("/v1/safety/reports", { site_id: siteId, limit: 500 }),
        "safety reports",
      ),
    );
  }

  async getSafetyReport(id: string): Promise<Safety.SafetyReport> {
    return safetyWire.fromWireReport(
      parse(safetyWire.wireReport, await this.http.get(`/v1/safety/reports/${encodeURIComponent(id)}`), "safety report"),
    );
  }

  async fileSafetyReport(input: Safety.FileReportInput, key?: string): Promise<Safety.FiledReport> {
    const attrs: Record<string, unknown> = { ...(input.attrs ?? {}) };
    if (input.kind) attrs.kind = input.kind;
    if (input.where?.trim()) attrs.where = input.where.trim();
    if (input.immediate) attrs.immediate = true;
    return safetyWire.fromWireFiled(
      parse(
        safetyWire.wireFiled,
        await this.http.post(
          "/v1/safety/reports",
          {
            site_id: input.siteId,
            title: input.title,
            narrative: input.narrative,
            confidentiality: input.confidentiality,
            category: input.category || undefined,
            occurred_at: input.occurredAt || undefined,
            hazard_id: input.hazardId || undefined,
            attrs,
          },
          key,
        ),
        "filed report",
      ),
    );
  }

  async setSafetyReportStatus(
    id: string,
    input: { status: Safety.ReportStatus; outcome?: string },
  ): Promise<{ reference: string; status: Safety.ReportStatus }> {
    const r = parse(
      z.object({ reference: z.string(), status: z.string() }).passthrough(),
      await this.http.post(`/v1/safety/reports/${encodeURIComponent(id)}/status`, {
        status: input.status,
        outcome: input.outcome?.trim() || undefined,
      }),
      "report status",
    );
    return { reference: r.reference, status: r.status as Safety.ReportStatus };
  }

  async linkSafetyReport(id: string, hazardId: string | null): Promise<{ reference: string; hazardReference: string | null }> {
    const r = parse(
      z.object({ reference: z.string(), hazard_reference: z.string().nullish() }).passthrough(),
      await this.http.post(`/v1/safety/reports/${encodeURIComponent(id)}/link`, { hazard_id: hazardId }),
      "report link",
    );
    return { reference: r.reference, hazardReference: r.hazard_reference ?? null };
  }

  async raiseHazardFromReport(
    id: string,
    input: { category: string; title: string; description?: string },
  ): Promise<{ id: string; reference: string; warning?: string }> {
    const r = parse(
      z.object({ id: z.string(), reference: z.string(), warning: z.string().nullish() }).passthrough(),
      await this.http.post(`/v1/safety/reports/${encodeURIComponent(id)}/hazard`, {
        category: input.category,
        title: input.title,
        description: input.description || undefined,
      }),
      "hazard from report",
    );
    return { id: r.id, reference: r.reference, ...(r.warning ? { warning: r.warning } : {}) };
  }

  async securityVocabulary(): Promise<Safety.IncidentCategory[]> {
    return safetyWire.fromWireIncidentCategories(
      parse(safetyWire.wireIncidentCategories, await this.http.get("/v1/security/vocabulary"), "security vocabulary"),
    );
  }

  async listSecurityIncidents(siteId: string): Promise<Safety.SecurityIncidentPage> {
    return safetyWire.fromWireIncidentPage(
      parse(
        safetyWire.wireIncidentPage,
        await this.http.get("/v1/security/incidents", { site_id: siteId, limit: 500 }),
        "security incidents",
      ),
    );
  }

  async raiseSecurityIncident(input: Safety.RaiseIncidentInput, key?: string): Promise<Safety.RaisedIncident> {
    return safetyWire.fromWireRaised(
      parse(
        safetyWire.wireRaised,
        await this.http.post(
          "/v1/security/incidents",
          {
            site_id: input.siteId,
            category: input.category,
            title: input.title,
            severity: input.severity,
            summary: input.summary || undefined,
            occurred_at: input.occurredAt || undefined,
            detail: input.detail ?? {},
          },
          key,
        ),
        "raised incident",
      ),
    );
  }

  async setSecurityIncidentStatus(
    id: string,
    input: { status: Safety.IncidentStatus; outcome?: string; note?: string },
  ): Promise<{ status: Safety.IncidentStatus; warnings: string[] }> {
    const r = parse(
      z.object({ status: z.string(), warnings: z.array(z.string()).nullish() }).passthrough(),
      await this.http.post(`/v1/security/incidents/${encodeURIComponent(id)}/status`, {
        status: input.status,
        outcome: input.outcome?.trim() || undefined,
        note: input.note || undefined,
      }),
      "incident status",
    );
    return { status: r.status as Safety.IncidentStatus, warnings: r.warnings ?? [] };
  }

  async linkSecurityIncident(id: string, input: { subjectKind: string; subjectId: string; note?: string }): Promise<void> {
    await this.http.post(`/v1/security/incidents/${encodeURIComponent(id)}/links`, {
      subject_kind: input.subjectKind,
      subject_id: input.subjectId,
      note: input.note,
    });
  }

  /* ── staff calls (routers/calls.py) ───────────────────────────────────── */

  async callsIce(): Promise<IceConfig> {
    return callsWire.fromWireIce(parse(callsWire.wireIce, await this.http.get("/v1/calls/ice"), "call servers"));
  }

  async callsDirectory(q?: string): Promise<CallDirectory> {
    const raw = parse(callsWire.wireDirectory, await this.http.get("/v1/calls/directory", { q: q || undefined }), "call directory");
    return callsWire.fromWireDirectory(raw);
  }

  async callsPoll(after: number, afterMsg = 0): Promise<CallPoll> {
    const raw = parse(
      callsWire.wirePoll,
      await this.http.get("/v1/calls/poll", { after, after_msg: afterMsg, device: "web" }),
      "calls",
    );
    return callsWire.fromWirePoll(raw);
  }

  private async callReply(raw: unknown, what: string): Promise<Call> {
    return callsWire.fromWireCall(parse(z.object({ call: callsWire.wireCall }), raw, what).call);
  }

  async callsCreate(
    invitees: string[],
    media: CallMedia,
    opts: CallStartOptions = {},
  ): Promise<{ call: Call; notes: string[] }> {
    const body: Record<string, unknown> = { invitees, media, title: opts.title?.trim() || null };
    /* v2 fields only when used, so a v1 platform reads the same body it always did */
    if (opts.join) body.join = callsWire.toWireJoin(opts.join);
    if (opts.urgent) body.urgent = true;
    const raw = parse(
      z.object({ call: callsWire.wireCall, notes: z.array(z.string()).nullish() }),
      await this.http.post("/v1/calls", body, crypto.randomUUID()),
      "call",
    );
    return { call: callsWire.fromWireCall(raw.call), notes: raw.notes ?? [] };
  }

  async callsAnswer(callId: string, video: boolean): Promise<Call> {
    return this.callReply(await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/answer`, { video }), "call");
  }

  async callsDecline(callId: string): Promise<Call> {
    return this.callReply(await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/decline`, {}), "call");
  }

  async callsLeave(callId: string): Promise<Call> {
    return this.callReply(await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/leave`, {}), "call");
  }

  async callsInvite(callId: string, invitees: string[]): Promise<Call> {
    return this.callReply(
      await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/invite`, { invitees }, crypto.randomUUID()),
      "call",
    );
  }

  async callsSignal(callId: string, to: string, kind: "offer" | "answer" | "ice", payload: unknown): Promise<void> {
    await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/signal`, { to, kind, payload });
  }

  async callsMedia(callId: string, audioOn: boolean, videoOn: boolean, sharing?: boolean): Promise<Call> {
    const body: Record<string, unknown> = { audio_on: audioOn, video_on: videoOn };
    if (sharing !== undefined) body.sharing = sharing;
    return this.callReply(await this.http.post(`/v1/calls/${encodeURIComponent(callId)}/media`, body), "call");
  }

  /* ── staff calls v2 ─────────────────────────────────────────────────── */

  private callPath(callId: string, tail: string): string {
    return `/v1/calls/${encodeURIComponent(callId)}/${tail}`;
  }

  /** A host action's reply: the call when the platform returns it, nothing when it returns only ok. */
  private maybeCall(raw: unknown): Call | undefined {
    const r = z.object({ call: callsWire.wireCall }).safeParse(raw);
    return r.success ? callsWire.fromWireCall(r.data.call) : undefined;
  }

  async callsDnd(): Promise<DndState> {
    const raw = parse(z.object({ dnd: callsWire.wireDnd }), await this.http.get("/v1/calls/me/dnd"), "do not disturb");
    return callsWire.fromWireDnd(raw.dnd);
  }

  async callsSetDnd(until: string | "forever" | null, allow: string[]): Promise<DndState> {
    const raw = parse(
      z.object({ dnd: callsWire.wireDnd }),
      await this.http.put("/v1/calls/me/dnd", { until, allow }),
      "do not disturb",
    );
    return callsWire.fromWireDnd(raw.dnd);
  }

  async callsJoin(callId: string): Promise<{ call: Call; state: "joined" | "waiting" }> {
    const raw = parse(
      z.object({ call: callsWire.wireCall, state: z.string().nullish() }),
      await this.http.post(this.callPath(callId, "join"), {}),
      "call",
    );
    return { call: callsWire.fromWireCall(raw.call), state: raw.state === "waiting" ? "waiting" : "joined" };
  }

  async callsSettings(callId: string, patch: CallSettingsPatch): Promise<Call> {
    const body: Record<string, unknown> = {};
    if (patch.join) body.join = callsWire.toWireJoin(patch.join);
    if (patch.locked !== undefined) body.locked = patch.locked;
    if (patch.title !== undefined) body.title = patch.title;
    return this.callReply(await this.http.post(this.callPath(callId, "settings"), body), "call");
  }

  async callsAdmit(callId: string, principalId: string, allow: boolean): Promise<Call> {
    return this.callReply(
      await this.http.post(this.callPath(callId, "admit"), { principal_id: principalId, allow }),
      "call",
    );
  }

  async callsRemove(callId: string, principalId: string): Promise<Call | undefined> {
    return this.maybeCall(await this.http.post(this.callPath(callId, "remove"), { principal_id: principalId }));
  }

  async callsMute(callId: string, who: string | "all"): Promise<Call | undefined> {
    return this.maybeCall(
      await this.http.post(this.callPath(callId, "mute"), who === "all" ? { all: true } : { principal_id: who }),
    );
  }

  async callsRole(callId: string, principalId: string, role: ParticipantRole): Promise<Call | undefined> {
    return this.maybeCall(await this.http.post(this.callPath(callId, "role"), { principal_id: principalId, role }));
  }

  async callsEnd(callId: string): Promise<Call | undefined> {
    return this.maybeCall(await this.http.post(this.callPath(callId, "end"), {}));
  }

  async callsHand(callId: string, up: boolean): Promise<Call> {
    return this.callReply(await this.http.post(this.callPath(callId, "hand"), { up }), "call");
  }

  async callsMessages(callId: string, after = 0): Promise<CallMessage[]> {
    const raw = parse(
      z.object({ items: z.array(callsWire.wireMessage) }),
      await this.http.get(this.callPath(callId, "messages"), { after }),
      "call chat",
    );
    return raw.items.map(callsWire.fromWireMessage);
  }

  async callsSendMessage(callId: string, text: string): Promise<CallMessage> {
    const raw = parse(
      z.object({ message: callsWire.wireMessage }),
      await this.http.post(this.callPath(callId, "messages"), { text }, crypto.randomUUID()),
      "call chat",
    );
    return callsWire.fromWireMessage(raw.message);
  }

  async callsLive(): Promise<LiveCall[]> {
    const raw = parse(
      z.object({ items: z.array(callsWire.wireLiveCall) }),
      await this.http.get("/v1/calls/live"),
      "live calls",
    );
    return raw.items.map(callsWire.fromWireLiveCall);
  }

  async callsMeetings(scope: "upcoming" | "past"): Promise<Meeting[]> {
    const raw = parse(
      z.object({ items: z.array(callsWire.wireMeeting) }),
      await this.http.get("/v1/calls/meetings", { scope }),
      "meetings",
    );
    return raw.items.map(callsWire.fromWireMeeting);
  }

  async callsSaveMeeting(draft: MeetingDraft, id?: string): Promise<Meeting> {
    const body = {
      title: draft.title.trim(),
      agenda: draft.agenda,
      starts_at: draft.startsAt,
      duration_min: draft.durationMin,
      invitees: draft.invitees,
      join: callsWire.toWireJoin(draft.join),
      media: draft.media,
    };
    const raw = id
      ? await this.http.put(`/v1/calls/meetings/${encodeURIComponent(id)}`, body)
      : await this.http.post("/v1/calls/meetings", body, crypto.randomUUID());
    return callsWire.fromWireMeeting(parse(z.object({ meeting: callsWire.wireMeeting }), raw, "meeting").meeting);
  }

  async callsCancelMeeting(id: string): Promise<Meeting> {
    const raw = await this.http.post(`/v1/calls/meetings/${encodeURIComponent(id)}/cancel`, {});
    return callsWire.fromWireMeeting(parse(z.object({ meeting: callsWire.wireMeeting }), raw, "meeting").meeting);
  }

  async callsStartMeeting(id: string): Promise<{ meeting: Meeting; call: Call; notes?: string[] }> {
    const raw = parse(
      z.object({
        meeting: callsWire.wireMeeting,
        call: callsWire.wireCall,
        notes: z.array(z.string()).nullish(),
      }),
      await this.http.post(`/v1/calls/meetings/${encodeURIComponent(id)}/start`, {}),
      "meeting",
    );
    return {
      meeting: callsWire.fromWireMeeting(raw.meeting),
      call: callsWire.fromWireCall(raw.call),
      notes: raw.notes ?? [],
    };
  }

  async callsHistory(limit = 50): Promise<Call[]> {
    const raw = parse(
      z.object({ items: z.array(callsWire.wireCall) }),
      await this.http.get("/v1/calls/history", { limit }),
      "call history",
    );
    return raw.items.map(callsWire.fromWireCall);
  }

  /* the bytes come with the same credentials as every call, so an <img src> cannot fetch them itself */
  async callsPhoto(employeeId: string): Promise<string | undefined> {
    try {
      const blob = await this.http.blob(`/v1/calls/photo/${encodeURIComponent(employeeId)}`);
      return URL.createObjectURL(blob);
    } catch {
      return undefined;
    }
  }

  async callsPolicy(): Promise<PolicyView> {
    return callsWire.fromWirePolicyView(parse(callsWire.wirePolicyView, await this.http.get("/v1/calls/policy"), "call policy"));
  }

  async saveCallsPolicy(policy: CallPolicy, basedOn?: string): Promise<PolicyView> {
    const raw = await this.http.put("/v1/calls/policy", {
      policy: callsWire.toWirePolicy(policy),
      ...(basedOn !== undefined ? { based_on: basedOn } : {}),
    });
    return callsWire.fromWirePolicyView(parse(callsWire.wirePolicyView, raw, "call policy"));
  }

  async checkCallsPolicy(from: string, to: string): Promise<CallCheck> {
    const raw = parse(
      callsWire.wireCheck,
      await this.http.post("/v1/calls/policy/check", { from, to }),
      "call check",
    );
    return { allowed: raw.allowed, video: raw.video, reason: raw.reason ?? "", rule: raw.rule ?? "matrix" };
  }

  /* ── the contact directory and who is told (routers/contacts.py) ───────── */

  async getDirectory(siteId: string): Promise<Directory> {
    const raw = parse(wireDirectory, await this.http.get("/v1/contacts", { site_id: siteId }), "contact directory");
    return {
      orgs: raw.orgs.map((o) => ({
        id: o.id,
        siteId: o.site_id ?? undefined,
        name: o.name,
        kind: o.kind as OrgKind,
        notes: o.notes ?? undefined,
        active: o.active,
        people: o.people.map(fromWirePerson),
      })),
      people: raw.people.map(fromWirePerson),
      channels: raw.channels
        ? { sms: !!raw.channels.sms?.configured, email: !!raw.channels.email?.configured }
        : undefined,
    };
  }

  async saveContactOrg(siteId: string, org: Omit<ContactOrg, "people">): Promise<{ id: string }> {
    const body = {
      name: org.name,
      kind: org.kind,
      site_id: org.siteId ?? siteId,
      notes: org.notes ?? null,
      active: org.active,
    };
    const raw = org.id
      ? await this.http.put(`/v1/contacts/orgs/${encodeURIComponent(org.id)}`, body)
      : await this.http.post("/v1/contacts/orgs", body, crypto.randomUUID());
    return parse(z.object({ id: z.string() }), raw, "organisation");
  }

  async saveContactPerson(person: ContactPerson): Promise<{ id: string }> {
    const body = {
      name: person.name,
      org_id: person.orgId ?? null,
      role: person.role ?? null,
      language: person.language,
      preferred: person.preferred,
      principal_id: person.principalId ?? null,
      employee_id: person.employeeId ?? null,
      hours: person.hours ?? null,
      notes: person.notes ?? null,
      active: person.active,
      points: person.points.map((p) => ({
        kind: p.kind,
        value: p.value,
        label: p.label ?? null,
        primary: p.primary,
        notify: p.notify,
      })),
    };
    const raw = person.id
      ? await this.http.put(`/v1/contacts/people/${encodeURIComponent(person.id)}`, body)
      : await this.http.post("/v1/contacts/people", body, crypto.randomUUID());
    return parse(z.object({ id: z.string() }), raw, "contact");
  }

  async listNotifyRules(siteId: string): Promise<NotifyRule[]> {
    const raw = parse(
      z.object({ items: z.array(wireRule) }),
      await this.http.get("/v1/notify-rules", { site_id: siteId }),
      "rules",
    );
    return raw.items.map(fromWireRule);
  }

  async saveNotifyRule(rule: NotifyRule): Promise<NotifyRule> {
    const body = {
      site_id: rule.siteId,
      name: rule.name,
      active: rule.active,
      events: rule.events,
      match: {
        priorities: rule.match.priorities ?? [],
        kinds: rule.match.kinds ?? [],
        zone_codes: rule.match.zoneCodes ?? [],
        dispatch_groups: rule.match.dispatchGroups ?? [],
      },
      recipients: rule.recipients,
      channels: rule.channels,
      escalate_after_min: rule.escalateAfterMin ?? null,
      escalate_to: rule.escalateTo,
    };
    const raw = rule.id
      ? await this.http.put(`/v1/notify-rules/${encodeURIComponent(rule.id)}`, body)
      : await this.http.post("/v1/notify-rules", body, crypto.randomUUID());
    return fromWireRule(parse(wireRule, raw, "rule"));
  }

  async previewRecipients(siteId: string, recipients: Recipient[]): Promise<Reach[]> {
    const raw = parse(
      z.object({
        people: z.array(
          z.object({ name: z.string(), sms: z.array(z.string()), email: z.array(z.string()), app: z.boolean() }),
        ),
      }),
      await this.http.post("/v1/notify-rules/preview", { site_id: siteId, recipients }, crypto.randomUUID()),
      "preview",
    );
    return raw.people;
  }

  async listWorkOrderMessages(workOrderId: string): Promise<WorkOrderMessage[]> {
    const raw = parse(
      z.object({ items: z.array(wireMessage) }),
      await this.http.get(`/v1/work-orders/${encodeURIComponent(workOrderId)}/messages`),
      "messages",
    );
    return raw.items.map((m) => ({
      id: m.id,
      recipient: m.recipient,
      channel: m.channel,
      to: m.to ?? undefined,
      status: m.status,
      error: m.error ?? undefined,
      event: m.event ?? undefined,
      createdAt: m.created_at ?? undefined,
      sentAt: m.sent_at ?? undefined,
      readAt: m.read_at ?? undefined,
      rule: m.rule ?? undefined,
      group: m.group ?? undefined,
    }));
  }

  async listDispatchNotifications(workOrderId: string): Promise<DispatchNotification[]> {
    const raw = parse(
      z.object({ items: z.array(wireDispatchNotification) }),
      await this.http.get("/v1/dispatch/notifications", { work_order_id: workOrderId }),
      "dispatch notifications",
    );
    return raw.items.map(fromWireNotification);
  }

  /* ── Store & Warehouse (routers/stores.py) ────────────────────────────── */

  async storesRegister(siteId: string): Promise<PlatformStores> {
    /* the register comes back under the web model's own names, so only its outline is checked here */
    const raw = parse(wireStoresRegister, await this.http.get("/v1/stores/register", { site_id: siteId }), "stores register");
    const { platform, ...register } = raw;
    return {
      register: register as unknown as StoresRegister,
      held: platform.held,
      noteWaiting: platform.noteWaiting,
      importedAt: platform.importedAt ?? undefined,
    };
  }

  async storesApply(siteId: string, ops: StoresOp[]): Promise<StoresApplied> {
    const key = crypto.randomUUID();
    const raw = parse(
      z.object({ results: z.array(z.record(z.string(), z.unknown())), replayed: z.boolean().optional() }),
      await this.http.post("/v1/stores/batch", { site_id: siteId, ops: ops.map((o) => toWireStep(o)) }, key),
      "stores batch",
    );
    return { results: raw.results, replayed: raw.replayed ?? false };
  }

  async storesImportNote(siteId: string): Promise<StoresImported> {
    const raw = parse(
      z.object({
        items: z.number(),
        movements: z.number(),
        balancing: z.number().optional(),
        reservations: z.number().optional(),
        requisitions: z.number().optional(),
        orders: z.number().optional(),
        replayed: z.boolean(),
      }),
      await this.http.post("/v1/stores/import-note", { site_id: siteId }),
      "stores import",
    );
    return {
      items: raw.items,
      movements: raw.movements,
      balancing: raw.balancing ?? 0,
      reservations: raw.reservations ?? 0,
      requisitions: raw.requisitions ?? 0,
      orders: raw.orders ?? 0,
      replayed: raw.replayed,
    };
  }

  /* ── build 4: approvals on the stores chain ── */

  async approvalsBoard(siteId: string): Promise<ApprovalsBoard> {
    const raw = parse(wireApprovalsBoard, await this.http.get("/v1/stores/approvals", { site_id: siteId }), "approvals");
    return {
      currency: raw.currency,
      ...(raw.me ? { me: raw.me } : {}),
      chain: raw.chain.map((s) => ({ ...s, limit: s.limit ?? null })),
      can: raw.can,
      requests: raw.requests.map((q) => ({
        number: q.number,
        kind: q.kind ?? "purchase",
        title: q.title ?? q.number,
        ...(q.supplier ? { supplier: q.supplier } : {}),
        value: q.value ?? 0,
        currency: q.currency ?? raw.currency,
        why: q.why ?? "",
        ...(q.source ? { source: q.source } : {}),
        emergency: q.emergency ?? false,
        raisedAt: q.raisedAt ?? "",
        raisedBy: q.raisedBy ?? "",
        status: q.status as ApprovalStatus,
        steps: q.steps.map((s) => ({
          step: s.step,
          decision: s.decision,
          by: s.by,
          at: s.at,
          ...(s.note ? { note: s.note } : {}),
          ...(s.limit != null ? { limit: s.limit } : {}),
          over: s.over ?? false,
        })),
        next: q.next ?? null,
        mayAct: q.mayAct,
        ...(q.whyNot ? { whyNot: q.whyNot } : {}),
        ...(q.order
          ? {
              order: {
                number: q.order.number,
                at: q.order.at ?? "",
                by: q.order.by ?? "",
                supplier: q.order.supplier ?? "",
                value: q.order.value ?? q.value ?? 0,
                status: q.order.status ?? "open",
              },
            }
          : {}),
      })),
      awaitingMe: raw.awaitingMe,
      stores: raw.stores,
    };
  }

  async raiseApprovalRequest(
    siteId: string,
    input: NewApprovalRequest,
    idempotencyKey?: string,
  ): Promise<{ number: string; replayed?: boolean }> {
    const raw = parse(
      z.object({ number: z.string(), replayed: z.boolean().optional() }).passthrough(),
      await this.http.post(
        "/v1/stores/requests",
        {
          site_id: siteId,
          chain: "approvals",
          kind: input.kind,
          title: input.title,
          supplier: input.supplier || undefined,
          value: input.value,
          why: input.why,
          source: input.source || undefined,
          emergency: !!input.emergency,
        },
        idempotencyKey,
      ),
      "approval request",
    );
    return { number: raw.number, replayed: raw.replayed ?? false };
  }

  async signApprovalStep(
    siteId: string,
    number: string,
    decision: "approved" | "rejected",
    note?: string,
    idempotencyKey?: string,
  ): Promise<ApprovalStepAck> {
    const raw = parse(
      z
        .object({
          number: z.string(),
          step: z.string(),
          decision: z.enum(["approved", "rejected"]),
          status: z.string(),
          over: z.boolean().optional(),
          replayed: z.boolean().optional(),
        })
        .passthrough(),
      /* no role, step or limit: which step it is, and whether this person holds it, is the platform's to say */
      await this.http.post(
        `/v1/stores/requests/${encodeURIComponent(number)}/step`,
        { site_id: siteId, decision, ...(note?.trim() ? { note: note.trim() } : {}) },
        idempotencyKey,
      ),
      "approval step",
    );
    return {
      number: raw.number,
      step: raw.step,
      decision: raw.decision,
      status: raw.status as ApprovalStatus,
      over: raw.over ?? false,
      replayed: raw.replayed ?? false,
    };
  }

  async orderApprovalRequest(
    siteId: string,
    number: string,
    supplier?: string,
    idempotencyKey?: string,
  ): Promise<{ number: string; replayed?: boolean }> {
    const raw = parse(
      z.object({ number: z.string(), replayed: z.boolean().optional() }).passthrough(),
      await this.http.post(
        `/v1/stores/requests/${encodeURIComponent(number)}/order`,
        { site_id: siteId, ...(supplier?.trim() ? { supplier: supplier.trim() } : {}) },
        idempotencyKey,
      ),
      "purchase order",
    );
    return { number: raw.number, replayed: raw.replayed ?? false };
  }

  async uploadWorkOrderPhoto(id: string, file: File, phase: "before" | "after"): Promise<WorkOrder> {
    const form = new FormData();
    form.append("file", file);
    form.append("subject_kind", "work_order");
    form.append("subject_id", id);
    form.append("phase", phase);
    await this.http.postForm("/v1/evidence", form);
    return this.getWorkOrder(id);
  }

  async getWorkSettings(siteId: string): Promise<WorkSettings> {
    const r = parse(
      z.object({ auto_verify_photo: z.boolean() }).passthrough(),
      await this.http.get("/v1/work-orders/settings", { site_id: siteId }),
      "work settings",
    );
    return { autoVerifyPhoto: r.auto_verify_photo };
  }

  async setWorkSettings(siteId: string, settings: WorkSettings): Promise<WorkSettings> {
    const r = parse(
      z.object({ auto_verify_photo: z.boolean() }).passthrough(),
      await this.http.put(`/v1/work-orders/settings?site_id=${encodeURIComponent(siteId)}`, {
        auto_verify_photo: settings.autoVerifyPhoto,
      }),
      "work settings",
    );
    return { autoVerifyPhoto: r.auto_verify_photo };
  }

  async confirmWorkOrderReceipt(id: string, input: ReceiptInput): Promise<WorkOrder> {
    await this.http.post(`/v1/work-orders/${encodeURIComponent(id)}/receipt`, {
      via: input.via,
      by: input.by?.trim() || null,
      note: input.note?.trim() || null,
    });
    return this.getWorkOrder(id);
  }

  async classifyWorkOrder(id: string, input: ClassifyInput): Promise<WorkOrder> {
    await this.http.post(`/v1/work-orders/${encodeURIComponent(id)}/classify`, {
      area: input.area || null,
      trade: input.trade || null,
      contractor: input.contractor?.trim() || null,
    });
    return this.getWorkOrder(id);
  }

  async setWorkOrderSla(id: string, input: SetSlaInput): Promise<WorkOrder> {
    await this.http.post(`/v1/work-orders/${encodeURIComponent(id)}/sla`, {
      sla_hours: input.slaHours,
      reason: input.reason,
    });
    return this.getWorkOrder(id);
  }

  /* ── build 6B: entry forms ── */
  async setKpiTarget(siteId: string, input: KpiTargetInput): Promise<void> {
    await this.http.post("/v1/insight/kpis/target", {
      metric_key: input.metricKey,
      target: input.target,
      status: input.status,
      basis: input.basis.trim(),
      site_id: siteId,
    });
  }

  async retireKpiTarget(siteId: string, metricKey: string, reason: string): Promise<void> {
    await this.http.post("/v1/insight/kpis/target/retire", { metric_key: metricKey, reason, site_id: siteId });
  }

  async kpiTargetHistory(siteId: string, metricKey: string): Promise<KpiTargetChange[]> {
    const d = parse(
      z
        .object({
          history: z.array(
            z
              .object({
                action: z.enum(["set", "retired"]),
                target: num.nullable().optional(),
                status: z.enum(["proposed", "agreed"]).nullable().optional(),
                basis: z.string().nullable().optional(),
                reason: z.string().nullable().optional(),
                scope: z.enum(["site", "tenant"]),
                by: z.string().nullable().optional(),
                at: z.string(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get("/v1/insight/kpis/target/history", { site_id: siteId, metric_key: metricKey }),
      "KPI target history",
    );
    return d.history.map((h) => ({
      action: h.action,
      target: h.target ?? null,
      status: h.status ?? null,
      basis: h.basis ?? null,
      reason: h.reason ?? null,
      scope: h.scope,
      by: h.by ?? null,
      at: h.at,
    }));
  }

  async amRecordCondition(assetId: string, input: AmConditionInput): Promise<AmConditionResult> {
    const r = parse(
      z
        .object({
          id: z.string(),
          tag: z.string(),
          condition_score: num,
          condition_at: z.string(),
          condition_basis: z.string(),
          health_score: num.nullable().optional(),
          health_band: z.number().nullable().optional(),
        })
        .passthrough(),
      await this.http.post(`/v1/asset-management/assets/${encodeURIComponent(assetId)}/condition`, {
        score: input.score,
        surveyed_on: input.surveyedOn || undefined,
        basis: input.basis.trim(),
      }),
      "condition score",
    );
    return {
      id: r.id,
      tag: r.tag,
      conditionScore: r.condition_score,
      conditionAt: r.condition_at,
      conditionBasis: r.condition_basis,
      healthScore: r.health_score ?? null,
      healthBand: r.health_band ?? null,
    };
  }

  async listEnvelopes(siteId: string): Promise<FundingEnvelope[]> {
    const d = parse(
      z
        .object({
          envelopes: z.array(
            z
              .object({
                id: z.string(),
                fiscal_year: z.number(),
                kind: z.enum(["capex", "opex"]),
                amount: num,
                currency: z.string(),
                label: z.string().nullable().optional(),
                scope: z.enum(["site", "tenant"]),
                applies: z.boolean(),
                set_by: z.string().nullable().optional(),
                set_at: z.string().nullable().optional(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get("/v1/liability/envelopes", { site_id: siteId }),
      "funding envelopes",
    );
    return d.envelopes.map((e) => ({
      id: e.id,
      fiscalYear: e.fiscal_year,
      kind: e.kind,
      amount: e.amount,
      currency: e.currency,
      label: e.label ?? null,
      scope: e.scope,
      applies: e.applies,
      setBy: e.set_by ?? null,
      setAt: e.set_at ?? null,
    }));
  }

  async setEnvelope(siteId: string, input: EnvelopeInput): Promise<void> {
    await this.http.post("/v1/liability/envelopes", {
      site_id: siteId,
      fiscal_year: input.fiscalYear,
      kind: input.kind,
      amount: input.amount,
      currency: input.currency.trim().toUpperCase(),
      label: input.label?.trim() || null,
    });
  }

  async removeEnvelope(id: string): Promise<void> {
    await this.http.delete(`/v1/liability/envelopes/${encodeURIComponent(id)}`);
  }

  async setTestThresholds(siteId: string, key: string, input: ThresholdsInput): Promise<void> {
    await this.http.put(`/v1/test-types/${encodeURIComponent(key)}/thresholds`, {
      thresholds: input.thresholds,
      interval_days: input.intervalDays,
      status: input.status,
      basis: input.basis.trim(),
      site_id: siteId,
    });
  }

  async listKpis(siteId: string): Promise<Kpi[]> {
    const k = parse(wireKpis, await this.http.get("/v1/insight/kpis", { site_id: siteId }), "KPIs");
    const computed: Kpi[] = k.metrics.map((m) => ({
      key: m.key,
      label: m.name,
      unit: m.unit ?? "",
      direction: m.direction === "lower_is_better" ? "down" : "up",
      target: m.target ?? null,
      targetStatus: m.target_status ?? undefined,
      targetBasis: m.target_basis ?? undefined,
      targetSetBy: m.target_set_by ?? undefined,
      targetSetAt: m.target_set_at ?? undefined,
      targetScope: m.target_scope ?? undefined,
      packTarget: m.pack_target ?? null,
      value: m.value ?? null,
      reason: m.value == null ? `No value computed yet for ${m.grain ?? "this"} grain` : undefined,
      source: m.computed_at ? `computed ${m.computed_at}` : undefined,
    }));
    const uncomputable: Kpi[] = k.uncomputable.map((u) => ({
      key: u.key,
      label: u.name,
      unit: "",
      direction: "up",
      target: null,
      value: null,
      reason: u.compile_error ?? "The pack's definition does not compile against this core",
    }));
    return [...computed, ...uncomputable];
  }

  async listAlerts(siteId: string, kind?: AlertKind): Promise<Alert[]> {
    const a = parse(wireAlerts, await this.http.get("/v1/alerts", { site_id: siteId, kind }), "alerts");
    return a.alerts.map(mapAlert);
  }

  async explainAlert(id: string): Promise<AlertExplanation> {
    const x = parse(
      wireExplain,
      await this.http.get(`/v1/insight/alerts/${encodeURIComponent(id)}/explain`),
      "explanation",
    );
    const a = x.alert;
    return {
      ...mapAlert(a),
      occurrences: a.occurrences ?? undefined,
      confirmed: a.confirmed,
      ruleName: a.rule_name ?? undefined,
      ruleClass: a.rule_class ?? undefined,
      expression: a.expression ?? undefined,
      reference: a.reference ?? undefined,
      parameters: a.parameters ?? {},
      resolved: a.resolved ?? {},
      verifyRequired: a.verify_required ?? undefined,
      subjectKind: a.subject_kind ?? undefined,
      subjectLabel: a.subject_label ?? undefined,
      outcome: a.outcome ?? undefined,
      resultingActions: x.resulting_actions.map((r) => ({
        kind: "work_order",
        ref: r.ref,
        title: r.title,
        status: r.status,
      })),
    };
  }

  async listInspections(q: InspectionQuery): Promise<Inspection[]> {
    const raw = await this.http.get("/v1/inspections", {
      site_id: q.siteId,
      status: q.status,
      template: q.template,
      limit: 200,
    });
    return parse(wireInspections, raw, "inspections").inspections.map((i) => mapInspection(i, q.siteId));
  }

  async getInspection(id: string): Promise<InspectionDetail> {
    const d = parse(
      wireInspectionDetail,
      await this.http.get(`/v1/inspections/${encodeURIComponent(id)}`),
      "inspection",
    );
    const obs = d.observations.map(mapObservation);
    const head = mapInspection(d.inspection, "");
    return {
      ...head,
      answers: obs.length,
      failures: obs.filter((o) => o.result === "fail").length,
      attrs: d.inspection.attrs ?? undefined,
      route: d.inspection.route,
      scoring: d.inspection.scoring ?? undefined,
      observations: obs,
    };
  }

  async listMapLayers(siteId: string): Promise<MapLayer[]> {
    const raw = await this.http.get("/v1/geo/layers", { site_id: siteId });
    return parse(wireLayers, raw, "map layers").layers.map(mapLayer);
  }

  async listMapFeatures(siteId: string, layer: string): Promise<MapFeature[]> {
    const raw = await this.http.get("/v1/geo/features", { site_id: siteId, layer, limit: 5000 });
    const fc = parse(wireFeatureCollection, raw, "map features");
    return fc.features
      .filter((f) => f.geometry != null)
      .map((f) => {
        const { layer: lyr, kind, asset_id, source_layer: _s, ...props } = f.properties as Record<string, unknown>;
        return {
          id: f.id,
          layer: typeof lyr === "string" ? lyr : layer,
          kind: typeof kind === "string" ? kind : undefined,
          assetId: typeof asset_id === "string" ? asset_id : undefined,
          geometry: f.geometry as unknown as Geometry,
          props,
        };
      });
  }

  async getCompliance(siteId: string): Promise<ComplianceReport> {
    const c = parse(wireCompliance, await this.http.get("/v1/insight/compliance", { site_id: siteId }), "compliance");
    return {
      readiness: c.readiness.map((r) => ({
        framework: r.framework,
        total: r.total,
        met: r.met,
        overdue: r.overdue,
        pct: r.pct ?? 0,
      })),
      obligations: c.obligations.map((o) => ({
        framework: o.framework,
        title: o.title,
        period: o.period ?? undefined,
        periodKey: o.period_key ?? undefined,
        status: o.status,
        dueAt: o.due_at ?? undefined,
        metAt: o.met_at ?? undefined,
        evidenceCount: o.evidence_count ?? 0,
      })),
    };
  }

  async listRules(): Promise<Rule[]> {
    const r = parse(wireRules, await this.http.get("/v1/insight/rules"), "rules");
    return r.rules.map((x) => ({
      key: x.key,
      name: x.name,
      ruleClass: x.class ?? undefined,
      kind: x.kind ?? undefined,
      severity: x.severity ?? 3,
      enabled: x.enabled ?? false,
      verifyRequired: x.verify_required ?? false,
      parameters: x.parameters ?? {},
      packDefaults: x.pack_defaults ?? {},
      confirmedAgainst: x.confirmed_against ?? undefined,
      verifiedAt: x.verified_at ?? undefined,
      reference: x.reference ?? undefined,
      trigger: x.trigger ?? undefined,
      gatedReason: x.gated_reason ?? undefined,
    }));
  }

  async verifyRule(input: VerifyRuleInput): Promise<VerifyRuleResult> {
    const raw = await this.http.post(
      "/v1/insight/rules/verify",
      { rule_key: input.ruleKey, parameters: input.parameters, confirmed_against: input.confirmedAgainst },
      crypto.randomUUID(),
    );
    const r = parse(wireVerify, raw, "rule verification");
    return {
      key: r.key,
      enabled: r.enabled,
      parameters: r.parameters ?? {},
      packDefaults: r.pack_defaults ?? {},
      confirmedAgainst: r.confirmed_against,
      verifiedAt: r.verified_at ?? undefined,
      note: r.note ?? undefined,
    };
  }

  async listDocuments(q: DocumentQuery): Promise<DocumentRow[]> {
    const raw = await this.http.get("/v1/library/documents", {
      site_id: q.siteId,
      site_scope: q.siteScope,
      type: q.type,
      category: q.category,
      status: q.status,
      tag: q.tag,
      q: q.q,
      limit: 500,
    });
    return parse(wireDocs, raw, "documents").documents.map(mapDocRow);
  }

  async getDocument(id: string): Promise<DocumentDetail> {
    const d = parse(wireDocDetail, await this.http.get(`/v1/library/documents/${encodeURIComponent(id)}`), "document");
    return {
      ...mapDocRow(d.document),
      ownerId: d.document.owner_id ?? undefined,
      withdrawnAt: d.document.withdrawn_at ?? undefined,
      withdrawnReason: d.document.withdrawn_reason ?? undefined,
      retentionUntil: d.document.retention_until ?? undefined,
      attrs: d.document.attrs ?? undefined,
      revisionHistory: d.revisions.map((r) => ({
        id: r.id,
        revision: r.revision,
        sequence: r.sequence,
        status: r.status,
        issuedOn: r.issued_on ?? undefined,
        effectiveFrom: r.effective_from ?? undefined,
        supersededAt: r.superseded_at ?? undefined,
        expiresOn: r.expires_on ?? undefined,
        reviewDueOn: r.review_due_on ?? undefined,
        changeNote: r.change_note ?? undefined,
        rejectedReason: r.rejected_reason ?? undefined,
        createdAt: r.created_at,
        author: r.author ?? undefined,
        approvedBy: r.approved_by_name ?? undefined,
        approvedAt: r.approved_at ?? undefined,
        sha256: r.sha256 ?? undefined,
        bytes: r.bytes ?? undefined,
        contentType: r.content_type ?? undefined,
        attrs: r.attrs ?? undefined,
      })),
      links: d.links.map((l) => ({
        entityKind: l.entity_kind,
        entityId: l.entity_id,
        relation: l.relation ?? undefined,
      })),
      acknowledgements: d.acknowledgements.map((a) => ({
        revision: a.revision,
        revisionStatus: a.revision_status,
        person: a.display_name,
        required: a.required ?? false,
        acknowledgedAt: a.acknowledged_at ?? undefined,
        isCurrent: a.is_current ?? false,
      })),
    };
  }

  /**
   * The document types, one per key.
   *
   * Types come from the packs a tenant has enabled, and two packs can define the
   * same key: an airport running both `airports` and `energy` is served
   * `calibration_certificate` twice — "Equipment Calibration Certificate" from
   * one and "Instrument Calibration Certificate" from the other.
   *
   * Everything this app does with a type is by key. It filters the library by
   * key, looks a document's type up by key, and sends a key when raising a new
   * document. Two entries sharing one are therefore not two choices: they would
   * render as a pair of options that read differently and do exactly the same
   * thing, and the library filter would list the same filter twice. React also
   * refuses to key a list on a value that repeats, which is how this surfaced.
   *
   * So a key appears once. The first wins, which is the order the platform
   * chose; there is no basis here for preferring one pack's wording over
   * another's, and inventing one — appending the pack name, say — would put a
   * label on screen that no pack actually uses.
   */
  async listDocumentTypes(): Promise<DocumentType[]> {
    const t = parse(wireDocTypes, await this.http.get("/v1/library/types"), "document types");
    const byKey = new Map<string, DocumentType>();
    for (const x of t.types) {
      if (byKey.has(x.key)) continue;
      byKey.set(x.key, {
        key: x.key,
        name: text(x.name) ?? x.key,
        description: text(x.description),
        category: x.category ?? undefined,
        controlled: x.controlled ?? false,
        requiresApproval: x.requires_approval ?? false,
        reviewMonths: x.review_months ?? undefined,
        expires: x.expires ?? false,
        retentionYears: x.retention_years ?? undefined,
        defaultClassification: x.default_classification ?? undefined,
        pack: x.pack,
      });
    }
    return [...byKey.values()];
  }

  async whenToWork(q: PlanQuery): Promise<PlanAnswer> {
    const raw = await this.http.get("/v1/weather/when-to-work", {
      site_id: q.siteId,
      position_code: q.positionCode,
      category: q.category,
      hours_needed: q.hoursNeeded,
      search_from: q.searchFrom,
      search_to: q.searchTo,
      step_minutes: q.stepMinutes ?? 60,
    });
    const p = parse(wirePlan, raw, "planning");
    return {
      position: p.position,
      category: p.category,
      hoursNeeded: p.hours_needed,
      traffic: mapSide(p.traffic),
      weather: mapSide(p.weather),
      found: p.found === true,
      known: p.known !== false,
      whyNot: p.why_not ?? undefined,
      windows: p.windows ?? [],
      windowCount: p.window_count ?? undefined,
      earliest: p.earliest ?? undefined,
      verdict: p.verdict ?? undefined,
      caveat: p.caveat ?? undefined,
      weatherBlockers: p.weather_blockers ?? undefined,
    };
  }

  async liabilityCurve(siteId: string, fiscalYear: number): Promise<LiabilityCurve> {
    const raw = await this.http.get("/v1/liability/curve", {
      site_id: siteId,
      fiscal_year: fiscalYear,
    });
    const c = parse(wireCurve, raw, "liability curve");
    return {
      available: c.available,
      fiscalYear: c.fiscal_year ?? fiscalYear,
      currency: c.currency ?? "",
      candidates: c.candidates,
      priced: c.priced,
      notPriced: c.not_priced,
      notPricedDetail: (c.not_priced_detail ?? []).map((x) => ({ title: x.title, whyNot: x.why_not })),
      coverageNote: c.coverage_note ?? c.why_not ?? undefined,
      scenarios: (c.scenarios ?? []).map((x) => ({
        envelope: x.envelope,
        funded: x.funded,
        fundedCost: x.funded_cost,
        deferred: x.deferred,
        deferredCostNow: x.deferred_cost_now,
        costOfWaitingOneYear: x.cost_of_waiting_one_year,
        escalationNotComputed: x.escalation_not_computed ?? 0,
        closureHoursPerYear: x.closure_hours_per_year ?? 0,
        closureKnownFor: x.closure_known_for ?? 0,
        closureUnknownFor: x.closure_unknown_for ?? 0,
        deferredDetail: (x.deferred_detail ?? []).map((d) => ({
          title: d.title,
          escalation: d.escalation ?? 0,
          consequence: {
            known: d.consequence?.known === true,
            whyNot: d.consequence?.why_not ?? undefined,
            closureHoursPerYear: d.consequence?.closure_hours_per_year ?? undefined,
          },
        })),
      })),
    };
  }

  /* ---- Asset Management ------------------------------------------------------ */

  async amRegister(siteId: string, filter: AmFilter = {}): Promise<AmRegister> {
    const raw = await this.http.get("/v1/asset-management/register", {
      site_id: siteId,
      q: filter.q || undefined,
      asset_type: filter.assetType || undefined,
      zone_id: filter.zoneId || undefined,
      stage: filter.stage || undefined,
    });
    return amWire.mapRegister(parse(amWire.wireRegister, raw, "asset register"));
  }

  async amAsset(assetId: string): Promise<AmAssetDetail> {
    const raw = await this.http.get(`/v1/asset-management/assets/${encodeURIComponent(assetId)}`);
    return amWire.mapDetail(parse(amWire.wireDetail, raw, "asset detail"));
  }

  async amEditAsset(assetId: string, patch: AmPatch): Promise<{ changed: string[] }> {
    return this.http.patch<{ changed: string[] }>(
      `/v1/asset-management/assets/${encodeURIComponent(assetId)}`,
      patchToWire(patch),
    );
  }

  async amImport(siteId: string, rows: AmImportRow[]): Promise<AmImportResult> {
    const raw = await this.http.post<{
      updated: number;
      not_found: string[];
      not_found_count: number;
      errors: { tag: string; error: string }[];
    }>("/v1/asset-management/import", {
      site_id: siteId,
      rows: rows.map(({ tag, ...rest }) => ({ tag, ...patchToWire(rest) })),
    });
    return { updated: raw.updated, notFound: raw.not_found, notFoundCount: raw.not_found_count, errors: raw.errors };
  }

  async amPolicies(): Promise<AmPolicy[]> {
    const raw = await this.http.get("/v1/asset-management/policies");
    return parse(amWire.wirePolicies, raw, "asset policies").policies.map(amWire.mapPolicy);
  }

  async amSetPolicy(assetType: string, p: Omit<AmPolicy, "assetType">): Promise<AmPolicy> {
    const raw = await this.http.put(`/v1/asset-management/policies/${encodeURIComponent(assetType)}`, {
      design_life_years: p.designLifeYears,
      replacement_cost: p.replacementCost,
      currency: p.currency,
      note: p.note,
    });
    return amWire.mapPolicy(parse(amWire.wirePolicy, raw, "asset policy"));
  }

  async amCondition(siteId: string): Promise<AmCondition> {
    const raw = await this.http.get("/v1/asset-management/condition", { site_id: siteId });
    return amWire.mapCondition(parse(amWire.wireCondition, raw, "asset condition"));
  }

  async amRefreshHealth(siteId: string): Promise<{ assessed: number; changed: number }> {
    const q = new URLSearchParams({ site_id: siteId });
    return this.http.post(`/v1/asset-management/health/refresh?${q}`);
  }

  async amMaintenance(siteId: string): Promise<AmMaintenance> {
    const raw = await this.http.get("/v1/asset-management/maintenance", { site_id: siteId });
    return amWire.mapMaintenance(parse(amWire.wireMaintenance, raw, "asset maintenance"));
  }

  async amCreatePlan(input: AmPlanInput): Promise<AmPlan> {
    const raw = await this.http.post("/v1/asset-management/plans", planToWire(input));
    return amWire.mapPlan(parse(amWire.wirePlan, raw, "maintenance plan"));
  }

  async amEditPlan(
    planId: string,
    patch: { active?: boolean; intervalDays?: number; nextDueOn?: string | null; title?: string },
  ): Promise<void> {
    await this.http.patch(`/v1/asset-management/plans/${encodeURIComponent(planId)}`, {
      ...(patch.active !== undefined ? { active: patch.active } : {}),
      ...(patch.intervalDays !== undefined ? { interval_days: patch.intervalDays } : {}),
      ...(patch.nextDueOn !== undefined ? { next_due_on: patch.nextDueOn } : {}),
      ...(patch.title !== undefined ? { title: patch.title } : {}),
    });
  }

  /* ── build 2: certificates ── */

  /**
   * Three requests, each with its own Idempotency-Key derived from the record's
   * one, so TRY AGAIN after any of them gets the same document and the same
   * issue back rather than a second certificate beside the first.
   */
  async recordCertificate(input: NewCertificateInput, key?: string): Promise<CertificateWrite> {
    const doc = parse(
      wireNewDoc,
      await this.http.post(
        "/v1/library/documents",
        {
          type: input.type ?? CERTIFICATE_TYPE,
          title: input.title,
          site_id: input.siteId,
          reference: input.reference ?? null,
          classification: null,
          tags: input.tags,
          attrs: input.attrs,
        },
        key ? `${key}:doc` : undefined,
      ),
      "new certificate",
    );
    try {
      return await this.certificateIssue(doc.id, input.issue, key);
    } catch (e) {
      /* not atomic: the document exists without its dates, which is a state to say, not to hide */
      throw new DataSourceError(
        `The certificate was created without its dates: ${e instanceof Error ? e.message : String(e)}`,
        { status: e instanceof DataSourceError ? e.status : undefined, cause: e },
      );
    }
  }

  renewCertificate(documentId: string, input: CertificateIssueInput, key?: string): Promise<CertificateWrite> {
    return this.certificateIssue(documentId, input, key);
  }

  async issueCertificate(revisionId: string): Promise<RevisionTransitionResult> {
    return this.transitionRevision(revisionId, { to: "issued" });
  }

  async attachRevisionFile(revisionId: string, file: File, key?: string): Promise<AttachedFile> {
    const form = new FormData();
    form.set("file", file, file.name);
    const r = parse(
      z
        .object({
          revision_id: z.string(),
          bytes: z.number().nullable().optional(),
          content_type: z.string().nullable().optional(),
        })
        .passthrough(),
      await this.http.postForm(
        `/v1/library/revisions/${encodeURIComponent(revisionId)}/file`,
        form,
        key ? `${key}:file` : undefined,
      ),
      "attached file",
    );
    return { revisionId: r.revision_id, bytes: r.bytes ?? undefined, contentType: r.content_type ?? undefined };
  }

  downloadRevision(revisionId: string): Promise<Blob> {
    return this.http.blob(`/v1/library/revisions/${encodeURIComponent(revisionId)}/content`);
  }

  /** One issue of a certificate, and — for someone who may — put in force. */
  private async certificateIssue(
    documentId: string,
    issue: CertificateIssueInput,
    key?: string,
  ): Promise<CertificateWrite> {
    const form = new FormData();
    form.set("revision", issue.revision);
    if (issue.changeNote) form.set("change_note", issue.changeNote);
    if (issue.issuedOn) form.set("issued_on", issue.issuedOn);
    form.set("expires_on", issue.expiresOn);
    if (issue.issuedBy) form.set("attrs", JSON.stringify({ issued_by: issue.issuedBy }));
    if (issue.file) form.set("file", issue.file, issue.file.name);
    const rev = parse(
      wireNewRev,
      await this.http.postForm(
        `/v1/library/documents/${encodeURIComponent(documentId)}/revisions`,
        form,
        key ? `${key}:rev` : undefined,
      ),
      "certificate issue",
    );
    const recorded: CertificateWrite = {
      documentId,
      revisionId: rev.id,
      revision: rev.revision,
      status: rev.status,
      issued: false,
    };
    if (!issue.issueNow) return { ...recorded, waiting: "approver" };
    /*
     * No effective_from: a back-dated issue earlier than the one in force
     * would be superseded before it began. The certificate's own validity is
     * its issued and expiry dates; in force is when the platform took it.
     */
    try {
      await this.transitionRevision(rev.id, { to: "issued" });
      return { ...recorded, status: "issued", issued: true };
    } catch (e) {
      if (!(e instanceof DataSourceError) || (e.status !== 409 && e.status !== 403)) throw e;
      /* a replay of an issue already put in force */
      if (/issued cannot become issued/.test(e.message)) return { ...recorded, status: "issued", issued: true };
      return { ...recorded, waiting: e.status === 403 ? "approver" : "review", why: e.message };
    }
  }

  async amApplyPlan(
    siteId: string,
    assetType: string,
    plan: Omit<AmPlanInput, "assetId">,
  ): Promise<{ created: number }> {
    const { asset_id: _ignored, ...wire } = planToWire({ ...plan, assetId: "-" });
    void _ignored;
    return this.http.post("/v1/asset-management/plans/apply", { site_id: siteId, asset_type: assetType, plan: wire });
  }

  async amGeneratePlans(siteId: string): Promise<{ created: { planId: string; workOrder: string; asset: string }[] }> {
    const q = new URLSearchParams({ site_id: siteId });
    const raw = await this.http.post<{ created: { plan_id: string; work_order: string; asset: string }[] }>(
      `/v1/asset-management/plans/generate?${q}`,
    );
    return { created: raw.created.map((c) => ({ planId: c.plan_id, workOrder: c.work_order, asset: c.asset })) };
  }

  async amTypes(): Promise<AmTypeDef[]> {
    const raw = await this.http.get("/v1/asset-management/types");
    return amWire.mapTypes(parse(amWire.wireTypes, raw, "asset types"));
  }

  async amCreateAsset(siteId: string, input: AmCreateInput): Promise<{ id: string; tag: string }> {
    const { attrs, ...rest } = input;
    const wire: Record<string, unknown> = { site_id: siteId, attrs };
    for (const [k, v] of Object.entries(rest)) {
      if (v === undefined || v === null || v === "") continue;
      wire[k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] = v;
    }
    return this.http.post<{ id: string; tag: string }>("/v1/asset-management/assets", wire);
  }

  async amHistory(assetId: string): Promise<AmHistoryEvent[]> {
    const raw = await this.http.get(`/v1/asset-management/assets/${encodeURIComponent(assetId)}/history`);
    return amWire.mapHistory(parse(amWire.wireHistory, raw, "asset history"));
  }

  async amNotes(assetId: string): Promise<AmNote[]> {
    const raw = await this.http.get(`/v1/asset-management/assets/${encodeURIComponent(assetId)}/notes`);
    return amWire.mapNotes(parse(amWire.wireNotes, raw, "asset notes"));
  }

  async amAddNote(assetId: string, body: string, category: NoteCategory): Promise<void> {
    await this.http.post(`/v1/asset-management/assets/${encodeURIComponent(assetId)}/notes`, { body, category });
  }

  async hrPeople(siteId: string): Promise<HrPeople> {
    const raw = await this.http.get("/v1/hr/people", { site_id: siteId });
    return hrWire.mapPeople(parse(hrWire.wirePeople, raw, "workforce"));
  }

  async hrAddPerson(siteId: string, input: HrEmployeeInput): Promise<{ id: string; employeeNo: string }> {
    const raw = await this.http.post<{ id: string; employee_no: string }>("/v1/hr/people", {
      ...(hrWire.snake(input) as Record<string, unknown>),
      site_id: input.siteId ?? siteId,
    });
    return { id: raw.id, employeeNo: raw.employee_no };
  }

  async hrEditPerson(employeeId: string, patch: HrEmployeePatch): Promise<{ changed: string[] }> {
    return this.http.patch(`/v1/hr/people/${encodeURIComponent(employeeId)}`, hrWire.snake(patch));
  }

  async hrRules(): Promise<HrRulesView> {
    const raw = await this.http.get("/v1/hr/rules");
    return hrWire.mapRulesView(parse(hrWire.wireRulesView, raw, "labour rules"));
  }

  async hrSetRules(restated: HrRulesPatch): Promise<HrRulesView> {
    const raw = await this.http.put("/v1/hr/rules", { rules: hrWire.snake(restated) });
    return hrWire.mapRulesView(parse(hrWire.wireRulesView, raw, "labour rules"));
  }

  async hrCost(siteId: string): Promise<HrCost> {
    const raw = await this.http.get("/v1/hr/cost", { site_id: siteId });
    return hrWire.mapCost(parse(hrWire.wireCost, raw, "manpower cost"));
  }

  async hrSetOvertime(employeeId: string, month: string, hours: number): Promise<void> {
    await this.http.put("/v1/hr/overtime", { employee_id: employeeId, month, hours });
  }

  async hrLabour(siteId: string): Promise<HrLabour> {
    const raw = await this.http.get("/v1/hr/labour", { site_id: siteId });
    return hrWire.mapLabour(parse(hrWire.wireLabour, raw, "labour cost"));
  }

  async hrSaudization(siteId: string): Promise<HrSaudization> {
    const raw = await this.http.get("/v1/hr/saudization", { site_id: siteId });
    return hrWire.mapSaudization(parse(hrWire.wireSaudization, raw, "Saudization"));
  }

  async hrFile(employeeId: string): Promise<HrStaffFile> {
    const raw = await this.http.get(`/v1/hr/people/${encodeURIComponent(employeeId)}`);
    return hrWire.mapFile(parse(hrWire.wireFile, raw, "staff file"));
  }

  async hrSetPhoto(employeeId: string, dataUrl: string): Promise<void> {
    await this.http.put(`/v1/hr/people/${encodeURIComponent(employeeId)}/photo`, { data_url: dataUrl });
  }

  async hrRecords(siteId: string, filter?: { kind?: string; status?: string }) {
    const raw = await this.http.get("/v1/hr/records", {
      site_id: siteId,
      ...(filter?.kind ? { kind: filter.kind } : {}),
      ...(filter?.status ? { status: filter.status } : {}),
    });
    return hrWire.mapRecords(parse(hrWire.wireRecords, raw, "HR records"));
  }

  async hrAddRecord(input: HrRecordInput): Promise<HrRecord> {
    const { data, ...rest } = input;
    const raw = await this.http.post("/v1/hr/records", { ...(hrWire.snake(rest) as object), data: data ?? {} });
    return hrWire.mapRecord(raw);
  }

  async hrDecide(recordId: string, status: RecordStatus, note?: string | null): Promise<HrRecord> {
    const raw = await this.http.post(`/v1/hr/records/${encodeURIComponent(recordId)}/decision`, {
      status,
      note: note ?? null,
    });
    return hrWire.mapRecord(raw);
  }

  async hrAttendanceDay(siteId: string, day: string): Promise<HrAttendanceDay> {
    const raw = await this.http.get("/v1/hr/attendance", { site_id: siteId, day });
    return hrWire.camel<HrAttendanceDay>(raw);
  }

  async hrAttendanceRange(siteId: string, from: string, to: string, employeeId?: string): Promise<HrAttendanceRow[]> {
    const raw = await this.http.get<{ rows: unknown[] }>("/v1/hr/attendance/range", {
      site_id: siteId,
      start: from,
      end: to,
      ...(employeeId ? { employee_id: employeeId } : {}),
    });
    return hrWire.camel<HrAttendanceRow[]>(raw.rows).map(hrWire.trimTimes);
  }

  async hrSetAttendance(input: HrAttendanceInput): Promise<{ recorded: number }> {
    return this.http.put("/v1/hr/attendance", hrWire.snake(input));
  }

  async hrAccess(): Promise<HrAccessView> {
    return hrWire.camel<HrAccessView>(await this.http.get("/v1/hr/access"));
  }

  /* ── build 2: closures ── */
  async declareClosure(input: DeclareClosureInput, idempotencyKey?: string): Promise<DeclaredClosure> {
    const d = parse(
      wireDeclaredClosure,
      await this.http.post(
        "/v1/closures",
        {
          site_id: input.siteId,
          zone_code: input.zoneCode,
          reason: input.reason,
          kind: input.kind,
          extent: "full",
          reference: input.reference,
          started_at: input.startedAt,
          planned_start: input.plannedStart,
          planned_end: input.plannedEnd ?? null,
          attrs: input.attrs ?? {},
        },
        idempotencyKey,
      ),
      "declared closure",
    );
    return {
      id: d.id,
      status: d.status,
      startedAt: d.started_at ?? null,
      ...(d.reference ? { reference: d.reference } : {}),
    };
  }

  async endClosure(id: string, endedAt?: string, idempotencyKey?: string): Promise<EndedClosure> {
    const d = parse(
      wireEndedClosure,
      await this.http.post(`/v1/closures/${encodeURIComponent(id)}/end`, { ended_at: endedAt }, idempotencyKey),
      "ended closure",
    );
    return { endedAt: d.ended_at, minutes: d.minutes ?? 0 };
  }

  async hrSetAccess(principalId: string, role: string | null): Promise<void> {
    await this.http.put(`/v1/hr/access/${encodeURIComponent(principalId)}`, { role });
  }

  async hrMe(): Promise<HrMe> {
    return hrWire.camel<HrMe>(await this.http.get("/v1/hr/me"));
  }

  async hrClockMe(input: HrClockInput) {
    return hrWire.camel<{ at?: string; place?: string; distanceM?: number | null; duplicate?: boolean }>(
      await this.http.post("/v1/hr/me/clock", hrWire.snake(input)),
    );
  }

  async hrSelfRequest(input: HrSelfRequest): Promise<HrRecord> {
    return hrWire.mapRecord(await this.http.post("/v1/hr/me/requests", hrWire.snake(input)));
  }

  async hrGeofences(siteId: string): Promise<HrGeofence[]> {
    const raw = await this.http.get<{ locations: unknown[] }>("/v1/hr/geofences", { site_id: siteId });
    return hrWire.camel<HrGeofence[]>(raw.locations).map((f) => ({ ...f, shiftStart: f.shiftStart.slice(0, 5) }));
  }

  async hrAddGeofence(siteId: string, f: Omit<HrGeofence, "id">): Promise<HrGeofence> {
    return hrWire.camel<HrGeofence>(
      await this.http.post("/v1/hr/geofences", { ...(hrWire.snake(f) as object), site_id: siteId }),
    );
  }

  async hrEditGeofence(id: string, patch: Partial<HrGeofence>): Promise<void> {
    await this.http.patch(`/v1/hr/geofences/${encodeURIComponent(id)}`, hrWire.snake(patch));
  }

  async hrClockEvents(siteId: string, day: string, employeeId?: string): Promise<HrClockEvent[]> {
    const raw = await this.http.get<{ events: unknown[] }>("/v1/hr/clock-events", {
      site_id: siteId,
      day,
      ...(employeeId ? { employee_id: employeeId } : {}),
    });
    return hrWire.camel<HrClockEvent[]>(raw.events);
  }

  async hrClockManual(employeeId: string, direction: "in" | "out", at: string, note: string): Promise<void> {
    await this.http.post("/v1/hr/clock/manual", { employee_id: employeeId, direction, at, note });
  }

  async hrNotifications(siteId: string, filter?: { employeeId?: string; status?: string }) {
    const raw = await this.http.get("/v1/hr/notifications", {
      site_id: siteId,
      ...(filter?.employeeId ? { employee_id: filter.employeeId } : {}),
      ...(filter?.status ? { status: filter.status } : {}),
    });
    return hrWire.camel<{ notifications: HrNotification[]; channels: HrChannels }>(raw);
  }

  async hrSendNow() {
    return this.http.post<{ sent: number; failed: number; not_configured: number }>("/v1/hr/notifications/send");
  }

  async hrImport(
    siteId: string,
    rows: { row: number; data: Record<string, unknown>; loginEmail: string | null; leaveBalance: number | null }[],
    opts: { dryRun: boolean; updateExisting: boolean },
  ): Promise<ImportResponse> {
    const raw = await this.http.post("/v1/hr/people/import", {
      site_id: siteId,
      dry_run: opts.dryRun,
      update_existing: opts.updateExisting,
      rows: rows.map((r) => ({ row: r.row, data: r.data, login_email: r.loginEmail, leave_balance: r.leaveBalance })),
    });
    return hrWire.camel<ImportResponse>(raw);
  }

  async hrRetryNotification(id: string): Promise<void> {
    await this.http.post(`/v1/hr/notifications/${encodeURIComponent(id)}/retry`);
  }

  async amLifecycle(siteId: string): Promise<AmLifecycle> {
    const raw = await this.http.get("/v1/asset-management/lifecycle", { site_id: siteId });
    return amWire.mapLifecycle(parse(amWire.wireLifecycle, raw, "asset lifecycle"));
  }

  async liabilityRegister(siteId: string): Promise<LiabilityRegister> {
    const raw = await this.http.get("/v1/liability/register", { site_id: siteId });
    const r = parse(wireRegister, raw, "liability register");
    return {
      candidates: r.candidates,
      priced: r.priced,
      notPriced: r.not_priced,
      note: r.note ?? undefined,
      register: r.register.map((x) => ({
        id: x.id,
        title: x.title,
        category: x.category ?? undefined,
        status: x.status ?? undefined,
        forecastFor: x.forecast_for ?? undefined,
        priced: x.priced,
        treatment: x.treatment ?? undefined,
        currency: x.currency ?? undefined,
        costNow: x.cost_now ?? undefined,
        costDeferred: x.cost_deferred ?? undefined,
        escalation: x.escalation ?? undefined,
        deferYears: x.defer_years ?? undefined,
        whyNot: x.why_not ?? undefined,
        basis: x.basis
          ? {
              unit: x.basis.unit ?? undefined,
              rate: x.basis.rate ?? undefined,
              pricedOn: x.basis.priced_on ?? undefined,
              rateSource: x.basis.rate_source ?? undefined,
              quantity: x.basis.quantity ?? undefined,
              factorPerYear: x.basis.factor_per_year ?? undefined,
              deferralBasis: x.basis.deferral_basis ?? undefined,
            }
          : undefined,
      })),
    };
  }

  async closureCost(q: ClosureQuery): Promise<ClosureCost> {
    const raw = await this.http.get("/v1/capacity/closure-cost", {
      site_id: q.siteId,
      position_code: q.positionCode,
      start: q.from,
      end: q.to,
    });
    const c = parse(wireClosure, raw, "closure cost");
    return {
      position: c.position ?? q.positionCode,
      window: c.window ?? { from: q.from, to: q.to },
      known: c.known !== false,
      whyNot: c.why_not ?? undefined,
      movements: c.movements ?? 0,
      absorbed: c.absorbed ?? 0,
      bussed: c.bussed ?? 0,
      stranded: c.stranded ?? 0,
      undetermined: c.undetermined ?? 0,
      hours: c.hours ?? c.window?.hours ?? undefined,
      scheduleCoverage: coverageText(c.schedule_coverage),
      assumption: c.assumption ?? c.verdict ?? undefined,
      detail: closureDetail(c.detail),
    };
  }

  async criticalPositions(siteId: string, from: string, to: string): Promise<CriticalReport> {
    const raw = await this.http.get("/v1/capacity/critical", {
      site_id: siteId,
      start: from,
      end: to,
    });
    const c = parse(wireCritical, raw, "critical positions");
    const row = (r: z.infer<typeof wireCriticalRow>) => ({
      position: r.position,
      stranded: r.stranded ?? 0,
      bussed: r.bussed,
      movements: r.movements,
      undetermined: r.undetermined,
    });
    return {
      window: c.window ?? { from, to },
      known: c.known !== false,
      whyNot: c.why_not ?? undefined,
      critical: (c.critical ?? []).map(row),
      flexible: (c.flexible ?? []).map(row),
      note: c.note ?? undefined,
      scheduleCoverage: coverageText(c.schedule_coverage),
    };
  }

  async driftChecks(siteId: string): Promise<DriftCheck[]> {
    const raw = await this.http.get("/v1/drift/checks", { site_id: siteId });
    return parse(wireDriftChecks, raw, "drift checks").checks.map((c) => ({
      id: c.id,
      modelKey: c.model_key,
      windowFrom: c.window_from,
      windowTo: c.window_to,
      predictions: c.predictions ?? 0,
      decided: c.decided ?? 0,
      undecided: c.undecided ?? 0,
      pending: c.pending ?? 0,
      judgeableFraction: c.judgeable_fraction ?? undefined,
      changePoint: c.change_point ?? undefined,
      verdict: c.verdict,
      /* absent means NOT conclusive: assuming a check can support a
         conclusion is the one default that turns an empty window green */
      conclusive: c.conclusive === true,
      checkedAt: c.checked_at ?? undefined,
    }));
  }

  async driftSchedule(siteId: string): Promise<DriftSchedule> {
    const raw = await this.http.get("/v1/drift/due", { site_id: siteId });
    const d = parse(wireDriftSchedule, raw, "drift schedule");
    const row = (x: z.infer<typeof wireDue>) => ({
      modelKey: x.model_key,
      referenceId: x.reference_id ?? undefined,
      horizonDays: x.horizon_days ?? undefined,
      windowDays: x.window_days ?? undefined,
      everyDays: x.every_days ?? undefined,
      lastChecked: x.last_checked ?? undefined,
    });
    return { due: (d.due ?? []).map(row), waiting: (d.waiting ?? []).map(row) };
  }

  async populations(siteId: string): Promise<Population[]> {
    const raw = await this.http.get("/v1/reliability/populations", { site_id: siteId });
    return parse(wirePopulations, raw, "populations").populations.map((p) => ({
      population: p.population,
      timeUnit: p.time_unit ?? "hours",
      units: p.units ?? 0,
      failures: p.failures ?? 0,
      failureModes: p.failure_modes ?? undefined,
      fittable: p.fittable === true,
      whyNot: p.why_not ?? undefined,
    }));
  }

  async fits(siteId: string, population: string): Promise<Fit[]> {
    /* the platform filters fits by `name`; sending `population` was ignored, so every population showed every fit */
    const raw = await this.http.get("/v1/reliability/fits", {
      site_id: siteId,
      name: population,
    });
    return parse(wireFits, raw, "fits").fits.map((f) => ({
      id: f.id,
      population: f.population,
      method: f.method ?? "",
      units: f.units ?? 0,
      failures: f.failures ?? 0,
      /* the units still running: the number a failures-only fit throws away */
      censored: f.censored ?? Math.max(0, (f.units ?? 0) - (f.failures ?? 0)),
      beta: f.beta,
      betaLow: f.beta_low ?? undefined,
      betaHigh: f.beta_high ?? undefined,
      eta: f.eta,
      timeUnit: f.time_unit ?? "hours",
      lastFailureAt: f.last_failure_at ?? undefined,
      fittedAt: f.fitted_at ?? undefined,
      shapeVerdict: f.basis?.shape_verdict ?? undefined,
      wearOut: f.basis?.wear_out
        ? {
            known: f.basis.wear_out.known === true,
            verdict: f.basis.wear_out.verdict ?? undefined,
            note: f.basis.wear_out.note ?? undefined,
            rejectsConstantRate: f.basis.wear_out.rejects_constant_rate ?? undefined,
            likelihoodRatio: f.basis.wear_out.likelihood_ratio ?? undefined,
            critical95: f.basis.wear_out.critical_95 ?? undefined,
          }
        : undefined,
    }));
  }

  /* ── the migration ───────────────────────────────────────────────────── */

  async migrationStatus(): Promise<MigrationStatus> {
    const d = parse(wireMigrationStatus, await this.http.get("/v1/migrate/screens"), "migration status");
    return {
      screens: d.screens.map(toScreen),
      byMode: d.by_mode as MigrationStatus["byMode"],
      total: d.total,
      note: d.note,
    };
  }

  async migrationScreen(key: string): Promise<ScreenDetail> {
    const d = parse(
      wireMigrationScreen,
      await this.http.get(`/v1/migrate/screens/${encodeURIComponent(key)}`),
      "migration screen",
    );
    return {
      ...toScreen(d),
      description: d.description ?? undefined,
      source: (d.source ?? {}) as Record<string, unknown>,
      projection: (d.projection ?? {}) as Record<string, string>,
      comparison: (d.comparison ?? {}) as Record<string, unknown>,
      history: (d.history ?? []).map((h) => ({
        fromMode: h.from_mode as ScreenMode,
        toMode: h.to_mode as ScreenMode,
        at: h.at,
        reason: h.reason ?? undefined,
        forced: !!h.forced,
      })),
    };
  }

  async migrationDifferences(key: string, untriagedOnly: boolean): Promise<Difference[]> {
    const d = parse(
      z.object({ differences: z.array(wireDifference) }),
      await this.http.get(`/v1/migrate/screens/${encodeURIComponent(key)}/differences`, {
        untriaged_only: String(untriagedOnly),
      }),
      "migration differences",
    );
    return d.differences.map((r) => ({
      id: r.id,
      observedAt: r.observed_at,
      verdict: r.verdict as Verdict,
      requestKey: r.request_key ?? "",
      legacyRows: (r.legacy_rows ?? []) as unknown[],
      platformRows: (r.platform_rows ?? []) as unknown[],
      differences: (r.differences ?? []) as unknown[],
      detail: r.detail ?? "",
      triage: (r.triage ?? undefined) as Triage | undefined,
      triageNote: r.triage_note ?? undefined,
    }));
  }

  async triageObservation(id: number, triage: Triage, note?: string): Promise<void> {
    await this.http.post(`/v1/migrate/observations/${id}/triage`, { triage, note: note ?? null });
  }

  /* ── build 2: friction ── */

  async listFrictionSurveys(siteId: string): Promise<import("../domain/friction").FrictionSurvey[]> {
    const num = z.number().nullable().optional();
    const d = parse(
      z
        .object({
          surveys: z.array(
            z
              .object({
                survey_id: z.string(),
                kind: z.enum(["survey", "manual"]).catch("survey"),
                observed_at: z.string(),
                observed_on: z.string(),
                reference: z.string().nullable().optional(),
                reference_length_m: num,
                forward: z.string().nullable().optional(),
                device: z.string().nullable().optional(),
                speed_kmh: num,
                levels: z.record(z.string(), z.number().nullable()).nullable().optional(),
                header: z.record(z.string(), z.unknown()).nullable().optional(),
                imported_by: z.string().nullable().optional(),
                imported_at: z.string().nullable().optional(),
                runs: z.array(
                  z
                    .object({
                      direction: z.string().nullable(),
                      lap: z.number(),
                      offset_m: num,
                      label: z.string().nullable().optional(),
                      from_m: z.array(z.number()),
                      to_m: z.array(z.number().nullable()),
                      value: z.array(z.number()),
                    })
                    .passthrough(),
                ),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
      await this.http.get("/v1/readings/surveys", { site_id: siteId }),
      "friction surveys",
    );
    return d.surveys.map((s) => {
      const lv = s.levels ?? null;
      const levels = lv
        ? {
            ...(lv.design != null ? { design: lv.design } : {}),
            ...(lv.planning != null ? { planning: lv.planning } : {}),
            ...(lv.minimum != null ? { minimum: lv.minimum } : {}),
          }
        : undefined;
      return {
        surveyId: s.survey_id,
        kind: s.kind,
        observedAt: s.observed_at,
        observedOn: s.observed_on,
        reference: s.reference ?? undefined,
        referenceLengthM: s.reference_length_m ?? undefined,
        forward: s.forward ?? undefined,
        device: s.device ?? undefined,
        speedKmh: s.speed_kmh ?? undefined,
        levels,
        header: s.header ?? {},
        importedBy: s.imported_by ?? undefined,
        importedAt: s.imported_at ?? undefined,
        runs: s.runs.map((r) => ({
          direction: r.direction ?? "",
          lap: r.lap,
          offsetM: r.offset_m ?? undefined,
          label: r.label ?? undefined,
          fromM: r.from_m,
          toM: r.to_m,
          value: r.value,
        })),
      };
    });
  }

  async importFrictionSurvey(
    siteId: string,
    input: import("../domain/friction").NewFrictionSurvey,
    idempotencyKey?: string,
  ): Promise<import("../domain/friction").FrictionSurveyAck> {
    const d = parse(
      z
        .object({
          survey_id: z.string(),
          runs: z.number(),
          segments: z.number(),
          documents: z.array(z.string().nullable()).default([]),
          imported_at: z.string().nullable().optional(),
          imported_by: z.string().nullable().optional(),
          replayed: z.boolean().optional(),
        })
        .passthrough(),
      await this.http.post(
        "/v1/readings/surveys",
        {
          site_id: siteId,
          survey_id: input.surveyId,
          kind: input.kind ?? "survey",
          reference_key: input.referenceKey,
          measurement: input.measurement ?? "friction_mu",
          observed_at: input.observedAt,
          device: input.device,
          speed_kmh: input.speedKmh,
          levels: input.levels,
          header: input.header ?? {},
          chainage: input.chainage ?? "reference",
          runs: input.runs.map((r) => ({
            direction: r.direction,
            lap: r.lap,
            offset_m: r.offsetM,
            label: r.label,
            segments: r.segments.map((s) => ({ from_m: s.fromM, to_m: s.toM, value: s.value })),
          })),
        },
        idempotencyKey,
      ),
      "imported survey",
    );
    return {
      surveyId: d.survey_id,
      runs: d.runs,
      segments: d.segments,
      documents: d.documents.filter((x): x is string => !!x),
      importedAt: d.imported_at ?? undefined,
      importedBy: d.imported_by ?? undefined,
      replayed: d.replayed,
    };
  }

  async recordCompletedWork(
    input: import("../domain/friction").CompletedWorkInput,
  ): Promise<import("../domain/friction").CompletedWorkAck> {
    const d = parse(
      z
        .object({
          id: z.string(),
          number: z.string(),
          status: z.string(),
          created: z.boolean(),
          completed_at: z.string().nullable().optional(),
          zone_code: z.string().nullable().optional(),
        })
        .passthrough(),
      await this.http.post(
        "/v1/work-orders/record-completed",
        {
          site_id: input.siteId,
          title: input.title,
          activity: input.activity,
          completed_on: input.completedOn,
          client_id: input.clientId,
          kind: input.kind ?? "preventive",
          priority: input.priority ?? 3,
          description: input.description,
          zone_code: input.zoneCode,
          zone_fallback: input.zoneFallback,
          asset_tag: input.assetTag,
          attrs: input.attrs,
        },
        input.clientId,
      ),
      "recorded work",
    );
    return {
      id: d.id,
      number: d.number,
      status: d.status,
      created: d.created,
      completedAt: d.completed_at ?? undefined,
      zoneCode: d.zone_code ?? undefined,
    };
  }

  async amendCompletedWork(
    id: string,
    patch: import("../domain/friction").CompletedWorkPatch,
  ): Promise<import("../domain/friction").CompletedWorkAmended> {
    return parse(
      z.object({
        id: z.string(),
        number: z.string(),
        status: z.string(),
        changed: z.boolean(),
        fields: z.array(z.string()).default([]),
      }),
      await this.http.post(`/v1/work-orders/${encodeURIComponent(id)}/record`, {
        attrs: patch.attrs ?? {},
        completed_on: patch.completedOn,
        note: patch.note,
      }),
      "amended record",
    );
  }

  private parseModelParams(raw: unknown): import("../domain/friction").ModelParams {
    const d = parse(
      z
        .object({
          model: z.string(),
          params: z.record(z.string(), z.number()).default({}),
          inputs: z.unknown(),
          zones: z.record(z.string(), z.record(z.string(), z.number())).default({}),
          note: z.string().nullable().optional(),
          saved_by: z.string().nullable().optional(),
          saved_at: z.string().nullable().optional(),
          zones_applied: z.array(z.string()).optional(),
          zones_missing: z.array(z.string()).optional(),
        })
        .passthrough(),
      raw,
      "model figures",
    );
    return {
      model: d.model,
      params: d.params,
      inputs: d.inputs ?? null,
      zones: d.zones,
      note: d.note ?? undefined,
      savedBy: d.saved_by ?? undefined,
      savedAt: d.saved_at ?? undefined,
      zonesApplied: d.zones_applied,
      zonesMissing: d.zones_missing,
    };
  }

  async getModelParams(siteId: string, model: string): Promise<import("../domain/friction").ModelParams> {
    return this.parseModelParams(
      await this.http.get(`/v1/insight/model-params/${encodeURIComponent(model)}`, { site_id: siteId }),
    );
  }

  async saveModelParams(
    siteId: string,
    model: string,
    input: import("../domain/friction").ModelParamsInput,
  ): Promise<import("../domain/friction").ModelParams> {
    return this.parseModelParams(
      await this.http.put(`/v1/insight/model-params/${encodeURIComponent(model)}`, {
        site_id: siteId,
        params: input.params,
        inputs: input.inputs ?? null,
        zones: input.zones ?? {},
        note: input.note,
      }),
    );
  }

  async setScreenMode(key: string, mode: ScreenMode, reason: string, force = false): Promise<ModeChange> {
    try {
      const d = parse(
        z.object({ from: z.string(), to: z.string(), forced: z.boolean().optional() }),
        await this.http.post(`/v1/migrate/screens/${encodeURIComponent(key)}/mode`, { mode, reason, force }),
        "mode change",
      );
      return { from: d.from as ScreenMode, to: d.to as ScreenMode, forced: !!d.forced };
    } catch (err) {
      /* The guard's refusal is an answer, not a failure: it carries the
         reasons, and the desk shows them beside the screen. Anything else
         is a real error and is thrown on. */
      const detail = (err as { detail?: unknown; status?: number }).detail as { blocking?: string[] } | undefined;
      const status = (err as { status?: number }).status;
      if (status === 409 && detail?.blocking) return { from: mode, to: mode, forced: false, blocking: detail.blocking };
      throw err;
    }
  }

  /* ── administering the platform ─────────────────────────────────────── */

  async adminSetup(): Promise<SetupState> {
    return parse(
      z.object({
        steps: z.array(
          z.object({
            key: z.string(),
            title: z.string(),
            done: z.boolean(),
            blocking: z.boolean(),
            is: z.string(),
            next: z.string(),
          }),
        ),
        done: z.number(),
        total: z.number(),
        blocking: z.array(z.string()),
        ready: z.boolean(),
        note: z.string(),
      }),
      await this.http.get("/v1/admin/setup"),
      "setup state",
    );
  }

  async adminPeople(): Promise<Person[]> {
    const d = parse(
      z.object({
        principals: z.array(
          z.object({
            id: z.string(),
            kind: z.string(),
            external_id: z.string().nullish(),
            display_name: z.string(),
            email: z.string().nullish(),
            groups: z.array(z.string()).nullish(),
            roles: z.array(z.string()).nullish(),
            last_seen_at: z.string().nullish(),
            disabled_at: z.string().nullish(),
          }),
        ),
      }),
      await this.http.get("/v1/admin/principals"),
      "principals",
    );
    return d.principals.map((p) => ({
      id: p.id,
      kind: (p.kind === "service" || p.kind === "device" ? p.kind : "user") as Person["kind"],
      externalId: p.external_id ?? undefined,
      displayName: p.display_name,
      email: p.email ?? undefined,
      groups: p.groups ?? [],
      roles: p.roles ?? [],
      lastSeenAt: p.last_seen_at ?? undefined,
      disabledAt: p.disabled_at ?? undefined,
    }));
  }

  async adminAddPerson(input: NewPerson): Promise<AddedPerson> {
    const d = parse(
      z.object({
        id: z.string(),
        display_name: z.string(),
        roles: z.array(z.string()).default([]),
        refused: z.array(z.object({ role: z.string(), why: z.string() })).default([]),
        access_level: z.string(),
        note: z.string().default(""),
      }),
      await this.http.post("/v1/admin/principals", {
        display_name: input.displayName,
        external_id: input.externalId,
        email: input.email ?? null,
        kind: input.kind ?? "user",
        roles: input.roles,
        site_id: input.siteId ?? null,
      }),
      "added person",
    );
    return {
      id: d.id,
      displayName: d.display_name,
      roles: d.roles,
      refused: d.refused,
      accessLevel: d.access_level as AccessLevel,
      note: d.note,
    };
  }

  async adminSetDisabled(principalId: string, disabled: boolean): Promise<void> {
    await this.http.post(`/v1/admin/principals/${encodeURIComponent(principalId)}/disabled?disabled=${disabled}`);
  }

  async adminRoles(): Promise<RoleRow[]> {
    const d = parse(
      z.object({
        roles: z.array(
          z.object({
            key: z.string(),
            name: z.string(),
            description: z.string().nullish(),
            permissions: z.array(z.string()).nullish(),
            access_level: z.string().nullish(),
            builtin: z.boolean().nullish(),
            tenant_id: z.string().nullish(),
          }),
        ),
      }),
      await this.http.get("/v1/admin/roles"),
      "roles",
    );
    return d.roles.map((r) => ({
      key: r.key,
      name: r.name,
      description: r.description ?? undefined,
      permissions: r.permissions ?? [],
      accessLevel: (r.access_level ?? "operations") as AccessLevel,
      /* a role with no tenant is one the platform ships, and is shared by
         every tenant on this deployment — which is why it cannot be edited here */
      builtin: r.builtin ?? !r.tenant_id,
    }));
  }

  async adminSetRoleLevel(key: string, level: AccessLevel): Promise<void> {
    await this.http.put(`/v1/admin/roles/${encodeURIComponent(key)}/access-level`, { access_level: level });
  }

  async adminAssignRole(principal: string, role: string, siteId?: string): Promise<void> {
    await this.http.post("/v1/admin/role-assignments", { principal, role, site_id: siteId ?? null });
  }

  async adminUnassignRole(principal: string, role: string, siteId?: string): Promise<void> {
    /* the query carries it: the delete helper takes no body, and a role
       assignment is identified by the three things that make it unique */
    const q = new URLSearchParams({ principal, role });
    if (siteId) q.set("site_id", siteId);
    await this.http.delete(`/v1/admin/role-assignments?${q.toString()}`);
  }

  async adminInvite(input: Invitation): Promise<Invited> {
    const d = parse(
      z.object({
        display_name: z.string(),
        email: z.string(),
        role: z.string(),
        access_level: z.enum(["operations", "engineering", "executive", "master"]),
        account_created: z.boolean(),
        already_listed: z.boolean(),
        email_sent: z.boolean(),
      }),
      await this.http.post("/v1/admin/invitations", {
        display_name: input.displayName,
        email: input.email,
        role: input.role,
      }),
      "invitation",
    );
    return {
      displayName: d.display_name,
      email: d.email,
      role: d.role,
      accessLevel: d.access_level,
      accountCreated: d.account_created,
      alreadyListed: d.already_listed,
      emailSent: d.email_sent,
    };
  }

  async adminUpdateProvider(id: string, change: ProviderChange): Promise<void> {
    await this.http.put(`/v1/admin/identity-providers/${encodeURIComponent(id)}`, {
      ...(change.enabled !== undefined ? { enabled: change.enabled } : {}),
      ...(change.defaultRole === null
        ? { clear_default_role: true }
        : change.defaultRole !== undefined
          ? { default_role: change.defaultRole }
          : {}),
    });
  }

  async adminRefreshProviderKeys(id: string): Promise<number> {
    const d = (await this.http.post(`/v1/admin/identity-providers/${encodeURIComponent(id)}/refresh-keys`)) as {
      keys_cached?: number;
    };
    return d.keys_cached ?? 0;
  }

  async adminAuthStatus(): Promise<AuthStatus> {
    const d = parse(
      z.object({
        authenticated_as: z.object({ principal: z.string().nullish(), method: z.string().nullish() }).partial(),
        identity_providers: z
          .array(
            z.object({
              id: z.string().nullish(),
              name: z.string(),
              issuer: z.string(),
              enabled: z.boolean().nullish(),
              cached_keys: z.number().nullish(),
              default_role: z.string().nullish(),
              can_invite: z.boolean().nullish(),
            }),
          )
          .default([]),
        development_header_enabled: z.boolean().default(false),
      }),
      await this.http.get("/v1/admin/auth/status"),
      "auth status",
    );
    return {
      method: d.authenticated_as.method ?? undefined,
      principal: d.authenticated_as.principal ?? undefined,
      providers: d.identity_providers.map((p) => ({
        id: p.id ?? undefined,
        canInvite: !!p.can_invite,
        name: p.name,
        issuer: p.issuer,
        enabled: !!p.enabled,
        cachedKeys: p.cached_keys ?? 0,
        defaultRole: p.default_role ?? undefined,
      })),
      developmentHeaderEnabled: d.development_header_enabled,
    };
  }

  async fieldPositions(siteId: string): Promise<FieldPosition[]> {
    const d = parse(
      z.object({
        positions: z.array(
          z.object({
            principal_id: z.string().nullish(),
            principal: z.string().nullish(),
            team: z.string().nullish(),
            event: z.string(),
            work_order_id: z.string().nullish(),
            work_order: z.string().nullish(),
            lon: z.number(),
            lat: z.number(),
            accuracy_m: z.number().nullish(),
            heading_deg: z.number().nullish(),
            speed_mps: z.number().nullish(),
            source: z.string(),
            at: z.string(),
            age_s: z.number().nullish(),
            stale: z.boolean().nullish(),
            clock_ahead: z.boolean().nullish(),
          }),
        ),
      }),
      await this.http.get("/v1/field/positions", { site_id: siteId }),
      "field positions",
    );
    return d.positions.map((p) => ({
      principalId: p.principal_id ?? undefined,
      principal: p.principal ?? undefined,
      team: p.team ?? undefined,
      event: p.event as FieldPosition["event"],
      workOrderId: p.work_order_id ?? undefined,
      workOrder: p.work_order ?? undefined,
      lon: p.lon,
      lat: p.lat,
      accuracyM: p.accuracy_m ?? undefined,
      headingDeg: p.heading_deg ?? undefined,
      speedMps: p.speed_mps ?? undefined,
      source: p.source as FieldPosition["source"],
      at: p.at,
      ageS: p.age_s ?? 0,
      stale: !!p.stale,
      clockAhead: p.clock_ahead ?? undefined,
    }));
  }

  async ledgerFacts(siteId: string): Promise<FactSummary[]> {
    const raw = await this.http.get("/v1/ledger/facts", { site_id: siteId });
    return parse(wireFacts, raw, "ledger facts").facts.map((f) => ({
      factKey: f.fact_key,
      assertions: f.assertions ?? 0,
      parties: f.parties ?? 0,
      partyKeys: f.party_keys ?? [],
      subjects: f.subjects ?? 0,
      comparableSubjects: f.comparable_subjects ?? 0,
      latest: f.latest ?? undefined,
      authority: f.authority ?? undefined,
    }));
  }

  async divergence(q: DivergenceQuery): Promise<Divergence> {
    const raw = await this.http.get("/v1/ledger/between", {
      site_id: q.siteId,
      fact_key: q.factKey,
      party_a: q.partyA,
      party_b: q.partyB,
    });
    const d = parse(wireBetween, raw, "divergence");
    return {
      fact: d.fact ?? q.factKey,
      partyA: d.party_a ?? q.partyA,
      partyB: d.party_b ?? q.partyB,
      comparable: d.comparable === true,
      whyNot: d.why_not ?? undefined,
      pairs: d.pairs ?? 0,
      medianSeconds: d.median_seconds ?? undefined,
      medianMinutes: d.median_minutes ?? undefined,
      iqrSeconds: d.iqr_seconds ?? undefined,
      bLaterPct: d.b_later_pct ?? undefined,
      systematic: d.systematic === true,
      finding: d.finding ?? undefined,
    };
  }

  async canWork(siteId: string, category: string, at: string): Promise<CanWork> {
    const raw = await this.http.get("/v1/weather/can-work", {
      site_id: siteId,
      category,
      at,
    });
    const w = parse(wireCanWork, raw, "weather");
    /* The short "not known" reply carries no conditions block: nothing was
       read, so there is nothing to report but the reason. */
    const c = w.conditions ?? { known: false, why_not: w.why_not ?? undefined };
    return {
      category: w.category,
      /* echo back what was asked about; the short reply does not repeat it */
      at: w.at ?? at,
      conditions: {
        known: c.known === true,
        basis: c.basis ?? undefined,
        whyNot: c.why_not ?? undefined,
        airTempC: c.air_temp_c ?? ("values" in c ? c.values?.temperature_c : undefined) ?? undefined,
        surfaceTempC: c.surface_temp_c ?? ("values" in c ? c.values?.surface_temp_c : undefined) ?? undefined,
        windKt: c.wind_kt ?? ("values" in c ? c.values?.wind_speed_kt : undefined) ?? undefined,
        gustKt: c.gust_kt ?? ("values" in c ? c.values?.wind_gust_kt : undefined) ?? undefined,
        visibilityM: c.visibility_m ?? ("values" in c ? c.values?.visibility_m : undefined) ?? undefined,
        humidity: c.humidity ?? ("values" in c ? c.values?.relative_humidity : undefined) ?? undefined,
      },
      breaches: (w.breaches ?? []).map((b) => ({ kind: b.kind, why: b.why ?? b.detail ?? "" })),
      unknowns: (w.unknowns ?? []).map((b) => ({ kind: b.kind, why: b.why ?? b.detail ?? "" })),
      limitsOnFile: w.limits_on_file ?? 0,
      checksMade: w.checks_made ?? 0,
      known: w.known === true,
      /* undefined collapses to null, never to false: "not determined" and
         "not allowed" are different answers */
      allowed: w.allowed ?? null,
      /* Never invent a verdict. If the platform gave none, the reason it gave
         IS the verdict, and an empty string would render as a blank panel
         that reads like a pass. */
      verdict: w.verdict ?? w.why_not ?? "The platform returned no verdict for this work.",
    };
  }

  async programme(siteId: string, fiscalYear: number, envelope?: number): Promise<Programme> {
    const raw = await this.http.get("/v1/programme", {
      site_id: siteId,
      fiscal_year: fiscalYear,
      envelope,
    });
    const p = parse(wireProgramme, raw, "programme");
    const t = p.totals;
    return {
      available: p.available,
      whyNot: p.why_not ?? undefined,
      fiscalYear: p.fiscal_year,
      currency: p.currency ?? undefined,
      envelope: p.envelope ?? undefined,
      searched: p.searched,
      scheduled: (p.scheduled ?? []).map((j) => ({
        id: j.id,
        title: j.title,
        subject: j.subject ?? undefined,
        cost: j.cost,
        escalationAvoided: j.escalation_avoided ?? undefined,
        from: j.from,
        to: j.to,
        whyThen: j.why_then,
        weatherBasis: j.weather_basis ?? undefined,
      })),
      deferred: (p.deferred ?? []).map((j) => ({
        id: j.id,
        title: j.title,
        costNow: j.cost_now ?? undefined,
        escalation: j.escalation ?? undefined,
        why: j.why,
      })),
      undecidable: (p.undecidable ?? []).map((j) => ({
        id: j.id,
        title: j.title,
        costNow: j.cost_now ?? undefined,
        missing: j.missing,
        whoSupplies: j.who_supplies,
        unblocks: j.unblocks,
      })),
      totals: {
        scheduled: t?.scheduled ?? 0,
        scheduledCost: t?.scheduled_cost ?? 0,
        deferred: t?.deferred ?? 0,
        deferredEscalation: t?.deferred_escalation ?? 0,
        undecidable: t?.undecidable ?? 0,
        envelopeUnspent: t?.envelope_unspent ?? 0,
        blockedByMissingFacts: t?.blocked_by_missing_facts ?? 0,
      },
      rankedBy: p.ranked_by ?? undefined,
      note: p.note ?? undefined,
      horizonNote: p.horizon_note ?? undefined,
    };
  }

  async counterfactual(siteId: string, fiscalYear: number, crews?: number): Promise<Study> {
    const raw = await this.http.get("/v1/counterfactual", {
      site_id: siteId,
      fiscal_year: fiscalYear,
      crews,
    });
    const c = parse(wireStudy, raw, "counterfactual");
    const info = c.information;
    return {
      available: c.available,
      whyNot: c.why_not ?? undefined,
      fiscalYear: c.fiscal_year,
      currency: c.currency ?? undefined,
      baseline: {
        envelope: c.baseline?.envelope ?? 0,
        crews: c.baseline?.crews ?? 1,
        totals: c.baseline?.totals ?? undefined,
        note: c.baseline?.note ?? undefined,
      },
      variants: (c.variants ?? []).map((v) => ({
        kind: v.kind === "crew" ? "crew" : "envelope",
        change: v.change,
        envelope: v.envelope ?? undefined,
        crews: v.crews ?? undefined,
        diff: {
          moved: v.diff.moved ?? [],
          scheduledDelta: v.diff.scheduled_delta ?? 0,
          costDelta: v.diff.cost_delta ?? 0,
          escalationDelta: v.diff.escalation_delta ?? 0,
          undecidableDelta: v.diff.undecidable_delta ?? 0,
        },
        reading: v.reading,
      })),
      information: {
        desks: (info?.desks ?? []).map((d) => ({
          desk: d.desk,
          facts: d.facts,
          worth: d.worth,
          candidates: d.candidates ?? [],
        })),
        factsOutstanding: info?.facts_outstanding ?? 0,
        worth: info?.worth ?? 0,
        reading: info?.reading ?? "",
        limit: info?.limit ?? "",
      },
      oneChangeOnly: c.one_change_only ?? undefined,
      notAForecast: c.not_a_forecast ?? undefined,
    };
  }

  async provenance(siteId: string, candidateId: string): Promise<Chain> {
    const raw = await this.http.get(`/v1/provenance/candidate/${encodeURIComponent(candidateId)}`, { site_id: siteId });
    const c = parse(wireChain, raw, "provenance");
    const link = (l: z.infer<typeof wireLink>): Link => ({
      step: l.step,
      /* an unrecognised kind is treated as `missing`, not as observed: a new
         link type the UI does not know must not silently strengthen a chain */
      kind: (["observed", "derived", "asserted", "missing"].includes(l.kind) ? l.kind : "missing") as Link["kind"],
      what: l.what,
      source: l.source ?? undefined,
      recordedBy: l.recorded_by ?? undefined,
      recordedAt: l.recorded_at ?? undefined,
      ageDays: l.age_days ?? undefined,
      concern: l.concern ?? undefined,
      wouldStrengthen: l.would_strengthen ?? undefined,
    });
    return {
      found: c.found,
      whyNot: c.why_not ?? undefined,
      subject: {
        id: c.subject?.id ?? candidateId,
        title: c.subject?.title ?? "",
        status: c.subject?.status ?? undefined,
        where: c.subject?.where ?? undefined,
      },
      links: (c.links ?? []).map(link),
      counts: (c.counts ?? {}) as Chain["counts"],
      complete: c.complete === true,
      weakest: c.weakest ? link(c.weakest) : undefined,
      gaps: (c.gaps ?? []).map(link),
      reading: c.reading ?? "",
      noScore: c.no_score ?? undefined,
    };
  }

  async createDocument(input: NewDocumentInput): Promise<NewDocumentResult> {
    const raw = await this.http.post("/v1/library/documents", {
      type: input.type,
      title: input.title,
      site_id: input.siteId ?? null,
      reference: input.reference ?? null,
      classification: input.classification ?? null,
      tags: input.tags,
    });
    const d = parse(wireNewDoc, raw, "new document");
    return {
      id: d.id,
      title: d.title,
      reference: d.reference ?? undefined,
      classification: d.classification,
      status: d.status,
      controlled: d.controlled ?? false,
    };
  }

  async addRevision(documentId: string, input: NewRevisionInput): Promise<NewRevisionResult> {
    const form = new FormData();
    form.set("revision", input.revision);
    if (input.changeNote) form.set("change_note", input.changeNote);
    if (input.issuedOn) form.set("issued_on", input.issuedOn);
    if (input.expiresOn) form.set("expires_on", input.expiresOn);
    if (input.file) form.set("file", input.file, input.file.name);
    const raw = await this.http.postForm(`/v1/library/documents/${encodeURIComponent(documentId)}/revisions`, form);
    const r = parse(wireNewRev, raw, "new revision");
    return {
      id: r.id,
      revision: r.revision,
      sequence: r.sequence,
      status: r.status,
      reviewDueOn: r.review_due_on ?? undefined,
      expiresOn: r.expires_on ?? undefined,
    };
  }

  async transitionRevision(revisionId: string, input: RevisionTransitionInput): Promise<RevisionTransitionResult> {
    const raw = await this.http.post(`/v1/library/revisions/${encodeURIComponent(revisionId)}/transition`, {
      to: input.to,
      note: input.note ?? null,
      effective_from: input.effectiveFrom ?? null,
    });
    const r = parse(wireRevTransition, raw, "revision transition");
    return { revisionId: r.revision_id, from: r.from, to: r.to };
  }

  async acknowledgeRevision(revisionId: string): Promise<{ acknowledged: boolean }> {
    const raw = await this.http.post(`/v1/library/revisions/${encodeURIComponent(revisionId)}/acknowledge`);
    return { acknowledged: parse(wireAck, raw, "acknowledgement").acknowledged };
  }

  /* ---- capital projects (projects.py) ------------------------------------------- */

  async getProjectVocabulary(): Promise<ProjectVocabulary> {
    const v = parse(wireVocabulary, await this.http.get("/v1/projects/vocabulary"), "project vocabulary");
    return {
      gateSets: v.gate_sets.map((g) => ({
        key: g.key,
        version: g.version ?? undefined,
        name: g.name ?? g.key,
        pack: g.pack ?? undefined,
        description: g.description ?? undefined,
        gates: g.gates.map((x) => ({
          key: x.key,
          name: x.name ?? x.key,
          requires: x.requires,
          description: x.description ?? undefined,
        })),
      })),
      disciplines: v.disciplines.map(mapVocabularyTerm),
      categories: v.categories.map(mapVocabularyTerm),
      issues: v.issues,
    };
  }

  /* ── build 2: inspections ── */
  async recordInspectionRound(input: InspectionRoundInput, idempotencyKey?: string): Promise<InspectionRoundResult> {
    const raw = await this.http.post("/v1/inspections", inspectionsWire.toWireRound(input), idempotencyKey);
    return inspectionsWire.fromWireRoundResult(parse(inspectionsWire.wireRoundResult, raw, "inspection round"));
  }

  async listInspectionRounds(q: InspectionRoundQuery): Promise<InspectionRound[]> {
    const raw = await this.http.get("/v1/inspections/rounds", {
      site_id: q.siteId,
      template: [...q.templates],
      limit: q.limit ?? 100,
    });
    return inspectionsWire.fromWireRounds(parse(inspectionsWire.wireRounds, raw, "inspection rounds"));
  }

  async listProjects(q: ProjectQuery): Promise<ProjectRow[]> {
    const raw = await this.http.get("/v1/projects", {
      site_id: q.siteId,
      status: q.status,
      category: q.category,
      /* Sent only when set: the router defaults it to false, and sending
         "false" would make an unfiltered request look like a filtered one in
         the platform's own echoed `filter`. */
      live_only: q.liveOnly ? "true" : undefined,
      limit: 500,
    });
    return parse(wireProjects, raw, "projects").projects.map(mapProjectRow);
  }

  /**
   * One project with everything hanging off it.
   *
   * `gates`, `holding` and `work_stopped` are counted on a portfolio row but
   * not on the detail, so they are taken here from the platform's own gate and
   * hold-point lists rather than left at zero — a detail page reporting "0
   * gates" for a project whose gates are on the screen beneath it is worse
   * than one reporting nothing.
   */
  async createProject(siteId: string, input: CreateProjectInput): Promise<{ id: string; code: string }> {
    return parse(
      z.object({ id: z.string(), code: z.string() }).passthrough(),
      await this.http.post("/v1/projects", {
        site_id: siteId,
        name: input.name,
        category: input.category,
        gate_set: input.gateSet,
        code: input.code,
        description: input.description,
        status: input.status ?? "planned",
        start_date: input.startDate,
        end_date: input.endDate,
        baseline_start: input.baselineStart,
        baseline_end: input.baselineEnd,
        budget_minor: input.budgetMinor,
        currency: input.currency ?? "SAR",
        contractor: input.contractor,
        consultant: input.consultant,
      }),
      "created project",
    );
  }

  async updateProject(id: string, input: UpdateProjectInput): Promise<void> {
    await this.http.post(`/v1/projects/${encodeURIComponent(id)}`, {
      name: input.name,
      description: input.description,
      status: input.status,
      category: input.category,
      budget_minor: input.budgetMinor,
      committed_minor: input.committedMinor,
      spent_minor: input.spentMinor,
      contractor: input.contractor,
      consultant: input.consultant,
    });
  }

  async setProjectSchedule(id: string, input: ScheduleInput): Promise<ScheduleResult> {
    const r = parse(
      z.object({ rebaselined: z.boolean(), warnings: z.array(z.string()).default([]) }).passthrough(),
      await this.http.post(`/v1/projects/${encodeURIComponent(id)}/schedule`, {
        start_date: input.startDate,
        end_date: input.endDate,
        baseline_start: input.baselineStart,
        baseline_end: input.baselineEnd,
        rebaseline_reason: input.rebaselineReason,
      }),
      "schedule",
    );
    return { rebaselined: r.rebaselined, warnings: r.warnings };
  }

  async getProjectLog(id: string): Promise<ProjectLog> {
    const entry = z
      .object({
        id: z.string(),
        kind: z.enum(["note", "update", "action"]),
        body: z.string(),
        progress_pct: z.number().nullable().optional(),
        owner: z.string().nullable().optional(),
        due_date: z.string().nullable().optional(),
        status: z.enum(["open", "done", "cancelled"]).nullable().optional(),
        overdue: z.boolean().default(false),
        closed_at: z.string().nullable().optional(),
        closure_note: z.string().nullable().optional(),
        created_at: z.string(),
        created_by_name: z.string().nullable().optional(),
        closed_by_name: z.string().nullable().optional(),
      })
      .passthrough();
    const r = parse(
      z
        .object({
          entries: z.array(entry),
          open_actions: z.number(),
          overdue_actions: z.number(),
          latest_progress: z
            .object({
              progress_pct: z.number(),
              reported_at: z.string(),
              reported_by: z.string().nullable().optional(),
            })
            .nullable()
            .optional(),
        })
        .passthrough(),
      await this.http.get(`/v1/projects/${encodeURIComponent(id)}/log`),
      "project log",
    );
    return {
      entries: r.entries.map((e) => ({
        id: e.id,
        kind: e.kind,
        body: e.body,
        progressPct: e.progress_pct ?? undefined,
        owner: e.owner ?? undefined,
        dueDate: e.due_date ?? undefined,
        status: e.status ?? undefined,
        overdue: e.overdue,
        closedAt: e.closed_at ?? undefined,
        closedBy: e.closed_by_name ?? undefined,
        closureNote: e.closure_note ?? undefined,
        createdAt: e.created_at,
        createdBy: e.created_by_name ?? undefined,
      })),
      openActions: r.open_actions,
      overdueActions: r.overdue_actions,
      latestProgress: r.latest_progress
        ? {
            pct: r.latest_progress.progress_pct,
            reportedAt: r.latest_progress.reported_at,
            reportedBy: r.latest_progress.reported_by ?? undefined,
          }
        : undefined,
    };
  }

  async addProjectLogEntry(id: string, input: NewLogEntryInput): Promise<void> {
    await this.http.post(`/v1/projects/${encodeURIComponent(id)}/log`, {
      kind: input.kind,
      body: input.body,
      progress_pct: input.progressPct,
      owner: input.owner,
      due_date: input.dueDate,
    });
  }

  async closeProjectAction(entryId: string, input: CloseActionInput): Promise<void> {
    await this.http.post(`/v1/projects/log/${encodeURIComponent(entryId)}/close`, {
      outcome: input.outcome,
      note: input.note,
    });
  }

  async getProject(id: string): Promise<ProjectDetail> {
    const p = parse(wireProjectDetail, await this.http.get(`/v1/projects/${encodeURIComponent(id)}`), "project");
    const gates = p.gates.gates.map(mapGate);
    const holdPoints = p.hold_points.hold_points.map(mapHoldPoint);
    return {
      ...mapProjectRow({
        ...p,
        gates: undefined,
        gates_approved: undefined,
        holding: undefined,
        work_stopped: undefined,
      }),
      gates: p.gates.count,
      gatesApproved: gates.filter((g) => g.status === "approved").length,
      holding: p.hold_points.count,
      workStopped: p.hold_points.state === "work_stopped",
      description: p.description ?? undefined,
      consultant: p.consultant ?? undefined,
      ownerName: p.owner_name ?? undefined,
      gateList: gates,
      gateSummary: {
        count: p.gates.count,
        decided: p.gates.decided,
        nextGate: p.gates.next_gate,
        decidedWithoutEvidence: p.gates.decided_without_evidence,
        state: p.gates.state,
        why: p.gates.why ?? undefined,
      },
      inspections: p.inspections.inspections.map(mapProjectInspection),
      links: p.links.map((l) => ({
        entityKind: l.entity_kind,
        entityId: l.entity_id,
        relation: l.relation ?? undefined,
        entityRef: l.entity_ref ?? undefined,
        entityName: l.entity_name ?? undefined,
      })),
      holdPoints,
      rebaselines: p.rebaselines.map((r) => ({
        baselineStart: r.baseline_start ?? undefined,
        baselineEnd: r.baseline_end ?? undefined,
        replacedAt: r.replaced_at ?? undefined,
        replacedBy: r.replaced_by ?? undefined,
        reason: r.reason ?? undefined,
      })),
    };
  }

  async listHoldPoints(siteId: string): Promise<HoldPointReport> {
    const h = parse(
      wireHoldPoints,
      await this.http.get("/v1/projects/hold-points", { site_id: siteId }),
      "hold points",
    );
    return {
      holdPoints: h.hold_points.map(mapHoldPoint),
      count: h.count,
      holdPointsEver: h.hold_points_ever,
      state: h.state,
      note: h.note ?? undefined,
    };
  }

  async getProjectAttention(siteId: string): Promise<AttentionReport> {
    const raw = await this.http.get("/v1/projects/attention", { site_id: siteId });
    return mapAttention(parse(wireAttention, raw, "projects needing attention"));
  }

  async getProjectInspection(id: string): Promise<ProjectInspectionDetail> {
    const i = parse(
      wireInspectionDetailP,
      await this.http.get(`/v1/projects/inspections/${encodeURIComponent(id)}`),
      "project inspection",
    );
    return {
      id: i.id,
      reference: i.reference,
      title: i.title,
      kind: i.kind,
      discipline: i.discipline,
      checklistKey: i.checklist_key ?? undefined,
      location: i.location ?? undefined,
      scheduledFor: i.scheduled_for ?? undefined,
      performedAt: i.performed_at ?? undefined,
      performedByName: i.performed_by_name ?? undefined,
      result: i.result ?? undefined,
      releasedAt: i.released_at ?? undefined,
      releasedByName: i.released_by_name ?? undefined,
      witnessedBy: i.witnessed_by ?? undefined,
      defects: i.defects,
      stoppingWork: i.stopping_work ?? (i.kind === "hold" && !i.released_at),
      holdingBecause: i.holding_because ?? undefined,
      createdAt: i.created_at,
      projectId: i.project_id,
      projectCode: i.project_code,
      projectName: i.project_name ?? undefined,
      siteId: i.site_id ?? undefined,
      notes: i.notes ?? undefined,
      sheet: i.items.map((x) => ({
        seq: x.seq,
        itemKey: x.item_key ?? undefined,
        prompt: x.prompt ?? undefined,
        result: x.result,
        valueNum: x.value_num ?? undefined,
        valueText: x.value_text ?? undefined,
        tolerance: x.tolerance ?? undefined,
        note: x.note ?? undefined,
        defectId: x.defect_id ?? undefined,
        defectStatus: x.defect_status ?? undefined,
        defectSeverity: x.defect_severity ?? undefined,
      })),
      quality: mapQuality(i.quality),
    };
  }

  async submitGate(projectId: string, gateKey: string, input: GateSubmitInput): Promise<GateSubmitResult> {
    const raw = await this.http.post(
      `/v1/projects/${encodeURIComponent(projectId)}/gates/${encodeURIComponent(gateKey)}/submit`,
      { evidence: input.evidence ?? [], due_date: input.dueDate ?? null },
      crypto.randomUUID(),
    );
    const r = parse(wireGateSubmit, raw, "gate submission");
    return {
      key: r.key,
      status: r.status,
      submittedAt: r.submitted_at ?? undefined,
      evidence: mapEvidence(r.evidence_state),
      warnings: r.warnings,
    };
  }

  /**
   * Decide a gate. `project:gate`, not `project:write`.
   *
   * The platform does not refuse a decision taken without the evidence the
   * gate asked for — refusing would produce gates decided in a meeting and
   * recorded nowhere. It records it instead, on the gate, on the event and in
   * the audit log, and hands it back in `warnings`. Those are surfaced to the
   * person who just decided rather than swallowed.
   */
  async decideGate(projectId: string, gateKey: string, input: GateDecisionInput): Promise<GateDecisionResult> {
    const raw = await this.http.post(
      `/v1/projects/${encodeURIComponent(projectId)}/gates/${encodeURIComponent(gateKey)}/decide`,
      { decision: input.decision, note: input.note ?? null, evidence: input.evidence ?? [] },
      crypto.randomUUID(),
    );
    const r = parse(wireGateDecision, raw, "gate decision");
    return {
      key: r.key,
      decision: r.decision,
      decidedAt: r.decided_at ?? undefined,
      projectCode: r.project_code ?? undefined,
      evidence: mapEvidence(r.evidence_state),
      outOfOrderAfter: r.out_of_order_after,
      warnings: r.warnings,
    };
  }

  async recordInspectionItems(inspectionId: string, results: ItemResultInput[]): Promise<InspectionWriteResult> {
    const raw = await this.http.post(
      `/v1/projects/inspections/${encodeURIComponent(inspectionId)}/items`,
      results.map((r) => ({
        seq: r.seq ?? null,
        item_key: r.itemKey ?? null,
        result: r.result,
        value_num: r.valueNum ?? null,
        value_text: r.valueText ?? null,
        tolerance: r.tolerance ?? null,
        note: r.note ?? null,
      })),
      crypto.randomUUID(),
    );
    return mapInspectionWrite(parse(wireInspectionWrite, raw, "recorded items"));
  }

  /** Records what was found. It does **not** release a hold point — that is `releaseHoldPoint`. */
  async recordInspectionResult(inspectionId: string, input: RecordResultInput): Promise<InspectionWriteResult> {
    const raw = await this.http.post(
      `/v1/projects/inspections/${encodeURIComponent(inspectionId)}/result`,
      {
        result: input.result,
        notes: input.notes ?? null,
        witnessed_by: input.witnessedBy ?? null,
        performed_at: input.performedAt ?? null,
        evidence: [],
      },
      crypto.randomUUID(),
    );
    return mapInspectionWrite(parse(wireInspectionWrite, raw, "inspection result"));
  }

  async releaseHoldPoint(inspectionId: string, note?: string): Promise<InspectionWriteResult> {
    const raw = await this.http.post(
      `/v1/projects/inspections/${encodeURIComponent(inspectionId)}/release`,
      { note: note ?? null },
      crypto.randomUUID(),
    );
    return mapInspectionWrite(parse(wireInspectionWrite, raw, "hold-point release"));
  }

  async listNotifications(siteId: string): Promise<Notification[]> {
    const n = parse(
      wireNotifications,
      await this.http.get("/v1/notifications", { site_id: siteId, limit: 50 }),
      "notifications",
    );
    return n.notifications.map((x) => ({
      id: String(x.id),
      at: x.created_at,
      severity: x.severity ?? undefined,
      role: x.role ?? undefined,
      title: x.title,
      body: x.body ?? undefined,
      ruleKey: x.rule_key ?? undefined,
      subjectKind: x.subject_kind ?? undefined,
      subjectId: x.subject_id ?? undefined,
      read: x.read_at != null,
    }));
  }

  async skyModel(): Promise<SkyModel> {
    /* A deployment too old to know the field, or one that refuses the whole
       capabilities call, is a deployment with no model — not an error to
       show anybody. */
    try {
      const raw = (await this.http.get("/v1/assist/capabilities")) as { sky_model?: Record<string, unknown> };
      const m = raw?.sky_model ?? {};
      return {
        configured: m.configured === true,
        model: (m.model as string) ?? null,
        provider: (m.provider as string) ?? null,
        note: (m.note as string) ?? "",
      };
    } catch {
      return { configured: false, model: null, provider: null, note: "" };
    }
  }

  async composeWithModel(brief: string, material: string, signal?: AbortSignal): Promise<ComposedText> {
    const raw = (await this.http.post("/v1/assist/compose", { brief, material }, undefined, signal)) as {
      text?: string;
      model?: string;
      tokens?: { input?: number; output?: number; remaining_today?: number | null };
    };
    return {
      text: (raw.text ?? "").trim(),
      model: raw.model ?? "",
      tokens: {
        input: raw.tokens?.input ?? 0,
        output: raw.tokens?.output ?? 0,
        remainingToday: raw.tokens?.remaining_today ?? null,
      },
    };
  }

  async sendVoiceLog(lines: string[]): Promise<void> {
    await this.http.post("/v1/assist/voice-log", { lines });
  }

  /* ── build 5: shared registers, row by row ── */

  async listRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    opts: { includeDeleted?: boolean } = {},
  ): Promise<import("../domain/registerRows").RegisterRows> {
    const raw = parse(
      registerWire.wireRows,
      await this.http.get(`/v1/registers/${encodeURIComponent(key)}/rows`, {
        site_id: siteId,
        include_deleted: opts.includeDeleted ? "true" : undefined,
      }),
      "register rows",
    );
    return {
      register: key,
      rows: raw.rows.map(registerWire.fromWireRow),
      ever: raw.ever ?? raw.rows.length,
      canWrite: raw.can_write ?? true,
    };
  }

  async changeRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    ops: import("../domain/registerRows").RowOp[],
  ): Promise<import("../domain/registerRows").RegisterRow[]> {
    try {
      const raw = parse(
        registerWire.wireChanged,
        await this.http.post(`/v1/registers/${encodeURIComponent(key)}/changes`, { site_id: siteId, ops }),
        "register changes",
      );
      return raw.rows.map(registerWire.fromWireRow);
    } catch (e) {
      throw registerWire.asConflict(e, key);
    }
  }

  async importRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    rows: { id: string; data: Record<string, unknown> }[],
    noteId?: string,
  ): Promise<{ imported: number }> {
    const raw = parse(
      registerWire.wireImported,
      await this.http.post(`/v1/registers/${encodeURIComponent(key)}/import`, {
        site_id: siteId,
        rows,
        note_id: noteId,
      }),
      "register import",
    );
    return { imported: raw.imported };
  }

  async converseWithSky(input: SkyConverseInput, signal?: AbortSignal): Promise<SkyConverseReply> {
    const raw = (await this.http.post(
      "/v1/assist/converse",
      {
        message: input.message,
        site_id: input.siteId,
        session_id: input.sessionId,
        local_time: input.localTime,
        history: input.history,
        screen: input.screen,
        screens: input.screens,
      },
      undefined,
      signal,
    )) as {
      text?: string;
      model?: string;
      session_id?: string;
      steps?: { intent?: string; row_count?: number }[];
      draft?: {
        title: string;
        priority: number;
        kind?: string;
        description?: string;
        asset_tag?: string;
        zone_code?: string;
        sla_hours?: number;
      } | null;
      navigate?: string | null;
      withheld?: boolean;
      general?: boolean;
    };
    const d = raw.draft;
    return {
      text: (raw.text ?? "").trim(),
      model: raw.model ?? "",
      sessionId: raw.session_id,
      steps: (raw.steps ?? []).map((s) => ({ intent: s.intent ?? "", rowCount: s.row_count ?? 0 })),
      draft: d
        ? {
            title: d.title,
            priority: d.priority,
            kind: d.kind ?? "corrective",
            description: d.description,
            assetTag: d.asset_tag,
            zoneCode: d.zone_code,
            slaHours: d.sla_hours,
          }
        : undefined,
      navigate: raw.navigate ?? undefined,
      withheld: Boolean(raw.withheld),
      general: Boolean(raw.general),
    };
  }

  async askAssistant(q: AssistQuestion): Promise<AssistReply> {
    const raw = await this.http.post("/v1/assist/ask", {
      question: q.question,
      site_id: q.siteId,
      session_id: q.sessionId,
    });
    const a = parse(wireAssist, raw, "assistant answer");
    const body = a.answer;
    return {
      question: a.question,
      answer: {
        text: body.text,
        empty: body.empty === true,
        highlights: body.highlights ?? [],
        truncated: body.truncated ? { shown: body.truncated.shown, total: body.truncated.total } : undefined,
        summary: body.summary ?? undefined,
        narrated: body.narrated === true,
        /* Two different failures — a summary withheld for naming a figure the
           rows do not support, and a model that could not be reached — and the
           platform words each of them itself. Passed through rather than
           re-phrased: the sentence ends "the figures above are unaffected",
           which is the part an operator needs and the part a paraphrase drops. */
        narrationError: body.narration_error ?? undefined,
      },
      intent: a.intent ?? undefined,
      answers: a.answers ?? undefined,
      parameters: a.parameters ?? {},
      resolver: a.resolver ?? "rules",
      confidence: a.confidence ?? 0,
      citations: (a.citations ?? []).map((c) => ({
        intent: c.intent,
        parameters: c.parameters ?? {},
        rowCount: c.row_count,
        /* Older deployments answer without it; the page is then all there was. */
        total: c.total ?? c.row_count,
        rows: c.rows ?? [],
      })),
      refused: a.refused === true,
      refusalReason: a.refusal_reason ?? undefined,
      /* Older deployments do not say; treating an unknown kind as a plain
         no-match keeps their behaviour exactly as it was. */
      refusalKind: a.refusal_kind === "out_of_scope" ? "out_of_scope" : "no_match",
      canAnswer: (a.can_answer ?? []).map((x) => ({
        intent: x.intent,
        question: x.question,
        example: x.example ?? "",
      })),
      inheritedNote: a.inherited_note ?? undefined,
      sessionId: a.session_id ?? undefined,
    };
  }
}

/* ----------------------------------------------------- the assistant ------
 *
 * Loose on purpose, and only in one direction. Every field the platform adds
 * to an answer is additive — a narration, a truncation note, a new citation
 * key — and a strict schema would turn each of those into a thrown
 * `DataSourceError` in front of an operator mid-question. So the envelope
 * passes through and the two fields that carry meaning rather than decoration,
 * `text` and `refused`, are the ones required.
 */
const wireAssist = z
  .object({
    question: z.string(),
    answer: z
      .object({
        text: z.string(),
        empty: z.boolean().optional(),
        narrated: z.boolean().optional(),
        highlights: z.array(z.string()).optional(),
        truncated: z.object({ shown: z.number(), total: z.number() }).passthrough().optional(),
        summary: z.string().optional(),
        narration_error: z.string().optional(),
      })
      .passthrough(),
    intent: z.string().nullable().optional(),
    answers: z.string().nullable().optional(),
    parameters: z.record(z.string(), z.string()).optional(),
    resolver: z.string().optional(),
    confidence: z.number().optional(),
    citations: z
      .array(
        z
          .object({
            intent: z.string(),
            parameters: z.record(z.string(), z.string()).optional(),
            row_count: z.number(),
            total: z.number().optional(),
            rows: z.array(z.record(z.string(), z.unknown())).optional(),
          })
          .passthrough(),
      )
      .optional(),
    refused: z.boolean().optional(),
    refusal_reason: z.string().nullable().optional(),
    refusal_kind: z.string().nullable().optional(),
    can_answer: z
      .array(z.object({ intent: z.string(), question: z.string(), example: z.string().optional() }).passthrough())
      .optional(),
    inherited_note: z.string().nullable().optional(),
    session_id: z.string().nullable().optional(),
  })
  .passthrough();

/* ------------------------------------------------------ the planning join --
 *
 * `known` is `.optional()` on each half and defaults to TRUE only where the
 * platform omitted it entirely. That default is the conservative one for a
 * legacy response and the dangerous one for a new field, so it is written
 * explicitly rather than left to a falsy check: `known: undefined` treated as
 * `false` would report every older platform as unassessable, and treated
 * silently as `true` would hide the very state this endpoint exists to report.
 */
const wireWindow = z.object({ from: z.string(), to: z.string() });

const wireSide = z.object({
  found: z.boolean().nullable().optional(),
  known: z.boolean().nullable().optional(),
  windows: z.array(wireWindow).optional(),
  why_not: z.string().nullable().optional(),
  least_bad: z.unknown().optional(),
  /* [reason, count] pairs — a tuple, not an object, in the platform's own shape */
  blocking: z
    .array(z.tuple([z.string(), z.number()]))
    .nullable()
    .optional(),
  assessed_from: z.array(z.string()).nullable().optional(),
});

const wirePlan = z.object({
  position: z.string(),
  category: z.string(),
  hours_needed: z.number(),
  traffic: wireSide,
  weather: wireSide,
  found: z.boolean().nullable().optional(),
  known: z.boolean().nullable().optional(),
  why_not: z.string().nullable().optional(),
  windows: z.array(wireWindow).optional(),
  window_count: z.number().nullable().optional(),
  earliest: wireWindow.nullable().optional(),
  verdict: z.string().nullable().optional(),
  caveat: z.string().nullable().optional(),
  weather_blockers: z
    .array(z.tuple([z.string(), z.number()]))
    .nullable()
    .optional(),
});

const mapSide = (x: z.infer<typeof wireSide>): PlanSide => ({
  found: x.found === true,
  known: x.known !== false,
  windows: x.windows ?? [],
  whyNot: x.why_not ?? undefined,
  leastBad: x.least_bad,
  blocking: x.blocking ?? undefined,
  assessedFrom: x.assessed_from ?? undefined,
});

/* -------------------------------------------------- deferred liability -----
 *
 * `not_priced_detail` is required rather than optional, and that is deliberate:
 * the platform's contract is that an excluded candidate is always named. A
 * response carrying totals with no account of what they left out is the one
 * shape this feature must refuse, because it is indistinguishable from a
 * complete answer. */
const wireUnpriced = z.object({ title: z.string(), why_not: z.string() });

const wireScenario = z.object({
  envelope: z.number(),
  funded: z.number(),
  funded_cost: z.number(),
  deferred: z.number(),
  deferred_cost_now: z.number(),
  cost_of_waiting_one_year: z.number(),
  escalation_not_computed: z.number().optional(),
  closure_hours_per_year: z.number().optional(),
  closure_known_for: z.number().optional(),
  closure_unknown_for: z.number().optional(),
  deferred_detail: z
    .array(
      z.object({
        title: z.string(),
        escalation: z.number().nullable().optional(),
        consequence: z
          .object({
            known: z.boolean().optional(),
            why_not: z.string().nullable().optional(),
            closure_hours_per_year: z.number().nullable().optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

/* A year with no funding envelope comes back short — available:false, why_not and a count —
   and used to fail the whole screen ("Unexpected liability curve response"). */
const wireCurve = z.object({
  available: z.boolean(),
  fiscal_year: z.number().optional(),
  why_not: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  candidates: z.number().default(0),
  priced: z.number().default(0),
  not_priced: z.number().default(0),
  not_priced_detail: z.array(wireUnpriced).optional(),
  coverage_note: z.string().nullable().optional(),
  scenarios: z.array(wireScenario).optional(),
});

const wireRegister = z.object({
  candidates: z.number(),
  priced: z.number(),
  not_priced: z.number(),
  note: z.string().nullable().optional(),
  register: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      category: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
      forecast_for: z.string().nullable().optional(),
      priced: z.boolean(),
      treatment: z.string().nullable().optional(),
      currency: z.string().nullable().optional(),
      cost_now: z.number().nullable().optional(),
      cost_deferred: z.number().nullable().optional(),
      escalation: z.number().nullable().optional(),
      defer_years: z.number().nullable().optional(),
      why_not: z.string().nullable().optional(),
      basis: z
        .object({
          unit: z.string().nullable().optional(),
          rate: z.string().nullable().optional(),
          priced_on: z.string().nullable().optional(),
          rate_source: z.string().nullable().optional(),
          quantity: z.string().nullable().optional(),
          factor_per_year: z.string().nullable().optional(),
          deferral_basis: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    }),
  ),
});

/* ------------------------------------------------------- closure cost ------ */
/**
 * The capacity model's schedule coverage, as a sentence. It arrives as
 * {covered, fraction, sources}; the client expected a string, so every
 * "closing a stand" and "critical positions" answer failed to parse
 * (audit 2026-10-10).
 */
function coverageText(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (!v || typeof v !== "object") return undefined;
  const o = v as { covered?: boolean; fraction?: number; sources?: string[] };
  const from = o.sources?.length ? ` (${o.sources.join(", ")})` : "";
  if (o.covered) return `the schedule covers the whole window${from}`;
  if (typeof o.fraction === "number") return `the schedule covers ${Math.round(o.fraction * 100)}% of the window${from}`;
  return "no schedule is loaded for this window";
}

type ClosureDetailRow = { flight?: string; sizeCode?: string; unitClass?: string; occupiedFrom?: string; movesTo?: string; why?: string };
const WHY_GROUP: Record<string, string> = {
  stranded: "nowhere to go",
  bussed: "remote position, passengers bus",
  undetermined: "cannot be assessed",
  absorbed: "moves to a compatible stand",
};
/** Flatten the grouped detail into rows; each row says which group it came from when it has no reason of its own. */
function closureDetail(v: unknown): ClosureDetailRow[] {
  type Raw = { flight?: string | null; size_code?: string | null; unit_class?: string | null; occupied_from?: string | null; moves_to?: string | string[] | null; why?: string | null };
  const one = (d: Raw, group?: string): ClosureDetailRow => ({
    flight: d.flight ?? undefined,
    sizeCode: d.size_code ?? undefined,
    unitClass: d.unit_class ?? undefined,
    occupiedFrom: d.occupied_from ?? undefined,
    movesTo: Array.isArray(d.moves_to) ? d.moves_to.join(", ") : (d.moves_to ?? undefined),
    why: d.why ?? (group ? WHY_GROUP[group] : undefined),
  });
  if (Array.isArray(v)) return (v as Raw[]).map((d) => one(d));
  if (!v || typeof v !== "object") return [];
  return Object.entries(v as Record<string, unknown>).flatMap(([group, rows]) =>
    Array.isArray(rows) ? (rows as Raw[]).map((d) => one(d, group)) : [],
  );
}

const wireClosure = z.object({
  /* absent when the model cannot answer — "no position X at this site" — so they come from the query */
  position: z.string().optional(),
  window: z.object({ from: z.string(), to: z.string(), hours: z.number().optional() }).optional(),
  known: z.boolean().optional(),
  why_not: z.string().nullable().optional(),
  movements: z.number().optional(),
  absorbed: z.number().optional(),
  bussed: z.number().optional(),
  stranded: z.number().optional(),
  undetermined: z.number().optional(),
  hours: z.number().nullable().optional(),
  schedule_coverage: z.unknown().optional(),
  assumption: z.string().nullable().optional(),
  /* the backend groups these as {stranded, bussed, undetermined, absorbed}; an older shape was one list */
  detail: z.unknown().optional(),
  verdict: z.string().nullable().optional(),
});

const wireCriticalRow = z.object({
  position: z.string(),
  stranded: z.number().optional(),
  bussed: z.number().optional(),
  movements: z.number().optional(),
  undetermined: z.number().optional(),
});

const wireCritical = z.object({
  /* absent when there is no schedule to judge from */
  window: z.object({ from: z.string(), to: z.string() }).optional(),
  known: z.boolean().optional(),
  why_not: z.string().nullable().optional(),
  critical: z.array(wireCriticalRow).optional(),
  flexible: z.array(wireCriticalRow).optional(),
  note: z.string().nullable().optional(),
  schedule_coverage: z.unknown().optional(),
});

/* ------------------------------------------------------------- drift ------ */
const wireDriftCheck = z.object({
  id: z.number(),
  model_key: z.string(),
  window_from: z.string(),
  window_to: z.string(),
  predictions: z.number().optional(),
  decided: z.number().optional(),
  undecided: z.number().optional(),
  pending: z.number().optional(),
  judgeable_fraction: z.number().nullable().optional(),
  change_point: z.string().nullable().optional(),
  verdict: z.string(),
  conclusive: z.boolean().optional(),
  checked_at: z.string().nullable().optional(),
});
const wireDriftChecks = z.object({ checks: z.array(wireDriftCheck) });
const wireDue = z.object({
  model_key: z.string(),
  reference_id: z.string().nullable().optional(),
  horizon_days: z.number().nullable().optional(),
  window_days: z.number().nullable().optional(),
  every_days: z.number().nullable().optional(),
  last_checked: z.string().nullable().optional(),
});
const wireDriftSchedule = z.object({
  due: z.array(wireDue).optional(),
  waiting: z.array(wireDue).optional(),
});

/* ------------------------------------------- survival, and the ledger ------ */
const wirePopulations = z.object({
  populations: z.array(
    z.object({
      population: z.string(),
      time_unit: z.string().nullable().optional(),
      units: z.number().optional(),
      failures: z.number().optional(),
      failure_modes: z.number().nullable().optional(),
      fittable: z.boolean().optional(),
      why_not: z.string().nullable().optional(),
    }),
  ),
});

const wireFits = z.object({
  fits: z.array(
    z.object({
      id: z.string(),
      population: z.string(),
      method: z.string().nullable().optional(),
      units: z.number().optional(),
      failures: z.number().optional(),
      censored: z.number().optional(),
      beta: z.number(),
      beta_low: z.number().nullable().optional(),
      beta_high: z.number().nullable().optional(),
      eta: z.number(),
      time_unit: z.string().nullable().optional(),
      last_failure_at: z.number().nullable().optional(),
      fitted_at: z.string().nullable().optional(),
      basis: z
        .object({
          shape_verdict: z.string().nullable().optional(),
          wear_out: z
            .object({
              known: z.boolean().optional(),
              verdict: z.string().nullable().optional(),
              note: z.string().nullable().optional(),
              rejects_constant_rate: z.boolean().nullable().optional(),
              likelihood_ratio: z.number().nullable().optional(),
              critical_95: z.number().nullable().optional(),
            })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
    }),
  ),
});

const wireFacts = z.object({
  facts: z.array(
    z.object({
      fact_key: z.string(),
      assertions: z.number().optional(),
      parties: z.number().optional(),
      party_keys: z.array(z.string()).optional(),
      subjects: z.number().optional(),
      comparable_subjects: z.number().optional(),
      latest: z.string().nullable().optional(),
      authority: z.string().nullable().optional(),
    }),
  ),
});

const wireBetween = z.object({
  /* a pair the platform cannot compare comes back without these; the query has them */
  fact: z.string().optional(),
  party_a: z.string().optional(),
  party_b: z.string().optional(),
  comparable: z.boolean().optional(),
  why_not: z.string().nullable().optional(),
  pairs: z.number().optional(),
  median_seconds: z.number().nullable().optional(),
  median_minutes: z.number().nullable().optional(),
  iqr_seconds: z.number().nullable().optional(),
  b_later_pct: z.number().nullable().optional(),
  systematic: z.boolean().optional(),
  finding: z.string().nullable().optional(),
});

/* ------------------------------------------------------------- weather ---- */
const wireConditions = z.object({
  known: z.boolean().optional(),
  basis: z.string().nullable().optional(),
  why_not: z.string().nullable().optional(),
  air_temp_c: z.number().nullable().optional(),
  surface_temp_c: z.number().nullable().optional(),
  wind_kt: z.number().nullable().optional(),
  gust_kt: z.number().nullable().optional(),
  visibility_m: z.number().nullable().optional(),
  humidity: z.number().nullable().optional(),
  /* the platform nests the readings under `values`, with its own names (insight/weather.py _values) */
  values: z
    .object({
      temperature_c: z.number().nullable().optional(),
      surface_temp_c: z.number().nullable().optional(),
      relative_humidity: z.number().nullable().optional(),
      wind_speed_kt: z.number().nullable().optional(),
      wind_gust_kt: z.number().nullable().optional(),
      visibility_m: z.number().nullable().optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
});
/* `at`, `conditions` and `verdict` are OPTIONAL because the platform's
   "not known" answer omits all three. When it holds no limits for a category
   it replies 200 with {known, category, why_not} and nothing else — there is
   no verdict to give and no conditions were read. Requiring them made the one
   shape this system is built around unrepresentable, and the screen showed a
   zod error where the module's actual sentence belonged. */
const wireCanWork = z.object({
  category: z.string(),
  at: z.string().optional(),
  conditions: wireConditions.optional(),
  why_not: z.string().nullable().optional(),
  /* a breach explains itself in `detail`, an unknown in `why`; requiring `why` failed the tab on any breach */
  breaches: z.array(z.object({ kind: z.string(), why: z.string().optional(), detail: z.string().optional() })).optional(),
  unknowns: z.array(z.object({ kind: z.string(), why: z.string().optional(), detail: z.string().optional() })).optional(),
  limits_on_file: z.number().optional(),
  checks_made: z.number().optional(),
  known: z.boolean().optional(),
  /* tri-state on the wire and tri-state here: null is not a soft yes */
  allowed: z.boolean().nullable().optional(),
  verdict: z.string().optional(),
});

/* ----------------------------------------------------------- programme ---- */
const wireJob = z.object({ id: z.string(), title: z.string() });
const wireProgramme = z.object({
  available: z.boolean(),
  why_not: z.string().nullable().optional(),
  fiscal_year: z.number(),
  currency: z.string().nullable().optional(),
  envelope: z.number().nullable().optional(),
  searched: z.object({ from: z.string(), to: z.string(), days: z.number() }).optional(),
  scheduled: z
    .array(
      wireJob.extend({
        subject: z.string().nullable().optional(),
        cost: z.number(),
        escalation_avoided: z.number().nullable().optional(),
        from: z.string(),
        to: z.string(),
        why_then: z.string(),
        weather_basis: z.array(z.string()).nullable().optional(),
      }),
    )
    .optional(),
  deferred: z
    .array(
      wireJob.extend({
        cost_now: z.number().nullable().optional(),
        escalation: z.number().nullable().optional(),
        why: z.string(),
      }),
    )
    .optional(),
  undecidable: z
    .array(
      wireJob.extend({
        cost_now: z.number().nullable().optional(),
        missing: z.string(),
        who_supplies: z.string(),
        unblocks: z.string(),
      }),
    )
    .optional(),
  totals: z
    .object({
      scheduled: z.number(),
      scheduled_cost: z.number(),
      deferred: z.number(),
      deferred_escalation: z.number(),
      undecidable: z.number(),
      envelope_unspent: z.number(),
      blocked_by_missing_facts: z.number().optional(),
    })
    .optional(),
  ranked_by: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  horizon_note: z.string().nullable().optional(),
});

/* ------------------------------------------------------ counterfactual ---- */
const wireDiff = z.object({
  moved: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        from: z.string(),
        to: z.string(),
      }),
    )
    .optional(),
  scheduled_delta: z.number().optional(),
  cost_delta: z.number().optional(),
  escalation_delta: z.number().optional(),
  undecidable_delta: z.number().optional(),
});
const wireStudy = z.object({
  available: z.boolean(),
  why_not: z.string().nullable().optional(),
  fiscal_year: z.number(),
  currency: z.string().nullable().optional(),
  baseline: z
    .object({
      envelope: z.number().optional(),
      crews: z.number().optional(),
      totals: z.record(z.string(), z.number()).nullable().optional(),
      note: z.string().nullable().optional(),
    })
    .optional(),
  variants: z
    .array(
      z.object({
        kind: z.string(),
        change: z.string(),
        envelope: z.number().nullable().optional(),
        crews: z.number().nullable().optional(),
        diff: wireDiff,
        reading: z.string(),
      }),
    )
    .optional(),
  information: z
    .object({
      desks: z
        .array(
          z.object({
            desk: z.string(),
            facts: z.number(),
            worth: z.number(),
            candidates: z.array(z.object({ title: z.string(), missing: z.string() })).optional(),
          }),
        )
        .optional(),
      facts_outstanding: z.number().optional(),
      worth: z.number().optional(),
      reading: z.string().optional(),
      limit: z.string().optional(),
    })
    .optional(),
  one_change_only: z.string().nullable().optional(),
  not_a_forecast: z.string().nullable().optional(),
});

/* ---------------------------------------------------------- provenance ---- */
const wireLink = z.object({
  step: z.string(),
  kind: z.string(),
  what: z.string(),
  source: z.string().nullable().optional(),
  recorded_by: z.string().nullable().optional(),
  recorded_at: z.string().nullable().optional(),
  age_days: z.number().nullable().optional(),
  concern: z.string().nullable().optional(),
  would_strengthen: z.string().nullable().optional(),
});
const wireChain = z.object({
  found: z.boolean(),
  why_not: z.string().nullable().optional(),
  subject: z
    .object({
      id: z.string(),
      title: z.string(),
      status: z.string().nullable().optional(),
      where: z.string().nullable().optional(),
    })
    .optional(),
  links: z.array(wireLink).optional(),
  counts: z.record(z.string(), z.number()).optional(),
  complete: z.boolean().optional(),
  weakest: wireLink.nullable().optional(),
  gaps: z.array(wireLink).optional(),
  reading: z.string().optional(),
  no_score: z.string().nullable().optional(),
});

/* ── the migration's wire shapes ──────────────────────────────────────────
   Read loosely on purpose: the counts are keyed by verdict and by triage,
   both of which the platform may extend, and a strict record would reject a
   new verdict rather than show it. */
const wireEvidence = z.object({
  days: z.number(),
  contract_version: z.number(),
  observations: z.number(),
  comparable: z.number(),
  by_verdict: z.record(z.string(), z.number()).default({}),
  agreement_pct: z.number().nullable(),
  untriaged_differences: z.number(),
  triage: z.record(z.string(), z.number()).default({}),
  platform_p95_ms: z.number().nullable(),
});

const wireScreen = z.object({
  id: z.union([z.string(), z.number()]),
  key: z.string(),
  name: z.string(),
  mode: z.string(),
  legacy_route: z.string().nullish(),
  owner: z.string().nullish(),
  tolerance_pct: z.number(),
  min_observations: z.number(),
  cutover_at: z.string().nullish(),
  reverted_at: z.string().nullish(),
  contract_version: z.number(),
  evidence: wireEvidence,
  blocking: z.array(z.string()).default([]),
});

const wireMigrationStatus = z.object({
  screens: z.array(wireScreen),
  by_mode: z.record(z.string(), z.number()).default({}),
  total: z.number(),
  note: z.string(),
});

const wireMigrationScreen = wireScreen.extend({
  description: z.string().nullish(),
  source: z.unknown().nullish(),
  projection: z.unknown().nullish(),
  comparison: z.unknown().nullish(),
  history: z
    .array(
      z.object({
        from_mode: z.string(),
        to_mode: z.string(),
        at: z.string(),
        reason: z.string().nullish(),
        forced: z.boolean().nullish(),
      }),
    )
    .default([]),
});

const wireDifference = z.object({
  id: z.number(),
  observed_at: z.string(),
  verdict: z.string(),
  request_key: z.string().nullish(),
  legacy_rows: z.unknown().nullish(),
  platform_rows: z.unknown().nullish(),
  differences: z.unknown().nullish(),
  detail: z.string().nullish(),
  triage: z.string().nullish(),
  triage_note: z.string().nullish(),
});

function toScreen(d: z.infer<typeof wireScreen>): MigrationScreen {
  const e = d.evidence;
  return {
    id: String(d.id),
    key: d.key,
    name: d.name,
    mode: d.mode as ScreenMode,
    legacyRoute: d.legacy_route ?? undefined,
    owner: d.owner ?? undefined,
    tolerancePct: d.tolerance_pct,
    minObservations: d.min_observations,
    cutoverAt: d.cutover_at ?? undefined,
    revertedAt: d.reverted_at ?? undefined,
    contractVersion: d.contract_version,
    evidence: {
      days: e.days,
      contractVersion: e.contract_version,
      observations: e.observations,
      comparable: e.comparable,
      byVerdict: e.by_verdict as MigrationEvidence["byVerdict"],
      agreementPct: e.agreement_pct,
      untriagedDifferences: e.untriaged_differences,
      triage: e.triage as MigrationEvidence["triage"],
      platformP95Ms: e.platform_p95_ms,
    },
    blocking: d.blocking,
  };
}

function parse<T extends z.ZodTypeAny>(schema: T, raw: unknown, what: string): z.infer<T> {
  const r = schema.safeParse(raw);
  if (r.success) return r.data;
  const issues = r.error.issues.slice(0, 5).map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`);
  throw new DataSourceError(`Unexpected ${what} response from the platform — ${issues.join("; ")}`, {
    detail: r.error.issues,
  });
}

function planToWire(p: AmPlanInput): Record<string, unknown> {
  return {
    asset_id: p.assetId,
    title: p.title,
    trigger: p.trigger,
    interval_days: p.intervalDays,
    usage_measurement: p.usageMeasurement,
    interval_usage: p.intervalUsage,
    first_due_on: p.firstDueOn,
    lead_days: p.leadDays,
    priority: p.priority,
    estimated_hours: p.estimatedHours,
    estimated_cost: p.estimatedCost,
    tasks: p.tasks ?? [],
  };
}

/* ── flight data feeds on the wire (routers/flight_feeds.py) ─────────── */

interface WireFeedStatus {
  state: FlightFeed["status"]["state"];
  why?: string | null;
  last_message_at?: string | null;
  lag_seconds?: number | null;
  source_lag_seconds?: number | null;
  per_hour: number;
  last_24h: number;
  outcomes_24h: Record<FeedOutcome, number>;
  refusals_24h: { reason: string; count: number }[];
}

interface WireFeed {
  id: string;
  key: string;
  name: string;
  kind: FlightFeed["kind"];
  transport: FlightFeed["transport"];
  enabled: boolean;
  config: Record<string, unknown> & {
    auth?: Record<string, string | undefined>;
    secret_refs?: Record<string, string>;
    imap?: FlightFeed["config"]["imap"];
    mapping?: FlightFeed["config"]["mapping"];
  };
  secrets: FlightFeed["secrets"];
  push_path?: string | null;
  last_poll_at?: string | null;
  last_ok_at?: string | null;
  last_error?: string | null;
  status?: WireFeedStatus;
}

interface WireFeedMessage {
  id: string;
  feed_id?: string | null;
  feed?: string | null;
  kind: string;
  channel: string;
  outcome: FeedOutcome;
  reason?: string | null;
  raw: string;
  envelope?: Record<string, unknown> | null;
  ignored?: string[];
  changes?: string[];
  movement_id?: string | null;
  duplicate_of?: string | null;
  replay_of?: string | null;
  sent_at?: string | null;
  received_at: string;
}

interface WireFeedResult {
  index: number;
  outcome: FeedOutcome;
  reason?: string | null;
  movement?: string | null;
  changed?: string[];
  ignored?: string[];
  unmapped?: string[];
  message_id?: string;
}

function emptyWireStatus(): WireFeedStatus {
  return {
    state: "quiet",
    per_hour: 0,
    last_24h: 0,
    outcomes_24h: { taken: 0, held: 0, stored: 0, duplicate: 0, refused: 0 },
    refusals_24h: [],
  };
}

function feedFromWire(f: WireFeed): FlightFeed {
  const c = f.config ?? {};
  const st = f.status ?? emptyWireStatus();
  return {
    id: f.id,
    key: f.key,
    name: f.name,
    kind: f.kind,
    transport: f.transport,
    enabled: f.enabled,
    config: {
      url: c.url as string | undefined,
      cursorParam: c.cursor_param as string | undefined,
      auth: c.auth && {
        kind: (c.auth.kind ?? "none") as "none" | "api_key" | "oauth",
        header: c.auth.header,
        prefix: c.auth.prefix,
        tokenUrl: c.auth.token_url,
        clientId: c.auth.client_id,
        scope: c.auth.scope,
      },
      secretRefs: c.secret_refs,
      imap: c.imap,
      mapping: c.mapping,
      pollSeconds: c.poll_seconds as number | undefined,
      quietMinutes: c.quiet_minutes as number | undefined,
      createUnknown: Boolean(c.create_unknown),
    },
    secrets: f.secrets ?? {},
    pushPath: f.push_path ?? undefined,
    lastPollAt: f.last_poll_at ?? undefined,
    lastOkAt: f.last_ok_at ?? undefined,
    lastError: f.last_error ?? undefined,
    status: {
      state: st.state,
      why: st.why ?? undefined,
      lastMessageAt: st.last_message_at ?? undefined,
      lagSeconds: st.lag_seconds ?? undefined,
      sourceLagSeconds: st.source_lag_seconds ?? undefined,
      perHour: st.per_hour,
      last24h: st.last_24h,
      outcomes24h: st.outcomes_24h,
      refusals24h: st.refusals_24h,
    },
  };
}

function resultFromWire(r: WireFeedResult): FeedResult {
  return {
    index: r.index,
    outcome: r.outcome,
    reason: r.reason ?? undefined,
    movement: r.movement ?? undefined,
    changed: r.changed,
    ignored: r.ignored,
    unmapped: r.unmapped,
    messageId: r.message_id,
  };
}
